var __defProp = Object.defineProperty;
var __export = (target, all) => {
  for (var name in all)
    __defProp(target, name, { get: all[name], enumerable: true });
};

// src/utils/createApiQueryClient.ts
import { QueryClient } from "@tanstack/react-query";
import deepmerge from "deepmerge";
var BASE_CONFIG = {
  defaultOptions: {
    queries: {
      gcTime: 1e3 * 60 * 30
      // 30 minutes
    }
  }
};
var createApiQueryClient = (config) => {
  return new QueryClient(deepmerge(BASE_CONFIG, config ?? {}));
};

// src/utils/createBaseApi.ts
import deepmerge2 from "deepmerge";
function mergeHeaders(headerA, headerB) {
  const newHeaders = { ...headerA, ...headerB };
  return Object.fromEntries(
    Object.entries(newHeaders).filter(([, value]) => value !== null)
  );
}
function mergeFetchOptions(defaults, overrides) {
  const mergedOptions = deepmerge2(defaults, overrides, {
    customMerge: (key) => {
      if (key === "headers") {
        return mergeHeaders;
      }
    }
  });
  return {
    ...mergedOptions,
    body: overrides.body instanceof FormData ? overrides.body : mergedOptions.body
  };
}
var createBaseApi = (baseUrl = "", defaultInit = {}, onError) => {
  const baseInit = {
    credentials: "include",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json"
    }
  };
  const resolvedDefaultInit = deepmerge2(baseInit, defaultInit);
  return async (resource, options = {}, config) => {
    const res = await fetch(
      `${baseUrl}${resource}`,
      mergeFetchOptions(resolvedDefaultInit, options)
    );
    if (!res.ok) {
      onError?.(res);
      return {
        data: null,
        error: true,
        res
      };
    }
    if (res.status === 204) {
      return {
        data: null,
        error: false,
        res
      };
    }
    const transformedData = await (config?.responseTransform?.(res) ?? res.json().catch(() => void 0));
    return {
      data: transformedData,
      error: false,
      res
    };
  };
};

// src/clients/dashboard/createDashboardApiClient.ts
var executeQuery = (ctx) => async (params) => {
  if (ctx.invalidateQueries?.length) {
    const response = await ctx.fetchFunction(params);
    if (response.error) {
      const error = new Error("Failed to fetch");
      Object.assign(error, { res: response.res });
      throw error;
    }
    return response;
  }
  const fetchRes = await ctx.apiQueryClient.fetchQuery({
    queryFn: async () => {
      const response = await ctx.fetchFunction(params);
      if (response.error) {
        const error = new Error("Failed to fetch");
        Object.assign(error, { res: response.res });
        throw error;
      }
      return response;
    },
    queryKey: [...ctx.queryKey, ...params ? [params] : [], "legacy"]
  });
  ctx.apiQueryClient.setQueryData(
    [...ctx.queryKey, ...params ? [params] : []],
    fetchRes.data
  );
  ctx.apiQueryClient.removeQueries({
    queryKey: [...ctx.queryKey, ...params ? [params] : [], "legacy"]
  });
  return fetchRes;
};
var createDashboardApiClient = (config) => {
  const dashboardApiClient = {};
  const initializeEndpoints = (ctx) => (baseApi) => {
    return Object.entries(config.endpoints).forEach(([key, value]) => {
      const fetchFunction = value(baseApi);
      const queryKey = [
        ...ctx.baseQueryKey ? [ctx.baseQueryKey] : [],
        ...value?.queryKey ?? ["No query key found"],
        config.version
      ];
      const invalidateQueries = (value.invalidateQueries ?? []).map(
        (queryToInvalidate) => [
          ...Array.isArray(queryToInvalidate) ? queryToInvalidate : [queryToInvalidate]
        ]
      );
      const query = async (params) => {
        try {
          const queryResponse = await executeQuery({
            apiQueryClient: ctx.apiQueryClient,
            fetchFunction,
            queryKey,
            invalidateQueries
          })(params);
          await Promise.all(
            invalidateQueries.map((queryKeyToInvalidate) => {
              const stringifiedQueryKey = typeof queryKeyToInvalidate === "string" ? queryKeyToInvalidate : queryKeyToInvalidate.join(".");
              ctx.apiQueryClient.invalidateQueries({
                predicate: (query2) => {
                  return query2.queryKey.join(".").includes(stringifiedQueryKey);
                }
              });
            })
          );
          return queryResponse;
        } catch (e) {
          return {
            error: true,
            /**
             * If we don't have res on the object, we error'd real hard. However, some downstream consumers
             * of this information may want to be looking at the status of the response to understand what
             * error path to take. We, therefore, return a 500 status here as it's the closest proxy we
             * have to what has likely happened (some kind of network/server error that if you refresh, may work).
             */
            res: e && typeof e === "object" && "res" in e ? e.res : { status: 500 },
            data: null
          };
        }
      };
      Object.assign(query, {
        request: fetchFunction,
        queryKey,
        mocking: value.mocking,
        method: value.method,
        version: config.version,
        invalidateQueries
      });
      dashboardApiClient[key] = query;
    }, {});
  };
  const initialize = (args) => {
    const apiOrigin = args.apiOrigin ?? config.apiOrigin ?? `${import.meta.env?.VITE_API_ORIGIN ?? ""}/api`;
    const baseApi = args.isDemoEnv ? createBaseApi(apiOrigin, {
      headers: {
        "Sequence-Version": config.version,
        Authorization: `Basic ${args.demoApiKey}`,
        ...args.defaultHeaders ?? {}
      }
    }) : createBaseApi(apiOrigin, {
      headers: {
        "Sequence-Version": config.version,
        ...args.defaultHeaders ?? {}
      }
    });
    initializeEndpoints({
      apiQueryClient: args.apiQueryClient ?? createApiQueryClient(),
      baseQueryKey: args.baseQueryKey
    })(baseApi);
  };
  initialize({});
  dashboardApiClient.initialize = initialize;
  return dashboardApiClient;
};

// src/clients/dashboard/v20240730/endpoints/index.ts
var endpoints_exports = {};
__export(endpoints_exports, {
  DeleteLinkedEntity: () => deleteLinkedEntity_api_exports,
  DeleteNotificationPolicy: () => deleteNotificationPolicy_api_exports,
  DeleteOAuthClient: () => deleteOAuthClient_api_exports,
  DeleteUser: () => deleteUser_api_exports,
  GetAllIntegrationCustomers: () => getAllIntegrationCustomers_api_exports,
  GetAllNotificationPolicies: () => getAllNotificationPolicies_api_exports,
  GetCreditNotePdf: () => getCreditNotePdf_api_exports,
  GetCreditNotesCsvDownload: () => getCreditNotesCsvDownload_api_exports,
  GetCreditNotesSettings: () => getCreditNotesSettings_api_exports,
  GetInsightsCustomerTotals: () => getInsightsCustomerTotals_api_exports,
  GetInsightsInvoicedRevenue: () => getInsightsInvoicedRevenue_api_exports,
  GetInsightsOutstandingInvoices: () => getInsightsOutstandingInvoices_api_exports,
  GetInsightsUsageData: () => getInsightsUsageData_api_exports,
  GetInsightsUsageEvents: () => getInsightsUsageEvents_api_exports,
  GetIntegration: () => getIntegration_api_exports,
  GetIntegrationCustomersService: () => getIntegrationCustomersService_api_exports,
  GetMerchantForSequenceAccount: () => getMerchantForSequenceAccount_api_exports,
  GetOAuthClients: () => getOAuthClients_api_exports,
  GetPrice: () => getPrice_api_exports,
  GetPriceMetadata: () => getPriceMetadata_api_exports,
  GetSeatBalances: () => getSeatBalances_api_exports,
  GetSeatEvent: () => getSeatEvent_api_exports,
  GetSeatEvents: () => getSeatEvents_api_exports,
  GetSeatMetric: () => getSeatMetric_api_exports,
  GetSeatMetrics: () => getSeatMetrics_api_exports,
  GetSeatTotals: () => getSeatTotals_api_exports,
  GetSequenceAccount: () => getSequenceAccount_api_exports,
  GetSequenceAccountId: () => getSequenceAccountId_api_exports,
  GetSequenceAccountSettings: () => getSequenceAccountSettings_api_exports,
  GetTaxRates: () => getTaxRates_api_exports,
  GetUser: () => getUser_api_exports,
  GetUsers: () => getUsers_api_exports,
  PostArchiveTaxRate: () => postArchiveTaxRate_api_exports,
  PostCreateCustomerFromExternalService: () => postCreateCustomerFromExternalService_api_exports,
  PostCreateCustomerFromExternalServiceProgress: () => postCreateCustomerFromExternalServiceProgress_api_exports,
  PostCreateGoogleSheetsInsightsExport: () => postCreateGoogleSheetsInsightsExport_api_exports,
  PostNewTaxRateVersion: () => postNewTaxRateVersion_api_exports,
  PostNotificationPolicy: () => postNotificationPolicy_api_exports,
  PostOAuthClient: () => postOAuthClient_api_exports,
  PostPushCreditNoteToExternalService: () => postPushCreditNoteToExternalService_api_exports,
  PostPushCustomerToExternalService: () => postPushCustomerToExternalService_api_exports,
  PostPushInvoiceToExternalService: () => postPushInvoiceToExternalService_api_exports,
  PostSeatEvent: () => postSeatEvent_api_exports,
  PostSeatMetric: () => postSeatMetrics_api_exports,
  PostSequenceAccountInvite: () => postSequenceAccountInvite_api_exports,
  PostSession: () => postSession_api_exports,
  PostSyncInvoiceWithExternalService: () => postSyncInvoiceWithExternalService_api_exports,
  PostTaxRate: () => postTaxRate_api_exports,
  PostValidateExternalEntity: () => postValidateExternalEntity_api_exports,
  PutCreditNotesSettings: () => putCreditNotesSettings_api_exports,
  PutSequenceAccount: () => putSequenceAccount_api_exports,
  PutSequenceAccountSettings: () => putSequenceAccountSettings_api_exports,
  PutTaxRate: () => putTaxRate_api_exports,
  assetsEndpoints: () => assetsEndpoints,
  authEndpoints: () => authEndpoints,
  creditGrantEndpoints: () => creditGrantEndpoints,
  creditNotesEndpoints: () => creditNotesEndpoints,
  customerEndpoints: () => customerEndpoints,
  endpoints20240730: () => endpoints20240730,
  insightsEndpoints: () => insightsEndpoints,
  integrationEndpoints: () => integrationEndpoints,
  invoiceEndpoints: () => invoiceEndpoints,
  notificationsEndpoints: () => notificationsEndpoints,
  priceEndpoints: () => priceEndpoints,
  quickbooksEndpoints: () => quickbooksEndpoints,
  quoteTemplatesEndpoints: () => quoteTemplatesEndpoints,
  savedViewsEndpoints: () => savedViewsEndpoints,
  schedulesEndpoints: () => schedulesEndpoints,
  seatsEndpoints: () => seatsEndpoints,
  sequenceAccountEndpoints: () => sequenceAccountEndpoints,
  taxRatesEndpoints: () => taxRatesEndpoints,
  usageEventsEndpoints: () => usageEventsEndpoints,
  usageMetricEndpoints: () => usageMetricEndpoints,
  userEndpoints: () => userEndpoints
});

// src/utils/createQueryParams.ts
var createQueryParams = (params) => {
  if (!params) {
    return "";
  }
  const filteredParams = Object.entries(params).reduce((acc, [key, value]) => {
    if (value === null || value === void 0) {
      return acc;
    }
    return {
      ...acc,
      [key]: value.toString()
    };
  }, {});
  if (Object.keys(filteredParams).length === 0) {
    return "";
  }
  return `?${new URLSearchParams(filteredParams).toString()}`;
};

// src/utils/createEndpoints.ts
var createQueryEndpoint = (args) => {
  const readyForClientFn = (apiClient) => {
    const endpointHandler = (params) => {
      return apiClient(
        args.route(params),
        {
          method: args.method ?? "GET",
          body: args.method === "POST" && args.body ? args.body(params) : null,
          ...args.requestOptions
        },
        args.config
      );
    };
    return endpointHandler;
  };
  readyForClientFn.mocking = args.mocking && {
    ...args.mocking,
    status: args.mocking?.status ?? 200
  };
  readyForClientFn.method = args.method ?? "GET";
  readyForClientFn.queryKey = args.queryKey;
  return readyForClientFn;
};
var createMutationEndpoint = (args) => {
  const readyForClientFn = (apiClient) => {
    const endpointHandler = (params) => {
      return apiClient(
        args.route(params),
        {
          ...args.requestOptions,
          method: args.method,
          body: args.body ? args.body(params) : null
        },
        args.config
      );
    };
    return endpointHandler;
  };
  readyForClientFn.invalidateQueries = args.invalidateQueries;
  readyForClientFn.method = args.method;
  readyForClientFn.mocking = args.mocking && {
    ...args.mocking,
    status: args.mocking?.status ?? 200
  };
  return readyForClientFn;
};

// src/clients/dashboard/v20240730/endpoints/bulkActions/bulkActions.cacheKeys.ts
var BULK_ACTIONS = "bulkActions";

// src/clients/dashboard/v20240730/endpoints/bulkActions/getBulkActions/getBulkActions.api.ts
var getBulkActions = createQueryEndpoint({
  route: (params) => `/bulk-actions${createQueryParams(params)}`,
  queryKey: [BULK_ACTIONS],
  mocking: {
    match: /\/api\/bulk-actions$/,
    mock: {
      items: [
        {
          id: "018ee64c-d146-79a8-9c07-9798d1462fd8",
          sequenceAccountId: "1986c663-9ee9-4811-bfbf-57d2ad666096",
          createdAt: "2022-06-28T16:47:00Z",
          type: "VOID_INVOICES",
          status: "COMPLETED_WITH_FAILURES",
          requestedIds: [
            "018ee663-e185-7b2c-9373-28c97e26f7f9",
            "018ee663-fbe4-7d2b-8758-3d870b2888df"
          ],
          completedIds: ["018ee663-fbe4-7d2b-8758-3d870b2888df"],
          failedIds: ["018ee663-e185-7b2c-9373-28c97e26f7f9"],
          skippedIds: []
        }
      ],
      pagination: {
        after: "MDE4NDUyNjYtNmQ2NC03N2M4LTlhZGYtNDQ4N2Q5NGQxNDU3",
        before: "MDE4NDQyMWUtYTljYy03MTFiLWFmOTMtYmYwNTExZDRkODMz",
        totalResultSize: 1
      }
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/bulkActions/postReviewBulkAction/postReviewBulkAction.api.ts
var postReviewBulkAction = createMutationEndpoint({
  route: (params) => `/bulk-actions/${params.id}/review`,
  method: "POST",
  invalidateQueries: [BULK_ACTIONS],
  mocking: {
    match: /\/api\/bulk-actions\/(.+)\/review$/,
    mock: {
      id: "018ee64c-d146-79a8-9c07-9798d1462fd8",
      sequenceAccountId: "1986c663-9ee9-4811-bfbf-57d2ad666096",
      createdAt: "2022-06-28T16:47:00Z",
      type: "VOID_INVOICES",
      status: "COMPLETED_WITH_FAILURES",
      requestedIds: [
        "018ee663-e185-7b2c-9373-28c97e26f7f9",
        "018ee663-fbe4-7d2b-8758-3d870b2888df"
      ],
      skippedIds: [],
      completedIds: ["018ee663-fbe4-7d2b-8758-3d870b2888df"],
      failedIds: ["018ee663-e185-7b2c-9373-28c97e26f7f9"],
      reviewedAt: "2022-06-28T16:47:00Z"
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/payments/payments.cacheKeys.ts
var PAYMENT_SETTTINGS = "paymentSettings";

// src/clients/dashboard/v20240730/endpoints/payments/getAccountPaymentSettings/getAccountPaymentSettings.api.ts
var getAccountPaymentSettings = createQueryEndpoint({
  route: () => `/payments/settings`,
  queryKey: [PAYMENT_SETTTINGS],
  mocking: {
    match: /\/api\/payments\/settings$/,
    mock: {
      id: "123",
      failureNotifications: {
        merchantNotifications: "SEND_NOTIFICATIONS",
        customerNotifications: "DO_NOT_SEND_NOTIFICATIONS"
      }
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/taxCategories/taxCategories.cacheKeys.ts
var TAX_CATEGORIES = "taxCategories";

// src/clients/dashboard/v20240730/endpoints/taxCategories/getTaxCategories/getTaxCategories.api.ts
var getTaxCategories = createQueryEndpoint({
  route: (params) => `/tax-categories${createQueryParams(params)}`,
  queryKey: [TAX_CATEGORIES],
  mocking: {
    match: /\/api\/tax-categories(\?(.*))?$/,
    mock: {
      items: [
        {
          id: "3271ca41-d538-404f-a15f-25856a37fb88",
          versionId: "82ef6f1a-afc2-4a8d-b868-6d9c72bf395f",
          version: 0,
          isPublished: false,
          name: "EU Sales Taxes",
          description: "All sales taxes required for the EU",
          taxRates: [],
          isDefault: false
        }
      ],
      pagination: {
        after: "",
        before: "",
        totalResultSize: 1
      }
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/taxCategories/getTaxCategory/getTaxCategory.api.ts
var getTaxCategory = createQueryEndpoint({
  route: ({ id }) => `/tax-categories/${id}`,
  queryKey: [TAX_CATEGORIES],
  mocking: {
    match: /\/api\/tax-categories\/(.+)$/,
    mock: {
      id: "3271ca41-d538-404f-a15f-25856a37fb88",
      versionId: "82ef6f1a-afc2-4a8d-b868-6d9c72bf395f",
      version: 0,
      isPublished: false,
      name: "EU Sales Taxes",
      description: "All sales taxes required for the EU",
      taxRates: [],
      isDefault: false
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/taxCategories/postTaxCategory/postTaxCategory.api.ts
var postTaxCategory = createMutationEndpoint({
  route: () => `/tax-categories`,
  method: "POST",
  body: ({ body }) => JSON.stringify(body),
  invalidateQueries: [TAX_CATEGORIES],
  mocking: {
    match: /\/api\/tax-categories$/,
    mock: {
      id: "3271ca41-d538-404f-a15f-25856a37fb88",
      versionId: "82ef6f1a-afc2-4a8d-b868-6d9c72bf395f",
      version: 0,
      isPublished: false,
      name: "EU Sales Taxes",
      description: "All sales taxes required for the EU",
      taxRates: [],
      isDefault: false
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/taxCategories/putTaxCategory/putTaxCategory.api.ts
var putTaxCategory = createMutationEndpoint({
  route: ({ id }) => `/tax-categories/${id}`,
  method: "PUT",
  body: ({ body }) => JSON.stringify(body),
  invalidateQueries: [TAX_CATEGORIES],
  mocking: {
    match: /\/api\/tax-categories\/(.+)$/,
    mock: {
      id: "3271ca41-d538-404f-a15f-25856a37fb88",
      versionId: "82ef6f1a-afc2-4a8d-b868-6d9c72bf395f",
      version: 0,
      isPublished: false,
      name: "EU Sales Taxes",
      description: "All sales taxes required for the EU",
      taxRates: [],
      isDefault: false
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/payments/putAccountPaymentSettings/putAccountPaymentSettings.api.ts
var putAccountPaymentSettings = createMutationEndpoint({
  route: ({ id }) => `/payments/settings/${id}`,
  method: "PUT",
  body: ({ body }) => JSON.stringify(body),
  invalidateQueries: [PAYMENT_SETTTINGS],
  mocking: {
    match: /\/api\/payments\/settings\/(.+)$/,
    mock: {
      id: "settings-123",
      failureNotifications: {
        merchantNotifications: "SEND_NOTIFICATIONS",
        customerNotifications: "SEND_NOTIFICATIONS"
      }
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/quotes/quotes.cacheKeys.ts
var QUOTES = "quotes";

// src/clients/dashboard/v20240730/commonModels/pricingStructure.examples.ts
var oneTimePrice = {
  id: "018fbed7-b1a6-7fff-8954-f913c357b5b9",
  productId: "018ba4fd-3483-7460-a8e5-5f8fd555337e",
  name: "More dolla",
  createdAt: "2024-06-10",
  structure: {
    pricingType: "ONE_TIME",
    price: "3500.00"
  },
  billingFrequency: "ONE_TIME",
  billingType: "IN_ARREARS",
  integrationIds: [],
  customMetricParameters: []
};
var fixedPrice = {
  id: "018fbed7-b1a6-7fff-8954-f913c357b5b9",
  productId: "018ba4fd-3483-7460-a8e5-5f8fd555337e",
  name: "More dolla",
  createdAt: "2024-03-12",
  structure: {
    pricingType: "FIXED",
    price: "3500.00"
  },
  billingFrequency: "MONTHLY",
  billingType: "IN_ARREARS",
  integrationIds: [],
  customMetricParameters: []
};
var linearPrice = {
  id: "018fbed7-b1a6-7fff-8954-f913c357b5b9",
  productId: "018ba4fd-3483-7460-a8e5-5f8fd555337e",
  name: "More dolla",
  createdAt: "2024-01-30",
  structure: {
    pricingType: "LINEAR",
    pricePerUnit: "3500.00",
    usageMetricId: "01956fe4-558a-465d-8e30-b948a8c4b132",
    isPricePercentage: false
  },
  billingFrequency: "MONTHLY",
  billingType: "IN_ARREARS",
  integrationIds: [],
  customMetricParameters: []
};
var volumePriceFixed = {
  id: "018fbed7-b1a6-7fff-8954-f913c357b5b9",
  productId: "018ba4fd-3483-7460-a8e5-5f8fd555337e",
  name: "More dolla",
  createdAt: "2024-02-15",
  structure: {
    tiers: [
      {
        fee: "50.00",
        price: "25.00111",
        upperBound: "10000.01078",
        isPricePercentage: false
      },
      {
        fee: "0.00",
        price: "5.00",
        isPricePercentage: false
      }
    ],
    pricingType: "VOLUME",
    usageMetricId: "018c59d2-dc31-7e4e-9202-ee1c239b31ce"
  },
  billingFrequency: "MONTHLY",
  billingType: "IN_ADVANCE",
  integrationIds: [],
  customMetricParameters: []
};
var volumePricePercentage = {
  id: "018fbed7-b1a6-7fff-8954-f913c357b5b9",
  productId: "018ba4fd-3483-7460-a8e5-5f8fd555337e",
  name: "More dolla",
  createdAt: "2023-12-20",
  structure: {
    tiers: [
      {
        fee: "50.00",
        price: "25.00111",
        upperBound: "10000.01078",
        isPricePercentage: true,
        maxPrice: "10",
        minPrice: "1"
      },
      {
        fee: "0.00",
        price: "5.00",
        isPricePercentage: true,
        maxPrice: "10",
        minPrice: "1"
      }
    ],
    pricingType: "VOLUME",
    usageMetricId: "018c59d2-dc31-7e4e-9202-ee1c239b31ce"
  },
  billingFrequency: "MONTHLY",
  billingType: "IN_ARREARS",
  integrationIds: [],
  customMetricParameters: []
};
var graduatedPrice = {
  id: "018fbed7-b1a6-7fff-8954-f913c357b5b9",
  productId: "018ba4fd-3483-7460-a8e5-5f8fd555337e",
  name: "More dolla",
  createdAt: "2024-04-25",
  structure: {
    tiers: [
      {
        fee: "50.00",
        price: "25.00111",
        upperBound: "10000.01078",
        isPricePercentage: false
      },
      {
        fee: "0.00",
        price: "5.00",
        isPricePercentage: false
      }
    ],
    pricingType: "GRADUATED",
    usageMetricId: "018c59d2-dc31-7e4e-9202-ee1c239b31ce",
    usageCalculationMode: "BILLING_PERIOD"
  },
  billingFrequency: "MONTHLY",
  billingType: "IN_ARREARS",
  integrationIds: [
    {
      service: "Xero",
      id: "123"
    }
  ],
  customMetricParameters: []
};
var packagePrice = {
  id: "018fbed7-b1a6-7fff-8954-f913c357b5b9",
  productId: "018ba4fd-3483-7460-a8e5-5f8fd555337e",
  name: "More dolla",
  createdAt: "2024-02-17",
  structure: {
    pricePerPackage: "10",
    packageSize: "20",
    pricingType: "PACKAGE",
    usageMetricId: "018c59d2-dc31-7e4e-9202-ee1c239b31ce"
  },
  billingFrequency: "YEARLY",
  billingType: "IN_ARREARS",
  integrationIds: [],
  customMetricParameters: [
    {
      parameterId: "079f93a2-e4d8-4729-a32a-de2511a7d1d1",
      value: "10"
    }
  ]
};
var seatBasedPriceNoOverages = {
  id: "018fbed7-b1a6-7fff-8954-f913c357b5b9",
  productId: "018ba4fd-3483-7460-a8e5-5f8fd555337e",
  name: "More dolla",
  createdAt: "2024-04-22",
  structure: {
    pricingType: "SEAT_BASED",
    seatMetricId: "2f97a232-d389-480b-b7ce-0610e6116ceb",
    pricePerSeat: "3500.00",
    prorationStrategy: "USE_MAXIMUM",
    contractedMinimumSeats: 10,
    tiers: [],
    prorateFlatFees: false
  },
  billingFrequency: "MONTHLY",
  billingType: "IN_ARREARS",
  integrationIds: [],
  customMetricParameters: []
};
var seatBasedPriceOverages = {
  id: "018fbed7-b1a6-7fff-8954-f913c357b5b9",
  productId: "018ba4fd-3483-7460-a8e5-5f8fd555337e",
  name: "More dolla",
  createdAt: "2024-05-01",
  structure: {
    pricingType: "SEAT_BASED",
    seatMetricId: "2f97a232-d389-480b-b7ce-0610e6116ceb",
    pricePerSeat: "3500.00",
    prorationStrategy: "USE_MAXIMUM",
    contractedMinimumSeats: 10,
    overagesBillingFrequency: "MONTHLY",
    tiers: [],
    prorateFlatFees: false
  },
  billingFrequency: "YEARLY",
  billingType: "IN_ARREARS",
  integrationIds: [],
  customMetricParameters: []
};
var seatBasedPriceTiers = {
  id: "018fbed7-b1a6-7fff-8954-f913c357b5b9",
  productId: "018ba4fd-3483-7460-a8e5-5f8fd555337e",
  name: "More dolla",
  createdAt: "2024-06-10",
  structure: {
    pricingType: "SEAT_BASED",
    seatMetricId: "2f97a232-d389-480b-b7ce-0610e6116ceb",
    pricePerSeat: "0",
    prorationStrategy: "USE_MAXIMUM",
    contractedMinimumSeats: 10,
    tiers: [
      {
        price: "25.00111",
        upperBound: "10000.01078"
      },
      {
        price: "5.00",
        fee: "10.00"
      }
    ],
    prorateFlatFees: false
  },
  billingFrequency: "MONTHLY",
  billingType: "IN_ARREARS",
  integrationIds: [],
  customMetricParameters: []
};
var examplePrices = [
  oneTimePrice,
  fixedPrice,
  linearPrice,
  graduatedPrice,
  volumePriceFixed,
  volumePricePercentage,
  packagePrice,
  seatBasedPriceNoOverages,
  seatBasedPriceOverages,
  seatBasedPriceTiers
];

// src/clients/dashboard/v20240730/endpoints/quotes/getQuote/getQuote.api.ts
var EXAMPLE_QUOTE = {
  id: "018fbe90-cefa-79e5-b17a-a0a5525b38bc",
  createdBy: "46204534-85ba-4187-96d9-9cc5d7175b31",
  quoteNumber: "Q00130",
  title: "Foo",
  currency: "USD",
  pdfStatus: "NOT_YET_REQUESTED",
  presentation: [
    {
      config: {
        phases: [
          {
            id: "new-uuid",
            name: "Phase 1",
            dates: {
              absolute: {
                end: "",
                start: ""
              },
              duration: {
                years: 1
              }
            },
            prices: examplePrices,
            minimums: [
              {
                id: "new-minimum::944be936-ea22-4f37-8c6e-71eb1f682387",
                name: "",
                scope: {
                  target: "allUsage",
                  priceIds: []
                },
                value: "15.00"
              }
            ],
            discounts: []
          },
          {
            id: "new-phase::01723118-789f-435c-b398-619ac3982928",
            dates: {
              absolute: {
                end: "",
                start: ""
              },
              duration: {
                years: 1
              }
            },
            prices: [
              {
                id: "new-price::60181225-95a0-4062-b89b-657de93404d3",
                name: "More dolla",
                productId: "018ba4fd-3483-7460-a8e5-5f8fd555337e",
                structure: {
                  price: "3500.00",
                  pricingType: "FIXED"
                },
                parameters: [],
                billingType: "IN_ARREARS",
                externalIds: [],
                billingFrequency: "MONTHLY"
              }
            ],
            minimums: [],
            discounts: [
              {
                id: "new-discount::25b1e237-136e-4c35-8cc0-53ea89a7d192",
                amount: 5,
                message: "New discount",
                priceIds: ["new-price::60181225-95a0-4062-b89b-657de93404d3"],
                applyToAllPrices: false,
                discountCalculationType: "PERCENTAGE"
              }
            ]
          }
        ]
      },
      version: 1,
      blockType: "phases"
    },
    {
      config: {
        text: []
      },
      version: 1,
      blockType: "paragraph"
    }
  ],
  customerId: "customer-123",
  publishedAt: "2024-05-28T10:55:57.776435Z",
  expiresAt: "2024-06-27",
  isExpired: false,
  status: "PUBLISHED",
  dealType: "NEW_BUSINESS",
  createdAt: "2024-05-28T09:38:32.307766Z",
  updatedAt: "2024-05-28T10:55:57.776435Z",
  readyToSignAt: "2024-05-28T10:55:57.776435Z",
  expiryDays: 30,
  merchantBranding: {
    logoUrl: "https://backend-production-librarybucket-1izigk5lryla9.s3.amazonaws.com/48bc5c87-69dd-4d3a-b977-8a3ce36efefd/1695221702164_download.jpeg",
    legalCompanyName: "Kai Test",
    primaryColour: "#2A9EDB",
    address: {
      line1: "Foo",
      line2: "Bar",
      town: "Hixon't",
      postcode: "ST1 1AA",
      country: "GB"
    },
    email: "foo@bar.com"
  },
  phases: [
    {
      id: "018fbed7-b1aa-7baa-ade6-0f3faaa4982d",
      createdAt: "2024-05-28T10:55:57.866154Z",
      name: "Phase 1",
      duration: {
        months: 0,
        years: 1
      },
      prices: examplePrices,
      discounts: [],
      minimums: [
        {
          id: "018fbed7-b1aa-7baa-ade7-54f102f362cc",
          amount: "15",
          restrictToPrices: [],
          createdAt: "2024-05-28T10:55:57.866181Z",
          updatedAt: "2024-05-28T10:55:57.866181Z"
        }
      ]
    },
    {
      id: "018fbed7-b1cf-719c-b46d-04554289888e",
      duration: {
        months: 0,
        years: 1
      },
      prices: [
        {
          id: "018fbed7-b1cb-782b-b331-eeb71fe1cea3",
          productId: "018ba4fd-3483-7460-a8e5-5f8fd555337e",
          name: "More dolla",
          createdAt: "2024-04-05",
          structure: {
            pricingType: "FIXED",
            price: "3500.00"
          },
          billingFrequency: "MONTHLY",
          billingType: "IN_ARREARS",
          integrationIds: [],
          customMetricParameters: []
        }
      ],
      discounts: [
        {
          id: "018fbed7-b1cf-719c-b46e-7deb87e939e6",
          restrictToPrices: ["018fbed7-b1cb-782b-b331-eeb71fe1cea3"],
          amount: "5",
          discountCalculationType: "PERCENTAGE",
          message: "New discount",
          seatDiscountType: "ALL_SEATS"
        }
      ],
      minimums: [
        {
          id: "13295b5e-d609-4e49-8ed5-38301cad9c6e",
          restrictToPrices: ["018fbed7-b1cb-782b-b331-eeb71fe1cea3"],
          amount: "5"
        }
      ]
    }
  ],
  contacts: [
    {
      contactId: "contact-1",
      name: "John",
      email: "john@example.com",
      signedAt: void 0
    },
    {
      contactId: "contact-2",
      name: "Mary",
      email: "mary@example.com",
      signedAt: "2024-05-28T10:55:57.866181Z"
    }
  ],
  counterSigners: [
    {
      id: "counter-signer-1",
      name: "Adam",
      email: "adam@example-pet-store.com",
      signedAt: void 0
    },
    {
      id: "counter-signer-2",
      name: "Becky",
      email: "Becky@example-pet-store.com",
      signedAt: "2024-05-28T10:55:57.866181Z"
    }
  ],
  isSignaturesEnabled: true,
  attachmentAssets: [
    {
      id: "assetId",
      type: "IMAGE",
      url: "url",
      fileName: "file",
      fileSizeBytes: 10,
      format: "PDF",
      createdBy: "upload-user-1",
      createdAt: "2025-03-11T00:00:00.000Z"
    }
  ]
};
var getQuote = createQueryEndpoint({
  route: ({ id }) => `/quotes/${id}`,
  queryKey: [QUOTES],
  mocking: {
    match: /\/api\/quotes\/(.+)$/,
    mock: EXAMPLE_QUOTE
  }
});

// src/clients/dashboard/v20240730/endpoints/quotes/getQuotes/getQuotes.api.ts
var getQuotes = createQueryEndpoint({
  route: (params) => `/quotes${createQueryParams(params)}`,
  queryKey: [QUOTES],
  mocking: {
    match: /\/api\/quotes(\?(.*))?$/,
    mock: {
      items: [
        {
          id: "018fbe90-cefa-79e5-b17a-a0a5525b38bc",
          createdBy: "46204534-85ba-4187-96d9-9cc5d7175b31",
          quoteNumber: "Q00130",
          acceptedAt: "2024-05-28T10:55:57.776435Z",
          isExpired: false,
          title: "Foo",
          currency: "USD",
          customerId: "customer-123",
          publishedAt: "2024-05-28T10:55:57.776435Z",
          expiresAt: "2024-06-27",
          status: "PUBLISHED",
          dealType: "NEW_BUSINESS",
          createdAt: "2024-05-28T09:38:32.307766Z",
          updatedAt: "2024-05-28T10:55:57.776435Z",
          expiryDays: 30
        }
      ],
      pagination: {
        after: "",
        before: "",
        totalResultSize: 1
      }
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/quotes/putQuote/putQuote.api.ts
var putQuote = createMutationEndpoint({
  route: ({ id }) => `/quotes/${id}`,
  method: "PUT",
  body: ({ body }) => JSON.stringify(body),
  invalidateQueries: [QUOTES],
  mocking: {
    match: /\/api\/quotes\/(.+)$/,
    mock: {
      id: "018fbe90-cefa-79e5-b17a-a0a5525b38bc",
      createdBy: "46204534-85ba-4187-96d9-9cc5d7175b31",
      quoteNumber: "Q00130",
      title: "Foo",
      currency: "USD",
      pdfStatus: "NOT_YET_REQUESTED",
      presentation: [
        {
          config: {
            phases: [
              {
                id: "new-uuid",
                dates: {
                  absolute: {
                    end: "",
                    start: ""
                  },
                  duration: {
                    years: 1
                  }
                },
                prices: examplePrices,
                minimums: [
                  {
                    id: "new-minimum::944be936-ea22-4f37-8c6e-71eb1f682387",
                    name: "",
                    scope: {
                      target: "allUsage",
                      priceIds: []
                    },
                    value: "15.00"
                  }
                ],
                discounts: []
              },
              {
                id: "new-phase::01723118-789f-435c-b398-619ac3982928",
                dates: {
                  absolute: {
                    end: "",
                    start: ""
                  },
                  duration: {
                    years: 1
                  }
                },
                prices: [
                  {
                    id: "new-price::60181225-95a0-4062-b89b-657de93404d3",
                    name: "More dolla",
                    productId: "018ba4fd-3483-7460-a8e5-5f8fd555337e",
                    structure: {
                      price: "3500.00",
                      pricingType: "FIXED"
                    },
                    parameters: [],
                    billingType: "IN_ARREARS",
                    externalIds: [],
                    billingFrequency: "MONTHLY"
                  }
                ],
                minimums: [],
                discounts: [
                  {
                    id: "new-discount::25b1e237-136e-4c35-8cc0-53ea89a7d192",
                    amount: 5,
                    message: "New discount",
                    priceIds: [
                      "new-price::60181225-95a0-4062-b89b-657de93404d3"
                    ],
                    applyToAllPrices: false,
                    discountCalculationType: "PERCENTAGE"
                  }
                ]
              }
            ]
          },
          version: 1,
          blockType: "phases"
        },
        {
          config: {
            text: []
          },
          version: 1,
          blockType: "paragraph"
        }
      ],
      customerId: "customer-123",
      publishedAt: "2024-05-28T10:55:57.776435Z",
      expiresAt: "2024-06-27",
      isExpired: false,
      status: "PUBLISHED",
      dealType: "NEW_BUSINESS",
      createdAt: "2024-05-28T09:38:32.307766Z",
      updatedAt: "2024-05-28T10:55:57.776435Z",
      readyToSignAt: "2024-05-28T10:55:57.776435Z",
      expiryDays: 30,
      merchantBranding: {
        logoUrl: "https://backend-production-librarybucket-1izigk5lryla9.s3.amazonaws.com/48bc5c87-69dd-4d3a-b977-8a3ce36efefd/1695221702164_download.jpeg",
        legalCompanyName: "Kai Test",
        primaryColour: "#2A9EDB",
        address: {
          line1: "Foo",
          line2: "Bar",
          town: "Hixon't",
          postcode: "ST1 1AA",
          country: "GB"
        },
        email: "foo@bar.com"
      },
      phases: [
        {
          id: "018fbed7-b1aa-7baa-ade6-0f3faaa4982d",
          createdAt: "2024-05-28T10:55:57.866154Z",
          duration: {
            months: 0,
            years: 1
          },
          prices: examplePrices,
          discounts: [],
          minimums: [
            {
              id: "018fbed7-b1aa-7baa-ade7-54f102f362cc",
              amount: "15",
              restrictToPrices: [],
              createdAt: "2024-05-28T10:55:57.866181Z",
              updatedAt: "2024-05-28T10:55:57.866181Z"
            }
          ]
        },
        {
          id: "018fbed7-b1cf-719c-b46d-04554289888e",
          duration: {
            months: 0,
            years: 1
          },
          prices: [
            {
              id: "018fbed7-b1cb-782b-b331-eeb71fe1cea3",
              productId: "018ba4fd-3483-7460-a8e5-5f8fd555337e",
              name: "More dolla",
              createdAt: "2024-04-05",
              structure: {
                pricingType: "FIXED",
                price: "3500.00"
              },
              billingFrequency: "MONTHLY",
              billingType: "IN_ARREARS",
              integrationIds: [],
              customMetricParameters: []
            }
          ],
          discounts: [
            {
              id: "018fbed7-b1cf-719c-b46e-7deb87e939e6",
              restrictToPrices: ["018fbed7-b1cb-782b-b331-eeb71fe1cea3"],
              amount: "5",
              discountCalculationType: "PERCENTAGE",
              message: "New discount",
              seatDiscountType: "ALL_SEATS"
            }
          ],
          minimums: [
            {
              id: "13295b5e-d609-4e49-8ed5-38301cad9c6e",
              restrictToPrices: ["018fbed7-b1cb-782b-b331-eeb71fe1cea3"],
              amount: "5"
            }
          ]
        }
      ],
      contacts: [
        {
          contactId: "contact-1",
          name: "John",
          email: "john@example.com",
          signedAt: void 0
        },
        {
          contactId: "contact-2",
          name: "Mary",
          email: "mary@example.com",
          signedAt: "2024-05-28T10:55:57.866181Z"
        }
      ],
      counterSigners: [
        {
          id: "counter-signer-1",
          name: "Adam",
          email: "adam@example-pet-store.com",
          signedAt: void 0
        },
        {
          id: "counter-signer-2",
          name: "Becky",
          email: "Becky@example-pet-store.com",
          signedAt: "2024-05-28T10:55:57.866181Z"
        }
      ],
      isSignaturesEnabled: true,
      attachmentAssets: [
        {
          id: "assetId",
          type: "IMAGE",
          url: "url",
          fileName: "file",
          fileSizeBytes: 10,
          format: "PDF",
          createdBy: "upload-user-1",
          createdAt: "2025-03-11T00:00:00.000Z"
        }
      ]
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/quotes/postQuote/postQuote.api.ts
var mockQuote = {
  id: "quote-123",
  isSignaturesEnabled: true,
  counterSigners: [],
  pdfStatus: "GENERATED",
  attachmentAssets: [],
  readyToSignAt: "2024-05-28T10:55:57.866181Z",
  isExpired: false,
  expiryDays: 60,
  contacts: [],
  archivedAt: "2024-05-28T10:55:57.866181Z",
  createdBy: "user-123",
  customerId: "customer-123",
  quoteNumber: "QUOTE-123",
  title: "Test Quote",
  currency: "USD",
  presentation: [],
  status: "ACCEPTED",
  dealType: "NEW_BUSINESS",
  expiresAt: "2024-07-30",
  publishedAt: "2024-05-28T10:55:57.866181Z",
  createdAt: "2024-05-28T10:55:57.866181Z",
  updatedAt: "2024-05-28T10:55:57.866181Z",
  merchantBranding: {
    email: "foo@bar.com",
    legalCompanyName: "Test Company",
    address: {
      line1: "123 Test St",
      town: "Test City",
      postcode: "12345",
      country: "US"
    }
  },
  phases: []
};
var postQuoteAccept = createMutationEndpoint({
  route: ({ id }) => `/quotes/${id}/accept`,
  method: "POST",
  invalidateQueries: [QUOTES],
  mocking: {
    match: /\/api\/quotes\/(.+)\/accept$/,
    mock: {
      ...mockQuote,
      acceptedAt: "2024-05-28T10:55:57.866181Z"
    }
  }
});
var postQuoteExecute = createMutationEndpoint({
  route: ({ id }) => `/quotes/${id}/execute`,
  method: "POST",
  invalidateQueries: [QUOTES],
  mocking: {
    match: /\/api\/quotes\/(.+)\/execute$/,
    mock: {
      ...mockQuote,
      status: "EXECUTED",
      preliminaryBillingStartDate: "2024-05-28",
      acceptedAt: "2024-05-28T10:55:57.866181Z",
      executedAt: "2024-05-28T10:55:57.866181Z"
    }
  }
});
var postQuotePublish = createMutationEndpoint({
  route: ({ id }) => `/quotes/${id}/publish`,
  method: "POST",
  body: ({ body }) => JSON.stringify(body),
  invalidateQueries: [QUOTES],
  mocking: {
    match: /\/api\/quotes\/(.+)\/publish$/,
    mock: {
      ...mockQuote,
      status: "PUBLISHED"
    }
  }
});
var postQuote = createMutationEndpoint({
  route: () => `/quotes`,
  method: "POST",
  invalidateQueries: [QUOTES],
  body: ({ body }) => JSON.stringify(body),
  mocking: {
    match: /\/api\/quotes$/,
    mock: {
      ...mockQuote,
      status: "DRAFT"
    }
  }
});
var postQuoteDuplicate = createMutationEndpoint({
  route: ({ id }) => `/quotes/${id}/duplicate`,
  method: "POST",
  invalidateQueries: [QUOTES],
  mocking: {
    match: /\/api\/quotes\/(.+)\/duplicate$/,
    mock: {
      ...mockQuote,
      status: "DRAFT"
    }
  }
});
var postQuoteArchive = createMutationEndpoint({
  route: ({ id }) => `/quotes/${id}/archive`,
  method: "POST",
  invalidateQueries: [QUOTES],
  mocking: {
    match: /\/api\/quotes\/(.+)\/archive$/,
    mock: mockQuote
  }
});

// src/clients/dashboard/v20240730/endpoints/quotes/deleteQuote/deleteQuote.api.ts
var deleteQuoteDraftVariant = createMutationEndpoint({
  route: ({ id }) => `/quotes/${id}/latest-draft`,
  method: "DELETE",
  invalidateQueries: [QUOTES],
  mocking: {
    match: /\/api\/quotes\/(.+)\/latest-draft$/,
    mock: {
      id: "quote-123",
      contacts: [],
      readyToSignAt: "2024-05-28T10:55:57.866181Z",
      isSignaturesEnabled: true,
      counterSigners: [],
      pdfStatus: "GENERATED",
      attachmentAssets: [],
      isExpired: false,
      expiryDays: 60,
      createdBy: "user-123",
      customerId: "customer-123",
      quoteNumber: "QUOTE-123",
      title: "Test Quote",
      currency: "USD",
      presentation: [],
      status: "PUBLISHED",
      dealType: "NEW_BUSINESS",
      expiresAt: "2024-07-30",
      publishedAt: "2024-05-28T10:55:57.866181Z",
      createdAt: "2024-05-28T10:55:57.866181Z",
      updatedAt: "2024-05-28T10:55:57.866181Z",
      merchantBranding: {
        legalCompanyName: "Test Company",
        email: "foo@bar.com",
        address: {
          line1: "123 Test St",
          town: "Test City",
          postcode: "12345",
          country: "US"
        }
      },
      phases: []
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/products/products.cacheKey.ts
var PRODUCTS = "products";

// src/clients/dashboard/v20240730/endpoints/products/postProduct/postProduct.api.ts
var postProductArchive = createMutationEndpoint({
  route: ({ id }) => `/products/${id}/archive`,
  method: "POST",
  invalidateQueries: [PRODUCTS],
  mocking: {
    match: /\/api\/products\/(.+)\/archive$/,
    mock: {
      id: "prod-123",
      name: "Brand new shiny test product",
      label: "With a label",
      createdAt: "2024-06-20T14:41:06.95878Z",
      updatedAt: "2024-06-20T14:41:06.958802Z"
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/merchants/merchants.cacheKeys.ts
var MERCHANT = "merchant";

// src/clients/dashboard/v20240730/endpoints/merchants/putMerchant/putMerchant.api.ts
var putMerchant = createMutationEndpoint({
  route: ({ id }) => `/merchants/${id}`,
  method: "PUT",
  body: ({ body }) => JSON.stringify(body),
  invalidateQueries: [MERCHANT],
  mocking: {
    match: /\/api\/merchants\/(.+)$/,
    mock: {
      id: "123",
      sequenceAccountId: "seq-123",
      invoiceNumberPrefix: "INV",
      creditNoteNumberPrefix: "CRE",
      lastInvoiceNumber: 1e3,
      lastCreditNoteNumber: 1,
      legalCompanyName: "Company",
      address: {
        line1: "123 Fake Street",
        town: "London",
        postcode: "SW1A 1AA",
        country: "GB"
      },
      email: "foo@bar.com",
      defaultDueDateDays: 45,
      bankAccountDetails: [
        {
          key: "EUR",
          value: {
            type: "IBAN",
            iban: "GB82WEST12345698765432"
          }
        },
        {
          key: "GBP",
          value: {
            type: "UK_BANK_ACCOUNT",
            ukBankAccount: {
              sortCode: "123456",
              accountNumber: "12345678"
            }
          }
        },
        {
          key: "USD",
          value: {
            type: "US_ACH",
            usAchDestination: {
              accountNumber: "12345678",
              accountName: "John Doe",
              bankName: "Bank of America",
              bankRoutingNumber: "123456789"
            }
          }
        },
        {
          key: "ZAR",
          value: {
            type: "US_WIRE",
            usWireDestination: {
              accountNumber: "12345678",
              accountName: "John Doe",
              bankName: "Bank of America",
              bankRoutingNumber: "123456789",
              swiftCode: "BOFAUS3N",
              bankAddress: {
                line1: "123 Fake Street",
                town: "London",
                postcode: "SW1A 1AA",
                country: "GB"
              }
            }
          }
        },
        {
          key: "CAD",
          value: {
            type: "CA_BANK_ACCOUNT",
            canadianBankAccount: {
              transitNumber: "123456789",
              institutionNumber: "123",
              accountNumber: "12345678"
            }
          }
        },
        {
          key: "JPY",
          value: {
            type: "CA_INTERNATIONAL",
            canadianInternationalDestination: {
              legalName: "Company",
              accountNumber: "12345678",
              transitNumber: "123456789",
              bankName: "Bank of America",
              bankAddress: {
                line1: "123 Fake Street",
                town: "London",
                postcode: "SW1A 1AA",
                country: "GB"
              },
              institutionNumber: "123",
              swiftCode: "BOFAUS3N",
              intermediaryBank: {
                bankName: "Bank of America",
                bankAddress: {
                  line1: "123 Fake Street",
                  town: "London",
                  postcode: "SW1A 1AA",
                  country: "GB"
                },
                swiftCode: "BOFAUS3N",
                routingNumber: "123456789"
              }
            }
          }
        },
        {
          key: "AUD",
          value: {
            type: "AUSTRALIAN",
            australianDestination: {
              legalName: "Company",
              accountNumber: "12345678",
              swiftCode: "BOFAUS3N",
              bsb: "123456"
            }
          }
        }
      ],
      customerPortalEnabled: true,
      includeBeneficiaryAddressInPaymentDetails: true,
      ccMerchantOnInvoiceEmails: true,
      showUsageInCustomerPortal: true
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/merchants/getMerchant/getMerchant.api.ts
var getMerchant = createQueryEndpoint({
  route: () => `/merchant-for-sequence-account`,
  queryKey: [MERCHANT],
  mocking: {
    match: /\/api\/merchant-for-sequence-account$/,
    mock: {
      id: "123",
      sequenceAccountId: "seq-123",
      invoiceNumberPrefix: "INV",
      creditNoteNumberPrefix: "CRE",
      lastInvoiceNumber: 1e3,
      lastCreditNoteNumber: 1,
      legalCompanyName: "Company",
      address: {
        line1: "123 Fake Street",
        town: "London",
        postcode: "SW1A 1AA",
        country: "GB"
      },
      email: "foo@bar.com",
      defaultDueDateDays: 45,
      bankAccountDetails: [
        {
          key: "EUR",
          value: {
            type: "IBAN",
            iban: "GB82WEST12345698765432"
          }
        },
        {
          key: "GBP",
          value: {
            type: "UK_BANK_ACCOUNT",
            ukBankAccount: {
              sortCode: "123456",
              accountNumber: "12345678"
            }
          }
        },
        {
          key: "USD",
          value: {
            type: "US_ACH",
            usAchDestination: {
              accountNumber: "12345678",
              accountName: "John Doe",
              bankName: "Bank of America",
              bankRoutingNumber: "123456789"
            }
          }
        },
        {
          key: "ZAR",
          value: {
            type: "US_WIRE",
            usWireDestination: {
              accountNumber: "12345678",
              accountName: "John Doe",
              bankName: "Bank of America",
              bankRoutingNumber: "123456789",
              swiftCode: "BOFAUS3N",
              bankAddress: {
                line1: "123 Fake Street",
                town: "London",
                postcode: "SW1A 1AA",
                country: "GB"
              }
            }
          }
        },
        {
          key: "CAD",
          value: {
            type: "CA_BANK_ACCOUNT",
            canadianBankAccount: {
              transitNumber: "123456789",
              institutionNumber: "123",
              accountNumber: "12345678"
            }
          }
        },
        {
          key: "JPY",
          value: {
            type: "CA_INTERNATIONAL",
            canadianInternationalDestination: {
              legalName: "Company",
              accountNumber: "12345678",
              transitNumber: "123456789",
              bankName: "Bank of America",
              bankAddress: {
                line1: "123 Fake Street",
                town: "London",
                postcode: "SW1A 1AA",
                country: "GB"
              },
              institutionNumber: "123",
              swiftCode: "BOFAUS3N",
              intermediaryBank: {
                bankName: "Bank of America",
                bankAddress: {
                  line1: "123 Fake Street",
                  town: "London",
                  postcode: "SW1A 1AA",
                  country: "GB"
                },
                swiftCode: "BOFAUS3N",
                routingNumber: "123456789"
              }
            }
          }
        },
        {
          key: "AUD",
          value: {
            type: "AUSTRALIAN",
            australianDestination: {
              legalName: "Company",
              accountNumber: "12345678",
              swiftCode: "BOFAUS3N",
              bsb: "123456"
            }
          }
        }
      ],
      customerPortalEnabled: true,
      includeBeneficiaryAddressInPaymentDetails: true,
      ccMerchantOnInvoiceEmails: true,
      showUsageInCustomerPortal: true
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/billingProducts/billingProducts.cacheKeys.ts
var BILLING_PRODUCTS = "billingProducts";

// src/clients/dashboard/v20240730/endpoints/billingProducts/getBillingProducts/getBillingProducts.api.ts
var getBillingProducts = createQueryEndpoint({
  route: (params) => `/billing-products${createQueryParams(params)}`,
  queryKey: [BILLING_PRODUCTS],
  mocking: {
    match: /\/api\/billing-products(\?(.*))?$/,
    mock: {
      items: [
        {
          id: "f409d651-c475-4142-aafd-a5d2bd74d4f9",
          name: "Dog tax",
          label: "Charge for not providing photos of your dog",
          sequenceAccountId: "683fe518-60c8-429f-9320-8aa7efc20bcc",
          createdAt: "2022-06-28T16:47:00Z",
          updatedAt: "2022-06-28T16:47:00Z",
          prices: []
        }
      ],
      pagination: {
        after: "",
        before: "",
        totalResultSize: 1
      }
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/taxCategories/postNewTaxCategoryVersion/postNewTaxCategoryVersion.api.ts
var postNewTaxCategoryVersion = createMutationEndpoint({
  route: ({ id }) => `/tax-categories/${id}/versions`,
  method: "POST",
  body: ({ body }) => JSON.stringify(body),
  invalidateQueries: [TAX_CATEGORIES],
  mocking: {
    match: /\/api\/tax-categories\/(.+)\/versions$/,
    mock: {
      id: "3271ca41-d538-404f-a15f-25856a37fb88",
      versionId: "82ef6f1a-afc2-4a8d-b868-6d9c72bf395f",
      version: 1,
      isPublished: true,
      name: "EU Sales Taxes",
      description: "All sales taxes required for the EU",
      taxRates: [],
      isDefault: false
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/taxRegistrations/taxRegistrations.cacheKeys.ts
var TAX_REGISTRATIONS = "taxRegistrations";

// src/clients/dashboard/v20240730/endpoints/taxRegistrations/postTaxRegistration/postTaxRegistration.api.ts
var postTaxRegistration = createMutationEndpoint({
  route: () => `/tax-registrations`,
  method: "POST",
  body: ({ body }) => JSON.stringify(body),
  invalidateQueries: [TAX_REGISTRATIONS],
  mocking: {
    match: /\/api\/tax-registrations$/,
    mock: {
      taxIdentifier: "123456789",
      country: "US",
      id: "123",
      sequenceAccountId: "seq-123",
      state: "AL"
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/taxRegistrations/putTaxRegistration/putTaxRegistration.api.ts
var putTaxRegistration = createMutationEndpoint({
  route: ({ id }) => `/tax-registrations/${id}`,
  method: "PUT",
  body: ({ body }) => JSON.stringify(body),
  invalidateQueries: [TAX_REGISTRATIONS],
  mocking: {
    match: /\/api\/tax-registrations\/(.+)$/,
    mock: {
      taxIdentifier: "123456789",
      country: "US",
      id: "123",
      sequenceAccountId: "seq-123",
      state: "AL"
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/taxRegistrations/getTaxRegistrations/getTaxRegistrations.api.ts
var getTaxRegistrations = createQueryEndpoint({
  route: ({ customerId, ...rest }) => `/tax-registrations/for-customer/${customerId}${createQueryParams(rest)}`,
  queryKey: [TAX_REGISTRATIONS],
  mocking: {
    match: /\/api\/tax-registrations\/for-customer\/[^\/]+(\?(.*))?$/,
    mock: {
      items: [
        {
          id: "3271ca41-d538-404f-a15f-25856a37fb88",
          sequenceAccountId: "42020696-da25-44d0-ab79-9ecbf8fc5307",
          taxIdentifier: "CA123456789",
          customerId: "06457f78-85cd-4140-9972-67c6cd8f6071",
          country: "GB"
        }
      ],
      pagination: {
        after: "",
        before: "",
        totalResultSize: 1
      }
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/invoices/invoices.cacheKeys.ts
var INVOICE_RENDER_SETTINGS = "invoiceRenderSettings";
var INVOICES = "invoices";
var INVOICES_LITE = "invoicesLite";
var INVOICE_SETTINGS = "invoiceSettings";
var INVOICE_GRID = "invoiceGrid";
var USAGE_ITEM_GROUPS = "usageItemGroups";
var INVOICE_MERCHANT_DETAILS = "invoiceMerchantDetails";
var INVOICE_CSV_DOWNLOAD = "invoiceCsvDownload";
var INVOICE_PDF_DOWNLOAD = "invoicePdfDownload";

// src/clients/dashboard/v20240730/endpoints/invoices/_mocks/mockInvoice.ts
var mockInvoice = {
  id: "inv-123",
  sequenceAccountId: "seq-123",
  status: "DRAFT",
  currency: "GBP",
  netTotal: "1000",
  grossTotal: "1000",
  totalTax: "0",
  customerId: "cus-123",
  customerEmails: ["beep@boop.com"],
  customerLegalCompanyName: "Test Co.",
  customerBillingAddress: {
    line1: "123 Fake St",
    town: "Springfield",
    postcode: "12345",
    country: "GB"
  },
  customerShippingAddress: {
    line1: "123 Fake St",
    town: "Springfield",
    postcode: "12345",
    country: "GB"
  },
  isCustomerArchived: false,
  metadata: [
    {
      key: "string",
      value: "string"
    }
  ],
  paymentStatus: "PAID",
  createdAt: "2024-01-05",
  creditNoteIds: [],
  linkedServices: [
    {
      externalId: "string",
      externalService: "Xero",
      externalUrl: "https://example.com",
      syncTime: "2024-01-05T12:00:00Z"
    }
  ],
  accountingDate: "2024-02-01",
  customerTaxStatus: "TAX_EXEMPT",
  renderSettings: {
    paymentLinkDisplay: "HIDE_PAYMENT_LINK"
  }
};

// src/clients/dashboard/v20240730/endpoints/invoices/convertInvoiceToDraft/convertInvoiceToDraft.api.ts
var convertInvoiceToDraft = createMutationEndpoint({
  route: (params) => `/invoices/${params.id}/draft`,
  method: "POST",
  invalidateQueries: [INVOICES, INVOICE_GRID],
  mocking: {
    match: /\/api\/invoices\/(.+)\/draft$/,
    mock: mockInvoice
  }
});

// src/clients/dashboard/v20240730/endpoints/invoices/deleteInvoice/deleteInvoice.api.ts
var deleteInvoice = createMutationEndpoint({
  route: ({ invoiceId }) => `/invoices/${invoiceId}`,
  method: "DELETE",
  invalidateQueries: [INVOICES, INVOICE_GRID],
  mocking: {
    match: /\/api\/invoices\/(.+)$/,
    mock: mockInvoice
  }
});

// src/clients/dashboard/v20240730/endpoints/invoices/deleteInvoiceSettings/deleteInvoiceSettings.api.ts
var deleteInvoiceSettings = createMutationEndpoint({
  route: ({ settingsId }) => `/payments/settings/invoices/${settingsId}`,
  method: "DELETE",
  invalidateQueries: [INVOICE_SETTINGS],
  mocking: {
    match: /\/api\/payments\/settings\/invoices\/(.+)$/,
    mock: {
      id: "123",
      sequenceAccountId: "seq-123",
      invoiceId: "inv-123",
      customerId: "cus-123",
      paymentProvider: "STRIPE"
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/invoices/getInvoice/getInvoice.api.ts
var getInvoice = createQueryEndpoint({
  route: (params) => `/invoices/${params.id}`,
  queryKey: [INVOICES],
  mocking: {
    match: /\/api\/invoices\/(.+)$/,
    mock: {
      ...mockInvoice,
      billingRunId: "brr-123"
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/invoices/getInvoiceSettingsForInvoice/getInvoiceSettingsForInvoice.api.ts
var getInvoiceSettingsForInvoice = createQueryEndpoint({
  route: ({ id }) => `/payments/settings/invoices/for-invoice/${id}`,
  method: "GET",
  queryKey: [INVOICE_SETTINGS],
  mocking: {
    match: /\/api\/payments\/settings\/invoices\/for-invoice\/(.+)$/,
    mock: {
      id: "123",
      sequenceAccountId: "seq-123",
      invoiceId: "inv-123",
      paymentProvider: "STRIPE"
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/invoices/getInvoicesRenderSettings/getInvoicesRenderSettings.api.ts
var getInvoicesRenderSettings = createQueryEndpoint({
  route: () => `/invoices/render-settings`,
  queryKey: [INVOICE_RENDER_SETTINGS],
  mocking: {
    match: /\/api\/invoices\/render-settings$/,
    mock: {
      id: "123",
      paymentLinkDisplay: "SHOW_PAYMENT_LINK"
    }
  }
});

// src/clients/dashboard/v99990101/endpoints/invoices/lineItems/lineItems.cacheKeys.ts
var LINE_ITEMS = "lineItems";

// src/clients/dashboard/v20240730/endpoints/invoices/lineItems/getLineItems/getLineItems.api.ts
var getLineItems = createQueryEndpoint({
  route: ({ invoiceId, ...params }) => `/invoices/${invoiceId}/line-items${createQueryParams(params)}`,
  queryKey: [LINE_ITEMS],
  mocking: {
    match: /\/api\/invoices\/(.+)\/line-items(\?(.*))?$/,
    mock: {
      items: [
        {
          id: "af60cab3-812d-4250-a051-0fb7133a00c7",
          invoiceId: "af60cab3-812d-4250-a051-0fb7133a00c7",
          title: "Ferraris",
          description: "Red",
          quantity: "2",
          rate: "20000.10",
          taxRate: "0.2",
          servicePeriodStart: "2022-10-20T00:00:00Z",
          servicePeriodEnd: "2022-10-24T00:00:00Z",
          servicePeriod: {
            startDate: "2022-10-20",
            endDate: "2022-10-24"
          },
          index: 2,
          groupId: "cd11218d-fa00-4faa-8684-a43113d076a8",
          netTotal: "51.20",
          totalTax: "2.50",
          grossTotal: "101.23",
          priceId: "fe2fcd0b-4c53-45c7-b2ae-b6c1a7d21e95",
          rateDisplay: "ABSOLUTE",
          externalIds: [
            {
              key: "Xero",
              value: "123"
            }
          ],
          revenueClassification: "EARNED",
          creditGrantId: "fe2fcd0b-4c53-45c7-b2ae-b6c1a7d21e95"
        }
      ],
      pagination: {
        after: "",
        before: "",
        totalResultSize: 1
      }
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/invoices/lineItems/lineItems.cacheKeys.ts
var LINE_ITEMS2 = "lineItems";

// src/clients/dashboard/v20240730/endpoints/invoices/lineItems/postLineItem/postLineItem.api.ts
var postLineItem = createMutationEndpoint({
  route: ({ invoiceId }) => `/invoices/${invoiceId}/line-items`,
  method: "POST",
  body: ({ body }) => JSON.stringify(body),
  invalidateQueries: [INVOICES, LINE_ITEMS2, INVOICE_GRID],
  mocking: {
    match: /\/api\/invoices\/(.+)\/line-items$/,
    mock: {
      id: "af60cab3-812d-4250-a051-0fb7133a00c7",
      invoiceId: "cd11218d-fa00-4faa-8684-a43113d076a8",
      title: "Ferraris",
      description: "Red",
      quantity: "2",
      rate: "20000.10",
      taxRate: "0.2",
      servicePeriodStart: "2022-10-20T00:00:00Z",
      servicePeriodEnd: "2022-10-24T00:00:00Z",
      servicePeriod: {
        startDate: "2022-10-20",
        endDate: "2022-10-24"
      },
      index: 2,
      groupId: "cd11218d-fa00-4faa-8684-a43113d076a8",
      netTotal: "51.20",
      totalTax: "2.50",
      grossTotal: "101.23",
      priceId: "fe2fcd0b-4c53-45c7-b2ae-b6c1a7d21e95",
      rateDisplay: "ABSOLUTE",
      externalIds: [
        {
          key: "Xero",
          value: "123"
        }
      ],
      revenueClassification: "EARNED",
      creditGrantId: "fe2fcd0b-4c53-45c7-b2ae-b6c1a7d21e95"
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/invoices/lineItems/putLineItem/putLineItem.api.ts
var putLineItem = createMutationEndpoint({
  route: ({ invoiceId, id }) => `/invoices/${invoiceId}/line-items/${id}`,
  method: "PUT",
  body: ({ body }) => JSON.stringify(body),
  invalidateQueries: [INVOICES, LINE_ITEMS2, INVOICE_GRID],
  mocking: {
    match: /\/api\/invoices\/(.+)\/line-items\/(.+)$/,
    mock: {
      id: "af60cab3-812d-4250-a051-0fb7133a00c7",
      invoiceId: "cd11218d-fa00-4faa-8684-a43113d076a8",
      title: "Ferraris",
      description: "Red",
      quantity: "2",
      rate: "20000.10",
      taxRate: "0.2",
      servicePeriodStart: "2022-10-20T00:00:00Z",
      servicePeriodEnd: "2022-10-24T00:00:00Z",
      servicePeriod: {
        startDate: "2022-10-20",
        endDate: "2022-10-24"
      },
      index: 2,
      groupId: "cd11218d-fa00-4faa-8684-a43113d076a8",
      netTotal: "51.20",
      totalTax: "2.50",
      grossTotal: "101.23",
      priceId: "fe2fcd0b-4c53-45c7-b2ae-b6c1a7d21e95",
      rateDisplay: "ABSOLUTE",
      externalIds: [
        {
          key: "Xero",
          value: "123"
        }
      ],
      revenueClassification: "EARNED",
      creditGrantId: "fe2fcd0b-4c53-45c7-b2ae-b6c1a7d21e95"
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/invoices/listInvoicesLite/listInvoicesLite.api.ts
var listInvoicesLite = createQueryEndpoint({
  route: () => `/invoices-lite/for-ids`,
  method: "POST",
  queryKey: [INVOICES_LITE],
  body: (params) => JSON.stringify(params),
  mocking: {
    match: /\/invoices-lite\/for-ids$/,
    mock: {
      items: [
        {
          id: "123",
          success: true,
          data: {
            id: "123",
            invoiceNumber: "INV00001",
            customerLegalCompanyName: "Adam co"
          }
        },
        {
          id: "456",
          success: true,
          data: {
            id: "456",
            invoiceNumber: "INV00007",
            customerLegalCompanyName: "Adam co"
          }
        },
        {
          id: "789",
          success: true,
          data: {
            id: "789",
            invoiceNumber: "INV00102",
            customerLegalCompanyName: "Aperture Science"
          }
        }
      ]
    }
  }
});

// src/clients/dashboard/v99990101/endpoints/invoices/invoices.cacheKeys.ts
var INVOICES2 = "invoices";

// src/clients/dashboard/v99990101/endpoints/invoices/postInvoice/postInvoice.api.ts
var invoiceResponseMock = {
  id: "ba37a392-7054-4dae-ab37-6fee9e23fe06",
  sequenceAccountId: "af9b5988-1d7b-45f6-a188-326df5e59b24",
  status: "DRAFT",
  currency: "GBP",
  invoiceNumber: "INV2",
  dueDate: "2022-11-05",
  purchaseOrderNumber: "PO123",
  reference: "ref",
  customerEmails: ["customer@example.com"],
  customerLegalCompanyName: "Facebook",
  customerBillingAddress: {
    line1: "Flat 1",
    line2: "15 Yemen Road",
    town: "Yemen",
    state: "CA",
    postcode: "YE1 2YE",
    country: "YE"
  },
  customerShippingAddress: {
    line1: "742 Evergreen Terrace",
    line2: "",
    town: "Springfield",
    state: "CA",
    postcode: "12345",
    country: "US"
  },
  memo: "Thanks",
  paymentOptions: ["BANK_TRANSFER"],
  billingPeriod: {
    start: "2022-10-01",
    endInclusive: "2022-10-30"
  },
  customerId: "b3c0cf23-6717-4cdc-b190-acf8aa6ccd8a",
  totalTax: "2",
  netTotal: "12",
  grossTotal: "10",
  metadata: [
    {
      key: "example-label",
      value: "label-value"
    }
  ],
  paymentStatus: "UNPAID",
  createdAt: "2022-10-30T00:00:00Z",
  creditNoteIds: ["941272d1-f840-4e16-b698-0cd455c81e0e"],
  linkedServices: [
    {
      externalId: "ID",
      externalService: "Xero",
      syncTime: "2022-06-28T16:47:00Z"
    }
  ],
  merchantDetails: {
    address: {
      line1: "Flat 1",
      line2: "123 Fake Street",
      town: "New York",
      state: "NY",
      postcode: "AB1 2EF",
      country: "US"
    },
    phoneNumber: "0800001066",
    email: "donald@example.com",
    taxId: "TAX",
    iban: "GB33BUKB20201555555555",
    ukAccountDetails: {
      sortCode: "123456",
      accountNumber: "12345678"
    },
    usAchDetails: {
      accountNumber: "12345678",
      accountName: "John Doe",
      bankName: "Chase",
      bankRoutingNumber: "123456789"
    },
    usWireDetails: {
      accountNumber: "12345678",
      accountName: "John Doe",
      bankName: "Chase",
      bankRoutingNumber: "123456789",
      swiftCode: "CHASUS33",
      bankAddress: {
        line1: "JP Morgan Chase",
        town: "New York City",
        state: "NY",
        postcode: "10017",
        country: "US"
      }
    },
    legalCompanyName: "The Merchant",
    customFields: [],
    includeBeneficiaryAddressInPaymentDetails: false
  },
  accountingDate: "2022-10-05",
  customerTaxStatus: "TAXED",
  renderSettings: {
    paymentLinkDisplay: "HIDE_PAYMENT_LINK"
  },
  isCustomerArchived: false
};
var postFinalizeAndSendInvoice = createMutationEndpoint({
  route: ({ id }) => `/invoices/${id}/finalize-and-send`,
  method: "POST",
  invalidateQueries: [INVOICES2],
  mocking: {
    match: /\/api\/invoices\/(.+)\/finalize-and-send$/,
    mock: invoiceResponseMock
  }
});
var postFinalizeInvoice = createMutationEndpoint({
  route: ({ id }) => `/invoices/${id}/finalize`,
  method: "POST",
  invalidateQueries: [INVOICES2],
  mocking: {
    match: /\/api\/invoices\/(.+)\/finalize$/,
    mock: invoiceResponseMock
  }
});

// src/clients/dashboard/v20240730/endpoints/invoices/mergeInvoices/mergeInvoices.api.ts
var postMergeInvoices = createMutationEndpoint({
  route: () => `/invoices/merge`,
  method: "POST",
  body: (params) => JSON.stringify(params),
  invalidateQueries: [INVOICES, BULK_ACTIONS, INVOICE_GRID],
  mocking: {
    match: /\/api\/invoices\/merge$/,
    mock: invoiceResponseMock
  }
});

// src/clients/dashboard/v20240730/endpoints/invoices/postFinalizeAndSendMultipleInvoices/postFinalizeAndSendMultipleInvoices.api.ts
var postFinalizeAndSendMultipleInvoices = createMutationEndpoint({
  route: () => "/invoices/finalize-and-send",
  body: ({ invoiceIds }) => JSON.stringify({ invoiceIds }),
  method: "POST",
  invalidateQueries: [BULK_ACTIONS],
  mocking: {
    match: /\/invoices\/finalize-and-send$/,
    mock: {
      id: "e007e884-5100-4183-8355-4771865cb4dd",
      sequenceAccountId: "1986c663-9ee9-4811-bfbf-57d2ad666096",
      type: "FINALISE_AND_SEND_INVOICES",
      status: "PROCESSING",
      createdAt: "2022-06-28T16:47:00Z",
      requestedIds: [],
      completedIds: [],
      failedIds: [],
      skippedIds: []
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/invoices/postFinalizeMultipleInvoices/postFinalizeMultipleInvoices.api.ts
var postFinalizeMultipleInvoices = createMutationEndpoint({
  route: () => "/invoices/finalize",
  body: ({ invoiceIds }) => JSON.stringify({ invoiceIds }),
  method: "POST",
  invalidateQueries: [BULK_ACTIONS],
  mocking: {
    match: /\/invoices\/finalize$/,
    mock: {
      id: "e007e884-5100-4183-8355-4771865cb4dd",
      sequenceAccountId: "1986c663-9ee9-4811-bfbf-57d2ad666096",
      type: "FINALISE_INVOICES",
      status: "PROCESSING",
      createdAt: "2022-06-28T16:47:00Z",
      requestedIds: [],
      completedIds: [],
      failedIds: [],
      skippedIds: []
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/invoices/postInvoiceSettings/postInvoiceSettings.api.ts
var postInvoiceSettings = createMutationEndpoint({
  route: () => `/payments/settings/invoices`,
  method: "POST",
  body: (params) => JSON.stringify(params),
  invalidateQueries: [INVOICE_SETTINGS],
  mocking: {
    match: /\/api\/payments\/settings\/invoices$/,
    mock: {
      id: "123",
      sequenceAccountId: "seq-123",
      invoiceId: "inv-123",
      customerId: "cus-123",
      paymentProvider: "STRIPE"
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/invoices/postRecalculateMultipleInvoices/postRecalculateMultipleInvoices.api.ts
var postRecalculateMultipleInvoices = createMutationEndpoint({
  route: () => "/invoices/recalculate",
  body: ({ invoiceIds }) => JSON.stringify({ invoiceIds }),
  method: "POST",
  invalidateQueries: [BULK_ACTIONS],
  mocking: {
    match: /\/invoices\/recalculate$/,
    mock: void 0
  }
});

// src/clients/dashboard/v20240730/endpoints/invoices/postSendMultipleInvoices/postSendMultipleInvoices.api.ts
var postSendMultipleInvoices = createMutationEndpoint({
  route: () => "/invoices/send",
  body: ({ invoiceIds }) => JSON.stringify({ invoiceIds }),
  method: "POST",
  invalidateQueries: [BULK_ACTIONS],
  mocking: {
    match: /\/invoices\/send$/,
    mock: {
      id: "e007e884-5100-4183-8355-4771865cb4dd",
      sequenceAccountId: "1986c663-9ee9-4811-bfbf-57d2ad666096",
      type: "SEND_INVOICES",
      status: "PROCESSING",
      createdAt: "2022-06-28T16:47:00Z",
      requestedIds: [],
      completedIds: [],
      failedIds: [],
      skippedIds: []
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/invoices/postVoidMultipleInvoices/postVoidMultipleInvoices.api.ts
var postVoidMultipleInvoices = createMutationEndpoint({
  route: () => "/invoices/void",
  body: ({ invoiceIds }) => JSON.stringify({ invoiceIds }),
  method: "POST",
  invalidateQueries: [BULK_ACTIONS],
  mocking: {
    match: /\/invoices\/void$/,
    mock: {
      id: "e007e884-5100-4183-8355-4771865cb4dd",
      sequenceAccountId: "1986c663-9ee9-4811-bfbf-57d2ad666096",
      type: "VOID_INVOICES",
      status: "PROCESSING",
      createdAt: "2022-06-28T16:47:00Z",
      requestedIds: [],
      completedIds: [],
      failedIds: [],
      skippedIds: []
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/invoices/putInvoice/putInvoice.api.ts
var putInvoice = createMutationEndpoint({
  route: ({ id }) => `/invoices/${id}`,
  method: "PUT",
  body: (params) => JSON.stringify(params.body),
  invalidateQueries: [INVOICES, INVOICE_GRID],
  mocking: {
    match: /\/api\/invoices\/(.+)$/,
    mock: mockInvoice
  }
});

// src/clients/dashboard/v20240730/endpoints/invoices/putInvoiceSettings/putInvoiceSettings.api.ts
var putInvoiceSettings = createMutationEndpoint({
  route: ({ settingsId }) => `/payments/settings/invoices/${settingsId}`,
  method: "PUT",
  body: ({ paymentProvider }) => JSON.stringify({ paymentProvider }),
  invalidateQueries: [INVOICE_SETTINGS],
  mocking: {
    match: /\/api\/payments\/settings\/invoices\/(.+)$/,
    mock: {
      id: "123",
      sequenceAccountId: "seq-123",
      invoiceId: "inv-123",
      customerId: "cus-123",
      paymentProvider: "STRIPE"
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/invoices/putInvoicesRenderSettings/putInvoicesRenderSettings.api.ts
var putInvoicesRenderSettings = createMutationEndpoint({
  route: ({ id }) => `/invoices/render-settings/${id}`,
  method: "PUT",
  body: ({ body }) => JSON.stringify(body),
  invalidateQueries: [INVOICE_RENDER_SETTINGS],
  mocking: {
    match: /\/api\/invoices\/render-settings\/(.+)$/,
    mock: {
      id: "123",
      paymentLinkDisplay: "SHOW_PAYMENT_LINK"
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/invoices/putUpdatePaymentStatusMultipleInvoices/putUpdatePaymentStatusMultipleInvoices.api.ts
var putUpdatePaymentStatusMultipleInvoices = createMutationEndpoint({
  route: () => "/invoices/payment-status",
  body: ({ invoiceIds, paymentStatus }) => JSON.stringify({ invoiceIds, paymentStatus }),
  method: "PUT",
  invalidateQueries: [BULK_ACTIONS, INVOICES],
  mocking: {
    match: /\/invoices\/payment-status$/,
    mock: void 0
  }
});

// src/clients/dashboard/v20240730/endpoints/invoices/postSendInvoice/postSendInvoice.api.ts
var postSendInvoice = createMutationEndpoint({
  route: ({ id }) => `/invoices/${id}/send`,
  method: "POST",
  invalidateQueries: [INVOICES, INVOICE_GRID],
  mocking: {
    match: /\/invoices\/(.+)\/send$/,
    mock: mockInvoice
  }
});

// src/clients/dashboard/v20240730/endpoints/invoices/postFinalizeAndSendInvoice/postFinalizeAndSendInvoice.api.ts
var postFinalizeAndSendInvoice2 = createMutationEndpoint({
  route: ({ id }) => `/invoices/${id}/finalize-and-send`,
  method: "POST",
  invalidateQueries: [INVOICES, INVOICE_GRID],
  mocking: {
    match: /\/invoices\/(.+)\/finalize-and-send$/,
    mock: mockInvoice
  }
});

// src/clients/dashboard/v20240730/endpoints/invoices/postRecalculateInvoice/postRecalculateInvoice.api.ts
var postRecalculateInvoice = createMutationEndpoint({
  route: ({ id }) => `/invoices/${id}/recalculate`,
  method: "POST",
  invalidateQueries: [INVOICES, INVOICE_GRID],
  mocking: {
    match: /\/invoices\/(.+)\/recalculate$/,
    mock: mockInvoice
  }
});

// src/clients/dashboard/v20240730/endpoints/invoices/postVoidInvoice/postVoidInvoice.api.ts
var postVoidInvoice = createMutationEndpoint({
  route: ({ id }) => `/invoices/${id}/void`,
  method: "POST",
  invalidateQueries: [INVOICES, INVOICE_GRID],
  mocking: {
    match: /\/invoices\/(.+)\/void$/,
    mock: mockInvoice
  }
});

// src/clients/dashboard/v20240730/endpoints/invoices/postFinalizeInvoice/postFinalizeInvoice.api.ts
var postFinalizeInvoice2 = createMutationEndpoint({
  route: ({ id }) => `/invoices/${id}/finalize`,
  method: "POST",
  invalidateQueries: [INVOICES, INVOICE_GRID],
  mocking: {
    match: /\/invoices\/(.+)\/finalize$/,
    mock: mockInvoice
  }
});

// src/clients/dashboard/v20240730/endpoints/invoices/postSendPaymentReminderForInvoice/postSendPaymentReminderForInvoice.api.ts
var postSendPaymentReminderForInvoice = createMutationEndpoint({
  route: ({ id }) => `/invoices/${id}/send-payment-reminder`,
  method: "POST",
  invalidateQueries: [INVOICES, INVOICE_GRID],
  mocking: {
    match: /\/invoices\/(.+)\/send-payment-reminder$/,
    mock: mockInvoice
  }
});

// src/clients/dashboard/v20240730/endpoints/invoices/deleteInvoiceLineItemGroup/deleteInvoiceLineItemGroup.api.ts
var deleteInvoiceLineItemGroup = createMutationEndpoint({
  route: ({ invoiceId, lineItemGroupId }) => `/invoices/${invoiceId}/line-item-groups/${lineItemGroupId}`,
  method: "DELETE",
  invalidateQueries: [INVOICES, INVOICE_GRID],
  mocking: {
    match: /\/api\/invoices\/(.+)\/line-item-groups\/(.+)$/,
    mock: {
      id: "4639a112-f821-4bc1-bc2d-6f9ece5b7c0f",
      invoiceId: "cd11218d-fa00-4faa-8684-a43113d076a8",
      title: "Users",
      description: "Charges for users",
      index: 2,
      netTotal: "51.20",
      totalTax: "2.50",
      grossTotal: "101.23",
      taxCategory: {
        id: "28227c88-6dba-455f-9b30-b9c76957e610",
        name: "VAT"
      }
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/invoices/deleteInvoiceLineItem/deleteInvoiceLineItem.api.ts
var deleteInvoiceLineItem = createMutationEndpoint({
  route: ({ invoiceId, lineItemId }) => `/invoices/${invoiceId}/line-items/${lineItemId}`,
  method: "DELETE",
  invalidateQueries: [INVOICES, INVOICE_GRID],
  mocking: {
    match: /\/api\/invoices\/(.+)\/line-items\/(.+)$/,
    mock: {
      id: "af60cab3-812d-4250-a051-0fb7133a00c7",
      invoiceId: "cd11218d-fa00-4faa-8684-a43113d076a8",
      title: "Ferraris",
      description: "Red",
      quantity: "2",
      rate: "20000.10",
      taxRate: "0.2",
      servicePeriodStart: "2022-10-20T00:00:00Z",
      servicePeriodEnd: "2022-10-24T00:00:00Z",
      servicePeriod: {
        startDate: "2022-10-20",
        endDate: "2022-10-24"
      },
      index: 2,
      groupId: "cd11218d-fa00-4faa-8684-a43113d076a8",
      netTotal: "51.20",
      totalTax: "2.50",
      grossTotal: "101.23",
      priceId: "fe2fcd0b-4c53-45c7-b2ae-b6c1a7d21e95",
      rateDisplay: "ABSOLUTE",
      externalIds: [
        {
          key: "Xero",
          value: "123"
        }
      ],
      revenueClassification: "EARNED",
      creditGrantId: "fe2fcd0b-4c53-45c7-b2ae-b6c1a7d21e95",
      productId: "dd55bf30-3b97-4644-a9fc-b15e0f59a110"
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/invoices/postSendTestInvoice/postSendTestInvoice.api.ts
var postSendTestInvoice = createMutationEndpoint({
  route: ({ id, testEmail }) => `/invoices/${id}/send/${testEmail}`,
  method: "POST",
  invalidateQueries: [INVOICES, INVOICE_GRID],
  mocking: {
    match: /\/invoices\/(.+)\/send\/(.+)$/,
    mock: mockInvoice
  }
});

// src/clients/dashboard/v20240730/endpoints/invoices/getInvoicesGrid/getInvoicesGrid.api.ts
var getInvoicesGrid = createQueryEndpoint({
  route: (params) => `/invoices/invoices-grid${createQueryParams({
    invoiceCurrency: params?.invoiceCurrency,
    customerIds: params?.customerIds?.join(","),
    invoiceStatuses: params?.invoiceStatuses?.join(","),
    paymentStatuses: params?.paymentStatuses?.join(",")
  })}`,
  queryKey: [INVOICE_GRID],
  mocking: {
    match: /\/invoices\/invoices-grid(\?(.*))?$/,
    mock: {
      customerDetails: [
        {
          customerId: "4ecc7384-e25e-459d-b811-fb5f3ae73923",
          legalName: "Saas Corp",
          total: "600",
          netTotal: "400",
          monthDetails: [
            {
              month: "2023-09-01",
              total: "300",
              netTotal: "200",
              invoiceIds: ["b78f0591-afcc-43bc-851f-2288aa25b07b"]
            },
            {
              month: "2023-10-01",
              total: "300",
              netTotal: "200",
              invoiceIds: ["227b8ff9-42ad-47f8-bfa5-9e5760d4d5d8"]
            }
          ]
        },
        {
          customerId: "a0bc49b1-8e9a-4995-af73-5db2f9bd494b",
          legalName: "Hermanos Cafeteros Inc.",
          total: "600",
          netTotal: "400",
          monthDetails: [
            {
              month: "2023-09-01",
              total: "300",
              netTotal: "200",
              invoiceIds: ["12e4c680-a33d-4440-b01b-6e259e0a51d4"]
            },
            {
              month: "2023-10-01",
              total: "300",
              netTotal: "200",
              invoiceIds: [
                "c82c7f82-2d76-49b4-832b-c9bb1969a5c6",
                "1b4d180d-ae8d-413c-9a97-08a7a85a2980"
              ]
            }
          ]
        }
      ],
      accountTotal: "1200",
      accountNetTotal: "800",
      monthDetails: [
        {
          month: "2023-09-01",
          total: "600",
          netTotal: "400",
          invoiceIds: [
            "b78f0591-afcc-43bc-851f-2288aa25b07b",
            "12e4c680-a33d-4440-b01b-6e259e0a51d4"
          ]
        },
        {
          month: "2023-10-01",
          total: "600",
          netTotal: "400",
          invoiceIds: [
            "227b8ff9-42ad-47f8-bfa5-9e5760d4d5d8",
            "c82c7f82-2d76-49b4-832b-c9bb1969a5c6",
            "1b4d180d-ae8d-413c-9a97-08a7a85a2980"
          ]
        }
      ]
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/invoices/getInvoiceUsageItemGroups/getInvoiceUsageItemGroups.api.ts
var getInvoiceUsageItemGroups = createQueryEndpoint({
  route: ({ invoiceId }) => `/invoices/${invoiceId}/usage-item-groups`,
  queryKey: [USAGE_ITEM_GROUPS],
  mocking: {
    match: /\/invoices\/(.+)\/usage-item-groups$/,
    mock: {
      items: [
        {
          id: "1aaeac35-cdc3-4adb-a741-5fa4816619ad",
          invoiceId: "invoice-1",
          title: "Successful API Calls",
          items: [
            {
              id: "cf8ad70f-f79a-4803-a41f-9a0b436c865a",
              invoiceId: "6cedbb5e-4f77-4217-b8d4-020e7e4d33c9",
              title: "Eldest Child",
              value: "150",
              groupedValues: [],
              lineItemGroupId: "f4457a3c-a0f2-44ea-9c8d-548fa1298626",
              usageItemGroupId: "1aaeac35-cdc3-4adb-a741-5fa4816619ad"
            },
            {
              id: "f00fcb1a-76ff-44d9-b8c3-b444e554cc9c",
              invoiceId: "6cedbb5e-4f77-4217-b8d4-020e7e4d33c9",
              title: "Youngest Child",
              value: "300",
              groupedValues: [],
              lineItemGroupId: "f4457a3c-a0f2-44ea-9c8d-548fa1298626",
              usageItemGroupId: "1aaeac35-cdc3-4adb-a741-5fa4816619ad"
            }
          ]
        }
      ]
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/invoices/postCalculateInvoiceTotals/postCalculateInvoiceTotals.api.ts
var postCalculateInvoiceTotals = createMutationEndpoint({
  route: () => `/invoices-totals`,
  method: "POST",
  body: ({ lineItems }) => JSON.stringify({ lineItems }),
  /**
   * No invalidation, as this is a one off query with no caching.
   */
  invalidateQueries: [],
  mocking: {
    match: /\/invoices-totals$/,
    mock: {
      total: {
        netTotal: "200000",
        totalTax: "10",
        grossTotal: "22000"
      },
      lineItemTotals: [
        {
          id: "af60cab3-812d-4250-a051-0fb7133a00c7",
          total: {
            netTotal: "200000",
            totalTax: "10",
            grossTotal: "22000"
          }
        }
      ],
      lineItemGroupTotals: [
        {
          id: "941272d1-f840-4e16-b698-0cd455c81e0e",
          total: {
            netTotal: "200000",
            totalTax: "10",
            grossTotal: "22000"
          }
        }
      ]
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/invoices/getUsageDataForInvoiceLineItemGroup/getUsageDataForInvoiceLineItemGroup.api.ts
var getUsageDataForInvoiceLineItemGroup = createQueryEndpoint({
  route: ({ invoiceId, lineItemGroupId }) => `/invoices/${invoiceId}/line-item-groups/${lineItemGroupId}/usage-data`,
  queryKey: [USAGE_ITEM_GROUPS],
  mocking: {
    match: /\/invoices\/(.+)\/line-item-groups\/(.+)\/usage-data$/,
    mock: {
      data: {
        usageStart: "2023-02-01",
        usageEnd: "2023-04-30",
        metricName: "API Calls",
        metricId: "27ba4761-7573-41c5-b780-2993b2c254f7",
        total: "35",
        usageData: [
          {
            periodStart: "2023-02-01",
            amount: "10",
            cumulativeAmount: "10"
          },
          {
            periodStart: "2023-03-01",
            amount: "15",
            cumulativeAmount: "25"
          },
          {
            periodStart: "2023-04-01",
            amount: "10",
            cumulativeAmount: "35"
          }
        ]
      }
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/invoices/getInvoiceMerchantDetails/getInvoiceMerchantDetails.api.ts
var getInvoiceMerchantDetails = createQueryEndpoint({
  route: (params) => `/invoice-merchant-details${createQueryParams(params)}`,
  method: "GET",
  queryKey: [INVOICE_MERCHANT_DETAILS],
  mocking: {
    match: /\/api\/invoice-merchant-details(\?(.*))?$/,
    mock: {
      address: {
        line1: "Flat 1",
        line2: "123 Fake Street",
        town: "New York",
        state: "NY",
        postcode: "AB1 2EF",
        country: "US"
      },
      phoneNumber: "0800001066",
      email: "donald@example.com",
      taxId: "TAX",
      iban: "GB33BUKB20201555555555",
      ukAccountDetails: {
        sortCode: "123456",
        accountNumber: "12345678"
      },
      usAchDetails: {
        accountNumber: "12345678",
        accountName: "John Doe",
        bankName: "Chase",
        bankRoutingNumber: "123456789"
      },
      usWireDetails: {
        accountNumber: "12345678",
        accountName: "John Doe",
        bankName: "Chase",
        bankRoutingNumber: "123456789",
        swiftCode: "CHASUS33",
        bankAddress: {
          line1: "JP Morgan Chase",
          town: "New York City",
          state: "NY",
          postcode: "10017",
          country: "US"
        }
      },
      legalCompanyName: "The Merchant",
      customFields: [],
      includeBeneficiaryAddressInPaymentDetails: false
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/invoices/postInvoice/postInvoice.api.ts
var postInvoice = createMutationEndpoint({
  route: () => `/invoices`,
  method: "POST",
  body: (params) => JSON.stringify(params),
  invalidateQueries: [INVOICES, INVOICE_GRID],
  mocking: {
    match: /\/api\/invoices$/,
    mock: mockInvoice
  }
});

// src/clients/dashboard/v20240730/endpoints/invoices/putInvoicePaymentStatus/putInvoicePaymentStatus.api.ts
var putInvoicePaymentStatus = createMutationEndpoint({
  route: ({ invoiceId }) => `/invoices/${invoiceId}/payment-status`,
  method: "PUT",
  body: ({ invoiceId, ...body }) => JSON.stringify(body),
  invalidateQueries: [INVOICES, INVOICE_GRID],
  mocking: {
    match: /\/api\/invoices\/(.+)\/payment-status$/,
    mock: mockInvoice
  }
});

// src/clients/dashboard/v20240730/endpoints/invoices/getInvoicesCsvDownload/getInvoicesCsvDownload.api.ts
var getInvoicesCsvDownload = createQueryEndpoint({
  route: (params) => `/invoices/invoices-csv${typeof params === "string" ? `?${params}` : createQueryParams(params)}`,
  config: { responseTransform: (res) => res.blob() },
  queryKey: [INVOICE_CSV_DOWNLOAD],
  mocking: {
    match: /\/api\/invoices\/invoices-csv(\?(.*))?$/,
    mock: new Blob([""], { type: "text/csv" })
  }
});

// src/clients/dashboard/v20240730/endpoints/invoices/getInvoicePdf/getInvoicePdf.api.ts
var getInvoicePdf = createQueryEndpoint({
  route: ({ id }) => `/invoices/${id}/download`,
  config: { responseTransform: (res) => res.blob() },
  queryKey: [INVOICE_PDF_DOWNLOAD],
  mocking: {
    match: /\/api\/invoices\/[^\/]+\/download$/,
    mock: new Blob([""], { type: "application/pdf" })
  }
});

// src/clients/dashboard/v20240730/endpoints/invoices/getInvoicesGridCsvDownload/getInvoicesGridCsvDownload.api.ts
var getInvoicesGridCsvDownload = createQueryEndpoint(
  {
    route: (params) => `/invoices/invoices-grid-csv${createQueryParams(params)}`,
    config: { responseTransform: (res) => res.blob() },
    queryKey: [INVOICE_CSV_DOWNLOAD],
    mocking: {
      match: /\/api\/invoices\/invoices-grid-csv(\?(.*))?$/,
      mock: new Blob([""], { type: "text/csv" })
    }
  }
);

// src/clients/dashboard/v20240730/endpoints/invoices/index.ts
var invoiceBulkActions = {
  postRecalculateMultipleInvoices,
  postFinalizeAndSendMultipleInvoices,
  postFinalizeMultipleInvoices,
  postSendMultipleInvoices,
  postVoidMultipleInvoices,
  putUpdatePaymentStatusMultipleInvoices
};
var invoiceEndpoints = {
  convertInvoiceToDraft,
  deleteInvoice,
  deleteInvoiceSettings,
  getInvoice,
  getInvoiceSettingsForInvoice,
  getInvoicesRenderSettings,
  getLineItems,
  postLineItem,
  putLineItem,
  listInvoicesLite,
  postMergeInvoices,
  postInvoiceSettings,
  putInvoice,
  putInvoiceSettings,
  putInvoicesRenderSettings,
  postSendInvoice,
  postFinalizeAndSendInvoice: postFinalizeAndSendInvoice2,
  postRecalculateInvoice,
  postVoidInvoice,
  postFinalizeInvoice: postFinalizeInvoice2,
  postSendPaymentReminderForInvoice,
  deleteInvoiceLineItemGroup,
  deleteInvoiceLineItem,
  postSendTestInvoice,
  getInvoicesGrid,
  getInvoiceUsageItemGroups,
  postCalculateInvoiceTotals,
  getUsageDataForInvoiceLineItemGroup,
  getInvoiceMerchantDetails,
  postInvoice,
  putInvoicePaymentStatus,
  getInvoicesCsvDownload,
  getInvoicePdf,
  getInvoicesGridCsvDownload,
  ...invoiceBulkActions
};

// src/clients/dashboard/v20240730/endpoints/creditGrants/creditGrants.cacheKeys.ts
var CREDIT_GRANTS = "credit-grants";
var CREDIT_GRANT_BALANCES = "credit-grant-balances";
var ALL_CREDIT_GRANTS = "all-credit-grants";

// src/clients/dashboard/v20240730/endpoints/creditGrants/postCreditGrant/postCreditGrant.api.ts
var postCreditGrant = createMutationEndpoint({
  route: () => `/credits`,
  method: "POST",
  body: ({ body }) => JSON.stringify(body),
  invalidateQueries: [CREDIT_GRANTS, CREDIT_GRANT_BALANCES, ALL_CREDIT_GRANTS],
  mocking: {
    match: /\/api\/credits$/,
    mock: {
      id: "746fc7f2-8098-4fe7-953c-7d51c580a126",
      sequenceAccountId: "dce9ae85-d8d0-42b4-bcad-f221a64363c6",
      customerId: "61b083e0-1faa-47ca-9aeb-6205da8f6c47",
      name: "On-boarding Credits",
      creditUnitType: "CURRENCY",
      currency: "GBP",
      metricId: "f092246c-6b90-4106-bcca-304ccf06bf45",
      amount: 10,
      costOfCredit: 10,
      effectiveDate: "2023-01-01",
      expiryDate: "2023-01-31",
      createdAt: "2022-10-07T14:12:08.826121Z",
      integrationIds: [
        {
          service: "Xero",
          id: "74ed2615-4730-49ab-9445-74f31723a206",
          isPending: false
        }
      ]
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/creditGrants/getCreditGrantsForCustomer/getCreditGrantsForCustomer.api.ts
var getCreditGrantsForCustomer = createQueryEndpoint({
  route: ({ customerId }) => `/credits/${customerId}/grants`,
  queryKey: [CREDIT_GRANTS],
  mocking: {
    match: /\/api\/credits\/(.*)\/grants$/,
    mock: {
      items: [
        {
          id: "0184421a-8202-70ea-a729-30308a40bbf7",
          name: "credit-grant-1",
          grantAmount: 100,
          balance: 50,
          issueDate: "2022-06-28",
          expiryDate: "2022-07-28"
        }
      ]
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/creditGrants/getCreditGrantBalancesForCustomer/getCreditGrantBalancesForCustomer.api.ts
var getCreditGrantBalancesForCustomer = createQueryEndpoint({
  route: ({ customerId }) => `/credits/${customerId}/balances`,
  queryKey: [CREDIT_GRANT_BALANCES],
  mocking: {
    match: /\/api\/credits\/(.*)\/balances$/,
    mock: {
      items: [
        {
          id: "6587e308-ab0d-4a58-8cc1-1d168490de7b",
          type: "CASH",
          name: "Credits",
          balance: "50",
          grants: [
            {
              id: "5a0faea3-7ad5-4a83-a420-09bb6576f7e6",
              sequenceAccountId: "a507e782-d26b-4d34-92b9-aacd1667e1f0",
              customerId: "eabb3196-9c73-4c0d-b508-d3c4fc7b2cbb",
              type: "CASH",
              currency: "GBP",
              metricId: "b84f5290-fe95-4c6f-bedc-477700125265",
              name: "GBP",
              originalAmount: "10",
              currentBalance: "1",
              expiryDate: "2024-01-02",
              costOfCredit: "10",
              taxRateId: "b3c37ea3-96bb-44fb-baed-01eb38d3328e",
              effectiveDate: "2024-01-01",
              createdAt: "2024-01-01T12:00:00Z"
            }
          ],
          transactions: [
            {
              id: "c770d296-ef09-459b-b8dd-42c334e00dab",
              grantId: "5a0faea3-7ad5-4a83-a420-09bb6576f7e6",
              reason: "because",
              invoiceId: "c6864c69-42a3-4966-8c22-1c56d7375a05",
              billingRunId: "e95256be-08e7-4585-b3c7-fa9928aacd90",
              type: "CREDIT",
              amount: "10",
              date: "2024-01-01"
            }
          ]
        }
      ]
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/creditGrants/getAllCreditGrants/getAllCreditGrants.api.ts
var getAllCreditGrants = createQueryEndpoint({
  route: ({ limit = 100, ...params } = {}) => `/credits/filtered${createQueryParams({ limit, ...params })}`,
  queryKey: [ALL_CREDIT_GRANTS],
  mocking: {
    match: /\/api\/credits\/filtered(\?(.*))?$/,
    mock: {
      items: [
        {
          id: "746fc7f2-8098-4fe7-953c-7d51c580a126",
          sequenceAccountId: "dce9ae85-d8d0-42b4-bcad-f221a64363c6",
          customerId: "61b083e0-1faa-47ca-9aeb-6205da8f6c47",
          name: "On-boarding Credits",
          creditUnitType: "CURRENCY",
          currency: "GBP",
          metricId: "f092246c-6b90-4106-bcca-304ccf06bf45",
          amount: 10,
          costOfCredit: 10,
          taxRateId: "179d1ab3-cc96-48e2-9310-067b0b42d47c",
          effectiveDate: "2023-01-01",
          expiryDate: "2023-01-31",
          createdAt: "2022-10-07T14:12:08.826121Z"
        }
      ],
      pagination: {
        after: "",
        before: "",
        totalResultSize: 1
      }
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/creditGrants/index.ts
var creditGrantEndpoints = {
  postCreditGrant,
  getCreditGrantsForCustomer,
  getCreditGrantBalancesForCustomer,
  getAllCreditGrants
};

// src/clients/dashboard/v20240730/endpoints/billingSchedules/billingSchedules.cacheKeys.ts
var BILLING_SCHEDULES_CSV_DOWNLOAD = "schedules-csv-download";
var BILLING_SCHEDULES = "billingSchedules";
var BILLING_SCHEDULES_SETTINGS = "billingSchedulesSettings";
var BILLING_PREVIEW = "billingPreview";

// src/clients/dashboard/v20240730/endpoints/billingSchedules/getBillingSchedule/getBillingSchedule.api.ts
var getBillingSchedule = createQueryEndpoint({
  route: ({ id }) => `/billing-schedules/${id}`,
  queryKey: [BILLING_SCHEDULES],
  mocking: {
    match: /\/api\/billing-schedules\/(.+)/,
    mock: {
      id: "b102e007-f5d7-4b1a-8a1d-9a51135ae73b",
      customerId: "cca32597-b6b0-4499-9669-128aef8702e4",
      status: "ACTIVE",
      startDate: "2022-01-01",
      endDate: "2022-12-01",
      taxRates: [],
      autoIssueInvoices: false,
      purchaseOrderNumber: "PO123",
      reference: "ref",
      label: "label",
      recurrenceDayOfMonth: 1,
      prices: [
        {
          id: "bc61d4b8-e9d6-4f4a-9bcf-f5dae874c730",
          productId: "f409d651-c475-4142-aafd-a5d2bd74d4f9",
          name: "Demo Product",
          currency: "GBP",
          structure: {
            pricePerUnit: "0.25",
            usageMetricId: "04c62961-04ba-48e6-b985-758bb7d3e712",
            isPricePercentage: false,
            pricingType: "LINEAR"
          },
          billingFrequency: "MONTHLY",
          billingType: "IN_ARREARS",
          createdAt: "2022-06-28T16:47:00Z",
          updatedAt: "2022-06-28T16:47:00Z",
          integrationIds: [],
          customMetricParameters: [
            {
              parameterId: "94703530-1293-479b-bd37-ccad42fd9c8b",
              value: "10"
            }
          ],
          listPriceId: "b4d7e884-e007-4183-8355-4771865c5100"
        }
      ],
      createdAt: "2022-06-28T16:47:00Z",
      phases: [
        {
          id: "6dc54805-0ac8-4f17-9341-4d3f2db9dcd8",
          priceIds: ["6dc54805-0ac8-4f17-9341-4d3f2db9dcd8"],
          startDate: "2022-01-01",
          endDate: "2022-06-30",
          name: "Phase name",
          discounts: [
            {
              restrictToPrices: ["6dc54805-0ac8-4f17-9341-4d3f2db9dcd8"],
              type: "PERCENTAGE",
              amount: 0.15,
              message: "15% Discount",
              separateLineItem: true,
              seatDiscountType: "ALL_SEATS"
            }
          ],
          minimums: [
            {
              amount: 100,
              restrictToPrices: ["6dc54805-0ac8-4f17-9341-4d3f2db9dcd8"]
            }
          ],
          recurrencePreference: "CONTINUE_FROM_PREVIOUS_PHASE",
          phasePriceMetadata: []
        }
      ],
      archivedAt: "2022-06-28T16:47:00Z",
      rollUpBilling: false,
      integrationIds: [],
      attachmentAssets: [],
      contractId: "0194d091-f65b-7b98-b75f-a923aa0f5b1a"
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/billingSchedules/getBillingSchedules/getBillingSchedules.api.ts
var getBillingSchedules = createQueryEndpoint({
  queryKey: [BILLING_SCHEDULES],
  route: (params) => `/billing-schedules${createQueryParams(params ?? {})}`,
  mocking: {
    match: /\/api\/billing-schedules(\?(.*))?$/,
    mock: {
      items: [
        {
          id: "schedule-1",
          customerId: "customer-1",
          status: "COMPLETED",
          phases: [
            {
              id: "test-phase",
              priceIds: ["test-price"],
              startDate: "2022-01-01",
              endDate: "2022-01-31",
              discounts: [
                {
                  restrictToPrices: ["test-price"],
                  type: "PERCENTAGE",
                  amount: 10,
                  message: "test-message",
                  seatDiscountType: "ALL_SEATS"
                }
              ],
              minimums: [
                {
                  restrictToPrices: ["test-price"],
                  amount: 10
                }
              ],
              recurrencePreference: "RESET",
              phasePriceMetadata: [
                {
                  priceId: "test-price",
                  arrCalculation: "INCLUDE"
                }
              ]
            }
          ],
          prices: [],
          autoIssueInvoices: false,
          purchaseOrderNumber: "test-purchase-order",
          reference: "test-reference",
          label: "test-label",
          recurrenceDayOfMonth: 1,
          rollUpBilling: false,
          attachmentAssets: [],
          integrationIds: [],
          startDate: "2022-01-01",
          endDate: "2022-01-31",
          taxRates: [],
          createdAt: "2022-01-01T00:00:00Z"
        }
      ],
      pagination: {
        after: "",
        before: "",
        totalResultSize: 1
      }
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/billingSchedules/getBillingSchedulesCsvDownload/getBillingSchedulesCsvDownload.api.ts
var getBillingSchedulesCsvDownload = createQueryEndpoint({
  route: (params) => `/billing-schedules/billing-schedules-csv${createQueryParams(params)}`,
  config: { responseTransform: (res) => res.blob() },
  queryKey: [BILLING_SCHEDULES_CSV_DOWNLOAD],
  mocking: {
    match: /\/api\/billing-schedules\/billing-schedules-csv(\?(.*))?$/,
    mock: new Blob([""], { type: "text/csv" })
  }
});

// src/clients/dashboard/v20240730/endpoints/billingSchedules/getBillingScheduleSettings/getBillingScheduleSettings.api.ts
var getBillingScheduleSettings = createQueryEndpoint({
  route: ({ billingScheduleId }) => `/payments/settings/billing-schedules/for-billing-schedule/${billingScheduleId}`,
  queryKey: [BILLING_SCHEDULES_SETTINGS],
  mocking: {
    match: /\/api\/payments\/settings\/billing-schedules\/for-billing-schedule\/(.+)/,
    mock: {
      id: "settings-1",
      billingScheduleId: "schedule-1",
      paymentProvider: "STRIPE",
      autoCharge: false
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/prices/prices.cacheKeys.ts
var PRICES = "prices";
var PRICE_METADATA = "priceMetadata";

// src/clients/dashboard/v20240730/endpoints/billingSchedules/postBillingSchedule/postBillingSchedule.api.ts
var postBillingSchedule = createMutationEndpoint({
  route: () => `/billing-schedules`,
  method: "POST",
  body: ({ body }) => JSON.stringify(body),
  invalidateQueries: [BILLING_SCHEDULES, PRICE_METADATA],
  mocking: {
    match: /\/api\/billing-schedules$/,
    mock: {
      id: "schedule-1",
      endDate: "2022-01-31",
      prices: [],
      createdAt: "2022-01-01T00:00:00Z",
      customerId: "customer-1",
      status: "COMPLETED",
      integrationIds: [],
      phases: [
        {
          id: "test-phase",
          priceIds: ["test-price"],
          startDate: "2022-01-01",
          endDate: "2022-01-31",
          discounts: [
            {
              restrictToPrices: ["test-price"],
              type: "PERCENTAGE",
              amount: 10,
              message: "test-message",
              separateLineItem: true,
              seatDiscountType: "ALL_SEATS"
            }
          ],
          minimums: [
            {
              restrictToPrices: ["test-price"],
              amount: 10
            }
          ],
          recurrencePreference: "CONTINUE_FROM_PREVIOUS_PHASE",
          phasePriceMetadata: [
            {
              priceId: "test-price",
              arrCalculation: "INCLUDE"
            }
          ]
        }
      ],
      startDate: "2022-01-01",
      taxRates: [
        {
          priceId: "test-price",
          taxRateId: "test-tax-rate"
        }
      ],
      autoIssueInvoices: false,
      purchaseOrderNumber: "test-purchase-order",
      reference: "test-reference",
      label: "test-label",
      recurrenceDayOfMonth: 1,
      rollUpBilling: false
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/billingSchedules/postBillingScheduleArchive/postBillingScheduleArchive.api.ts
var postBillingScheduleArchive = createMutationEndpoint({
  route: ({ id }) => `/billing-schedules/${id}/archive`,
  method: "POST",
  invalidateQueries: [BILLING_SCHEDULES],
  mocking: {
    match: /\/api\/billing-schedules\/(.+)\/archive$/,
    mock: {
      customerId: "customer-1",
      endDate: "2022-01-31",
      integrationIds: [],
      status: "COMPLETED",
      phases: [
        {
          id: "test-phase",
          priceIds: ["test-price"],
          startDate: "2022-01-01",
          endDate: "2022-01-31",
          discounts: [
            {
              restrictToPrices: ["test-price"],
              type: "PERCENTAGE",
              amount: 10,
              message: "test-message",
              separateLineItem: true,
              seatDiscountType: "ALL_SEATS"
            }
          ],
          minimums: [
            {
              restrictToPrices: ["test-price"],
              amount: 10
            }
          ],
          recurrencePreference: "CONTINUE_FROM_PREVIOUS_PHASE",
          phasePriceMetadata: [
            {
              priceId: "test-price",
              arrCalculation: "INCLUDE"
            }
          ]
        }
      ],
      startDate: "2022-01-01",
      taxRates: [
        {
          priceId: "test-price",
          taxRateId: "test-tax-rate"
        }
      ],
      autoIssueInvoices: false,
      purchaseOrderNumber: "test-purchase-order",
      reference: "test-reference",
      label: "test-label",
      recurrenceDayOfMonth: 1,
      rollUpBilling: false,
      id: "schedule-1",
      createdAt: "2022-01-01T00:00:00Z",
      prices: [],
      attachmentAssets: [
        {
          id: "test-asset-id",
          type: "IMAGE",
          format: "PDF",
          fileName: "my-image.png",
          fileSizeBytes: 12345,
          url: `https://assets.sequencehq.com/123/images/test-asset-id.png`,
          createdBy: "upload-user-1",
          createdAt: "2025-03-11T00:00:00.000Z"
        }
      ]
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/billingSchedules/postBillingScheduleBillingPreview/postBillingScheduleBillingPreview.api.ts
var postBillingScheduleBillingPreview = createQueryEndpoint({
  route: () => `/billing-schedules/preview`,
  method: "POST",
  body: ({ body }) => JSON.stringify(body),
  queryKey: [BILLING_PREVIEW],
  mocking: {
    match: /\/api\/billing-schedules\/preview$/,
    mock: {
      occurrences: [
        {
          start: "2022-01-01",
          end: "2022-01-31"
        },
        {
          start: "2022-02-01",
          end: "2022-02-28"
        },
        {
          start: "2022-03-01",
          end: "2022-03-31"
        }
      ]
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/billingSchedules/postBillingScheduleSettings/postBillingScheduleSettings.api.ts
var postBillingScheduleSettings = createMutationEndpoint({
  route: () => `/payments/settings/billing-schedules`,
  method: "POST",
  body: ({ body }) => JSON.stringify(body),
  invalidateQueries: [BILLING_SCHEDULES_SETTINGS],
  mocking: {
    match: /\/api\/payments\/settings\/billing-schedules$/,
    mock: {
      id: "settings-1",
      billingScheduleId: "schedule-1",
      paymentProvider: "STRIPE",
      autoCharge: false
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/billingSchedules/postBillingSchedulesValidateTaxes/postBillingSchedulesValidateTaxes.api.ts
var postBillingScheduleValidateTaxes = createQueryEndpoint({
  route: () => `/billing-schedules/validate-taxes`,
  method: "POST",
  body: ({ body }) => JSON.stringify(body),
  queryKey: [],
  mocking: {
    match: /\/api\/billing-schedules\/validate-taxes/,
    mock: null
  }
});

// src/clients/dashboard/v20240730/endpoints/billingSchedules/putBillingSchedule/putBillingSchedule.api.ts
var putBillingSchedule = createMutationEndpoint({
  route: ({ id }) => `/billing-schedules/${id}`,
  method: "PUT",
  /**
   * The type is dangerous as it can cause phase saving to break - we always
   * want to save as phases so always set the type to PHASES. The type shouldn't
   * be required, but for now we manually set it.
   */
  body: (params) => JSON.stringify({ ...params.body, type: "PHASES" }),
  invalidateQueries: [BILLING_SCHEDULES, PRICE_METADATA],
  mocking: {
    match: /\/api\/billing-schedules\/(.+)$/,
    mock: {
      customerId: "customer-1",
      endDate: "2022-01-31",
      status: "COMPLETED",
      integrationIds: [],
      phases: [
        {
          id: "test-phase",
          priceIds: ["test-price"],
          startDate: "2022-01-01",
          endDate: "2022-01-31",
          discounts: [
            {
              restrictToPrices: ["test-price"],
              type: "PERCENTAGE",
              amount: 10,
              message: "test-message",
              separateLineItem: true,
              seatDiscountType: "ALL_SEATS"
            }
          ],
          minimums: [
            {
              restrictToPrices: ["test-price"],
              amount: 10
            }
          ],
          recurrencePreference: "RESET",
          phasePriceMetadata: []
        }
      ],
      startDate: "2022-01-01",
      taxRates: [
        {
          priceId: "test-price",
          taxRateId: "test-tax-rate"
        }
      ],
      autoIssueInvoices: false,
      purchaseOrderNumber: "test-purchase-order",
      reference: "test-reference",
      label: "test-label",
      recurrenceDayOfMonth: 1,
      rollUpBilling: false,
      id: "schedule-1",
      createdAt: "2022-01-01T00:00:00Z",
      prices: [],
      attachmentAssets: [
        {
          id: "test-asset-id",
          type: "IMAGE",
          format: "PDF",
          fileName: "my-image.png",
          fileSizeBytes: 12345,
          url: `https://assets.sequencehq.com/123/images/test-asset-id.png`,
          createdBy: "upload-user-1",
          createdAt: "2025-03-11T00:00:00.000Z"
        }
      ]
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/billingSchedules/putBillingScheduleActivate/putBillingScheduleActivate.api.ts
var putBillingScheduleActivate = createMutationEndpoint({
  route: ({ id }) => `/billing-schedules/${id}/activate`,
  method: "PUT",
  invalidateQueries: [BILLING_SCHEDULES],
  mocking: {
    match: /\/api\/billing-schedules\/(.+)\/activate$/,
    mock: {
      customerId: "customer-1",
      endDate: "2022-01-31",
      integrationIds: [],
      status: "COMPLETED",
      phases: [
        {
          id: "test-phase",
          priceIds: ["test-price"],
          startDate: "2022-01-01",
          endDate: "2022-01-31",
          discounts: [
            {
              restrictToPrices: ["test-price"],
              type: "PERCENTAGE",
              amount: 10,
              message: "test-message",
              separateLineItem: true,
              seatDiscountType: "ALL_SEATS"
            }
          ],
          minimums: [
            {
              restrictToPrices: ["test-price"],
              amount: 10
            }
          ],
          recurrencePreference: "CONTINUE_FROM_PREVIOUS_PHASE",
          phasePriceMetadata: [
            {
              priceId: "test-price",
              arrCalculation: "EXCLUDE"
            }
          ]
        }
      ],
      startDate: "2022-01-01",
      taxRates: [
        {
          priceId: "test-price",
          taxRateId: "test-tax-rate"
        }
      ],
      autoIssueInvoices: false,
      purchaseOrderNumber: "test-purchase-order",
      reference: "test-reference",
      label: "test-label",
      recurrenceDayOfMonth: 1,
      rollUpBilling: false,
      id: "schedule-1",
      createdAt: "2022-01-01T00:00:00Z",
      prices: [],
      attachmentAssets: [
        {
          id: "test-asset-id",
          type: "IMAGE",
          format: "PDF",
          fileName: "my-image.png",
          fileSizeBytes: 12345,
          url: `https://assets.sequencehq.com/123/images/test-asset-id.png`,
          createdBy: "upload-user-1",
          createdAt: "2025-03-11T00:00:00.000Z"
        }
      ]
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/billingSchedules/putBillingScheduleSettings/putBillingScheduleSettings.api.ts
var putBillingScheduleSettings = createMutationEndpoint({
  route: ({ id }) => `/payments/settings/billing-schedules/${id}`,
  method: "PUT",
  body: (params) => JSON.stringify(params.body),
  invalidateQueries: [BILLING_SCHEDULES_SETTINGS],
  mocking: {
    match: /\/api\/payments\/settings\/billing-schedules\/(.+)$/,
    mock: {
      id: "settings-1",
      billingScheduleId: "schedule-1",
      paymentProvider: "STRIPE",
      autoCharge: false
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/billingSchedules/postDuplicateBillingSchedule/postDuplicateBillingSchedule.api.ts
var postDuplicateBillingSchedule = createMutationEndpoint({
  route: ({ id }) => `/billing-schedules/${id}/duplicate`,
  body: ({ customerId }) => JSON.stringify({ customerId }),
  method: "POST",
  invalidateQueries: [BILLING_SCHEDULES],
  mocking: {
    match: /\/api\/billing-schedules\/[^\/]+\/duplicate$/,
    mock: {
      id: "b102e007-f5d7-4b1a-8a1d-9a51135ae73b",
      customerId: "cca32597-b6b0-4499-9669-128aef8702e4",
      status: "ACTIVE",
      startDate: "2022-01-01",
      endDate: "2022-12-01",
      taxRates: [],
      autoIssueInvoices: false,
      purchaseOrderNumber: "PO123",
      reference: "ref",
      label: "label",
      recurrenceDayOfMonth: 1,
      prices: [
        {
          id: "bc61d4b8-e9d6-4f4a-9bcf-f5dae874c730",
          productId: "f409d651-c475-4142-aafd-a5d2bd74d4f9",
          name: "Demo Product",
          currency: "GBP",
          structure: {
            pricePerUnit: "0.25",
            usageMetricId: "04c62961-04ba-48e6-b985-758bb7d3e712",
            isPricePercentage: false,
            pricingType: "LINEAR"
          },
          billingFrequency: "MONTHLY",
          billingType: "IN_ARREARS",
          createdAt: "2022-06-28T16:47:00Z",
          updatedAt: "2022-06-28T16:47:00Z",
          integrationIds: [],
          customMetricParameters: [
            {
              parameterId: "94703530-1293-479b-bd37-ccad42fd9c8b",
              value: "10"
            }
          ],
          listPriceId: "b4d7e884-e007-4183-8355-4771865c5100"
        }
      ],
      createdAt: "2022-06-28T16:47:00Z",
      phases: [
        {
          id: "6dc54805-0ac8-4f17-9341-4d3f2db9dcd8",
          priceIds: ["6dc54805-0ac8-4f17-9341-4d3f2db9dcd8"],
          startDate: "2022-01-01",
          endDate: "2022-06-30",
          name: "Phase name",
          discounts: [
            {
              restrictToPrices: ["6dc54805-0ac8-4f17-9341-4d3f2db9dcd8"],
              type: "PERCENTAGE",
              amount: 0.15,
              message: "15% Discount",
              separateLineItem: true,
              seatDiscountType: "ALL_SEATS"
            }
          ],
          minimums: [
            {
              amount: 100,
              restrictToPrices: ["6dc54805-0ac8-4f17-9341-4d3f2db9dcd8"]
            }
          ],
          recurrencePreference: "CONTINUE_FROM_PREVIOUS_PHASE",
          phasePriceMetadata: []
        }
      ],
      archivedAt: "2022-06-28T16:47:00Z",
      rollUpBilling: false,
      integrationIds: [],
      attachmentAssets: [],
      contractId: "0194d091-f65b-7b98-b75f-a923aa0f5b1a"
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/billingSchedules/index.ts
var schedulesEndpoints = {
  getBillingSchedule,
  getBillingSchedules,
  getBillingSchedulesCsvDownload,
  getBillingScheduleSettings,
  postBillingSchedule,
  postBillingScheduleBillingPreview,
  postBillingScheduleSettings,
  postBillingScheduleValidateTaxes,
  putBillingSchedule,
  postBillingScheduleArchive,
  putBillingScheduleSettings,
  putBillingScheduleActivate,
  postDuplicateBillingSchedule
};

// src/clients/dashboard/v20240730/endpoints/savedViews/savedViews.cacheKeys.ts
var SAVED_VIEWS = "savedViews";

// src/clients/dashboard/v20240730/endpoints/savedViews/getSavedViews/getSavedViews.api.ts
var getSavedViews = createQueryEndpoint({
  route: () => "/saved-views",
  queryKey: [SAVED_VIEWS],
  mocking: {
    match: /\/api\/saved-views(\?(.*))?$/,
    mock: {
      items: [
        {
          id: "saved-view-1",
          entityType: "INVOICES",
          name: "Saved View 1",
          activeFilters: [
            {
              paramName: "customerId",
              type: "multiSelect",
              values: ["01946a39-eaf0-7be0-a647-a19d3cfbb5ef"]
            },
            {
              type: "datePreset",
              paramName: "due",
              preset: "TODAY",
              operator: "EQUALS"
            }
          ],
          createdAt: "2025-01-28T00:00:00.000Z",
          updatedAt: "2025-01-28T00:00:00.000Z"
        }
      ]
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/savedViews/postSavedView/postSavedView.api.ts
var postSavedView = createMutationEndpoint({
  route: () => "/saved-views",
  method: "POST",
  body: ({ body }) => JSON.stringify(body),
  invalidateQueries: [SAVED_VIEWS],
  mocking: {
    match: /\/api\/saved-views$/,
    mock: {
      id: "saved-view-2",
      entityType: "INVOICES",
      name: "Saved View 2",
      activeFilters: [
        {
          paramName: "invoicePaymentStatus",
          type: "multiSelect",
          values: ["PAID"]
        },
        {
          paramName: "invoiceCurrency",
          type: "multiSelect",
          values: ["EUR"]
        }
      ],
      createdAt: "2025-01-28T00:00:00.000Z",
      updatedAt: "2025-01-28T00:00:00.000Z"
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/savedViews/putSavedView/putSavedView.api.ts
var putSavedView = createMutationEndpoint({
  route: ({ id }) => `/saved-views/${id}`,
  method: "PUT",
  body: ({ body }) => JSON.stringify(body),
  invalidateQueries: [SAVED_VIEWS],
  mocking: {
    match: /\/api\/saved-views\/(.+)$/,
    mock: {
      id: "updated-saved-view",
      entityType: "INVOICES",
      name: "Updated Saved View",
      activeFilters: [
        {
          paramName: "invoicePaymentStatus",
          type: "multiSelect",
          values: ["PAID"]
        },
        {
          paramName: "invoiceCurrency",
          type: "multiSelect",
          values: ["EUR"]
        },
        {
          paramName: "invoiceStatus",
          type: "multiSelect",
          values: ["DRAFT"]
        }
      ],
      createdAt: "2025-01-28T00:00:00.000Z",
      updatedAt: "2025-01-28T00:00:00.000Z"
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/savedViews/deleteSavedView/deleteSavedView.api.ts
var deleteSavedView = createMutationEndpoint({
  route: ({ id }) => `/saved-views/${id}`,
  method: "DELETE",
  invalidateQueries: [SAVED_VIEWS],
  mocking: {
    match: /\/api\/saved-views\/(.+)$/,
    mock: {
      id: "deleted-saved-view",
      entityType: "INVOICES",
      name: "Deleted Saved View",
      activeFilters: [
        {
          paramName: "customerId",
          type: "multiSelect",
          values: ["01946a39-eaf0-7be0-a647-a19d3cfbb5ef"]
        },
        {
          type: "datePreset",
          paramName: "due",
          preset: "TODAY",
          operator: "EQUALS"
        }
      ],
      createdAt: "2025-01-28T00:00:00.000Z",
      updatedAt: "2025-01-28T00:00:00.000Z"
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/savedViews/index.ts
var savedViewsEndpoints = {
  getSavedViews,
  postSavedView,
  putSavedView,
  deleteSavedView
};

// src/clients/dashboard/v20240730/endpoints/quoteTemplates/quoteTemplates.cacheKeys.ts
var QUOTE_TEMPLATES = "quote-templates";

// src/clients/dashboard/v20240730/endpoints/quoteTemplates/getListQuoteTemplates/getListQuoteTemplates.api.ts
var getListQuoteTemplates = createQueryEndpoint({
  route: () => `/quote-templates`,
  queryKey: [QUOTE_TEMPLATES],
  mocking: {
    match: /\/api\/quote-templates$/,
    mock: {
      items: [
        {
          id: "1",
          title: "Test Quote Template",
          currency: "USD",
          /**
           * Pact still expects this to be an array, but it can an object or array.
           */
          presentation: [],
          dealType: "NEW_BUSINESS",
          createdAt: "2022-01-01T00:00:00Z",
          createdBy: "1",
          updatedAt: "2022-01-01T00:00:00Z"
        }
      ],
      pagination: {
        after: "",
        before: "",
        totalResultSize: 1
      }
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/quoteTemplates/getQuoteTemplate/getQuoteTemplate.api.ts
var getQuoteTemplate = createQueryEndpoint({
  route: ({ id }) => `/quote-templates/${id}`,
  queryKey: [QUOTE_TEMPLATES],
  mocking: {
    match: /\/api\/quote-templates\/(.+)$/,
    mock: {
      template: {
        id: "1",
        title: "Test Quote Template",
        currency: "USD",
        /**
         * Pact still expects this to be an array, but it can an object or array.
         */
        presentation: [],
        dealType: "NEW_BUSINESS",
        createdAt: "2022-01-01T00:00:00Z",
        createdBy: "1",
        updatedAt: "2022-01-01T00:00:00Z"
      },
      attachmentAssets: [],
      phases: []
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/quoteTemplates/postArchiveQuoteTemplate/postArchiveQuoteTemplate.api.ts
var postArchiveQuoteTemplate = createMutationEndpoint({
  route: ({ id }) => `/quote-templates/${id}/archive`,
  method: "POST",
  invalidateQueries: [QUOTE_TEMPLATES],
  mocking: {
    match: /\/api\/quote-templates\/(.+)\/archive$/,
    mock: {
      template: {
        id: "1",
        title: "Test Quote Template",
        currency: "USD",
        presentation: [],
        dealType: "NEW_BUSINESS",
        createdAt: "2022-01-01T00:00:00Z",
        createdBy: "1",
        updatedAt: "2022-01-01T00:00:00Z"
      },
      attachmentAssets: [],
      phases: []
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/quoteTemplates/postCreateQuoteFromTemplate/postCreateQuoteFromTemplate.api.ts
var postCreateQuoteFromTemplate = createMutationEndpoint({
  route: ({ id }) => `/quotes/from-template/${id}`,
  method: "POST",
  invalidateQueries: [QUOTES],
  mocking: {
    match: /\/api\/quotes\/from-template\/(.+)$/,
    mock: EXAMPLE_QUOTE
  }
});

// src/clients/dashboard/v20240730/endpoints/quoteTemplates/postCreateQuoteTemplate/postCreateQuoteTemplate.api.ts
var postCreateQuoteTemplate = createMutationEndpoint({
  route: () => `/quote-templates`,
  method: "POST",
  invalidateQueries: [QUOTE_TEMPLATES],
  body: ({ quoteId } = {}) => quoteId ? JSON.stringify({ quoteId }) : JSON.stringify({}),
  mocking: {
    match: /\/api\/quote-templates$/,
    mock: {
      template: {
        id: "1",
        title: "Test Quote Template",
        currency: "USD",
        presentation: [],
        dealType: "NEW_BUSINESS",
        createdAt: "2022-01-01T00:00:00Z",
        createdBy: "1",
        updatedAt: "2022-01-01T00:00:00Z"
      },
      attachmentAssets: [],
      phases: []
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/quoteTemplates/postCreateQuoteTemplateFromQuote/postCreateQuoteTemplateFromQuote.api.ts
var postCreateQuoteTemplateFromQuote = createMutationEndpoint({
  route: ({ id }) => `/quote-templates/from-quote/${id}`,
  method: "POST",
  invalidateQueries: [QUOTES, QUOTE_TEMPLATES],
  mocking: {
    match: /\/api\/quote-templates\/from-quote\/(.+)$/,
    mock: {
      template: {
        id: "1",
        title: "Test Quote Template",
        currency: "USD",
        presentation: [],
        dealType: "NEW_BUSINESS",
        createdAt: "2022-01-01T00:00:00Z",
        createdBy: "1",
        updatedAt: "2022-01-01T00:00:00Z"
      },
      attachmentAssets: [],
      phases: []
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/quoteTemplates/postDuplicateQuoteTemplate/postDuplicateQuoteTemplate.api.ts
var postDuplicateQuoteTemplate = createMutationEndpoint({
  route: ({ id }) => `/quote-templates/${id}/duplicate`,
  method: "POST",
  invalidateQueries: [QUOTE_TEMPLATES],
  mocking: {
    match: /\/api\/quote-templates\/(.+)\/duplicate$/,
    mock: {
      template: {
        id: "1",
        title: "Test Quote Template",
        currency: "USD",
        presentation: [],
        dealType: "NEW_BUSINESS",
        createdAt: "2022-01-01T00:00:00Z",
        createdBy: "1",
        updatedAt: "2022-01-01T00:00:00Z"
      },
      attachmentAssets: [],
      phases: []
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/quoteTemplates/putUpdateQuoteTemplate/putUpdateQuoteTemplate.api.ts
var putUpdateQuoteTemplate = createMutationEndpoint({
  route: ({ id }) => `/quote-templates/${id}`,
  method: "PUT",
  invalidateQueries: [QUOTE_TEMPLATES],
  body: ({ body }) => JSON.stringify({
    template: body.template,
    phases: body.phases,
    attachmentAssetIds: body.attachmentAssetIds
  }),
  mocking: {
    match: /\/api\/quote-templates\/(.+)$/,
    mock: {
      template: {
        id: "1",
        title: "Test Quote Template",
        currency: "USD",
        presentation: [],
        dealType: "NEW_BUSINESS",
        createdAt: "2022-01-01T00:00:00Z",
        createdBy: "1",
        updatedAt: "2022-01-01T00:00:00Z"
      },
      phases: [],
      attachmentAssets: []
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/quoteTemplates/index.ts
var quoteTemplatesEndpoints = {
  getQuoteTemplate,
  getListQuoteTemplates,
  postArchiveQuoteTemplate,
  postCreateQuoteFromTemplate,
  postCreateQuoteTemplate,
  putUpdateQuoteTemplate,
  postCreateQuoteTemplateFromQuote,
  postDuplicateQuoteTemplate
};

// src/clients/dashboard/v20240730/endpoints/usageEvents/usageEvents.cacheKeys.ts
var USAGE_EVENTS = "usageEvents";
var USAGE_EVENT_PROPERTIES = "usageEventProperties";
var USAGE_EVENT_TYPES = "usageEventTypes";

// src/clients/dashboard/v20240730/endpoints/usageEvents/getUniqueUsageEventProperties/getUniqueUsageEventProperties.api.ts
var getUniqueUsageEventProperties = createQueryEndpoint({
  route: (params) => `/unique-usage-event-properties${createQueryParams(params)}`,
  queryKey: [USAGE_EVENT_PROPERTIES],
  mocking: {
    match: /\/api\/unique-usage-event-properties(\?(.*))?$/,
    mock: {
      items: ["transactions"]
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/usageEvents/getUsageEvents/getUsageEvents.api.ts
var getUsageEvents = createQueryEndpoint({
  route: (params) => {
    const { customerAlias, ...rest } = params || {};
    if (customerAlias) {
      const allCustomAliases = customerAlias.split(",");
      const customAliasQueryParams = allCustomAliases.map((alias) => `customerAlias=${alias}`).join("&");
      const restQueryParams = createQueryParams(rest);
      if (restQueryParams) {
        return `/usage-events${restQueryParams}&${customAliasQueryParams}`;
      }
      return `/usage-events?${customAliasQueryParams}`;
    }
    return `/usage-events${createQueryParams(params)}`;
  },
  queryKey: [USAGE_EVENTS],
  mocking: {
    match: /\/api\/usage-events(\?(.*))?$/,
    mock: {
      items: [
        {
          id: "01849e38-bbb0-7fab-86eb-62865adf75c7",
          customerEventId: "eventid-33bb8cbb-430f-43d1-b26a-29a9e534e598",
          customerAlias: "7d9249ce-aa44-45fe-99dd-2847dd4b3112",
          eventType: "message_sent",
          eventTimestamp: "2022-06-28T16:47:00Z",
          eventProperties: { type: "abcd", length: 1 },
          sequenceAccountId: "78c7558d-ae00-43a0-8b56-e1a27909550d"
        }
      ],
      pagination: {
        after: "",
        before: "",
        totalResultSize: 1
      }
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/usageEvents/getUniqueUsageEventTypes/getUniqueUsageEventTypes.api.ts
var getUniqueUsageEventTypes = createQueryEndpoint({
  route: (params) => `/unique-usage-event-types${createQueryParams(params)}`,
  queryKey: [USAGE_EVENT_TYPES],
  mocking: {
    match: /\/api\/unique-usage-event-types(\?(.*))?$/,
    mock: {
      items: ["transactions"]
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/usageEvents/getUsageEvent/getUsageEvent.api.ts
var getUsageEvent = createQueryEndpoint(
  {
    route: ({ id }) => `/usage-events/${id}`,
    queryKey: [USAGE_EVENTS],
    mocking: {
      match: /\/api\/usage-events\/(.*)$/,
      mock: {
        id: "0184421a-8202-70ea-a729-30308a40bbf7",
        customerEventId: "event-id-H4twuTWpYx1rkd8OMTki2hTUcZ",
        customerAlias: "customer-id-2H4u5BBwBWsS5V2sroRFqJfTXpW",
        eventType: "message-sent",
        sequenceAccountId: "0184421e-a9cc-711b-af93-bf0511d4d833",
        eventTimestamp: "2022-10-01T00:00:00Z",
        eventProperties: {}
      }
    }
  }
);

// src/clients/dashboard/v20240730/endpoints/usageEvents/postUsageEvent/postUsageEvent.api.ts
var postUsageEvent = createMutationEndpoint({
  route: () => `/usage-events`,
  invalidateQueries: [USAGE_EVENTS],
  method: "POST",
  body: (params) => JSON.stringify(params),
  mocking: {
    match: /\/api\/usage-events$/,
    mock: {
      id: "0184421a-8202-70ea-a729-30308a40bbf7",
      customerEventId: "event-id-H4twuTWpYx1rkd8OMTki2hTUcZ",
      customerAlias: "customer-id-2H4u5BBwBWsS5V2sroRFqJfTXpW",
      eventType: "message-sent",
      sequenceAccountId: "0184421e-a9cc-711b-af93-bf0511d4d833",
      eventTimestamp: "2022-10-01T00:00:00Z",
      eventProperties: {
        length: 500,
        type: "text_message"
      }
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/usageEvents/index.ts
var usageEventsEndpoints = {
  getUniqueUsageEventProperties,
  getUsageEvents,
  getUniqueUsageEventTypes,
  getUsageEvent,
  postUsageEvent
};

// src/clients/dashboard/v20240730/endpoints/usageMetrics/usageMetrics.cacheKeys.ts
var USAGE_METRICS = "usageMetrics";
var USAGE_METRICS_CALCULATION = "usageMetricsCalculation";

// src/clients/dashboard/v20240730/endpoints/usageMetrics/getUsageMetric/getUsageMetric.api.ts
var getUsageMetric = createQueryEndpoint({
  route: ({ id }) => `/usage-metrics/${id}`,
  queryKey: [USAGE_METRICS],
  mocking: {
    match: /\/api\/usage-metrics\/([^\/]+)$/,
    mock: {
      id: "01849e38-bbb0-7fab-86eb-62865adf75c7",
      name: "name",
      description: "desc",
      metricType: "SIMPLE",
      eventType: "event",
      aggregationType: "SUM",
      createdAt: "2022-06-28T16:47:00Z",
      propertyFilters: {},
      caseSensitive: false,
      propertiesToNegate: [],
      parameters: [],
      sequenceAccountId: "78c7558d-ae00-43a0-8b56-e1a27909550d",
      aggregationProperty: void 0,
      groupingProperty: void 0,
      unit: void 0
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/usageMetrics/getUsageMetrics/getUsageMetrics.api.ts
var getUsageMetrics = createQueryEndpoint({
  route: (params) => `/usage-metrics${createQueryParams(params)}`,
  queryKey: [USAGE_METRICS],
  mocking: {
    match: /\/api\/usage-metrics(\?(.*))?$/,
    mock: {
      items: [
        {
          id: "01849e38-bbb0-7fab-86eb-62865adf75c7",
          name: "name",
          description: "desc",
          metricType: "SIMPLE",
          eventType: "event",
          aggregationType: "SUM",
          createdAt: "2022-06-28T16:47:00Z",
          propertyFilters: {},
          caseSensitive: false,
          propertiesToNegate: [],
          parameters: [],
          sequenceAccountId: "78c7558d-ae00-43a0-8b56-e1a27909550d",
          aggregationProperty: void 0,
          groupingProperty: void 0,
          unit: void 0
        }
      ],
      pagination: {
        after: "",
        before: "",
        totalResultSize: 1
      }
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/usageMetrics/postUsageMetric/postUsageMetric.api.ts
var postUsageMetric = createMutationEndpoint({
  route: () => `/usage-metrics`,
  method: "POST",
  body: ({ body }) => JSON.stringify(body),
  invalidateQueries: [USAGE_METRICS],
  mocking: {
    match: /\/api\/usage-metrics$/,
    mock: {
      name: "API CALL PART 3",
      metricType: "SIMPLE",
      eventType: "aerial_photo_api_call",
      aggregationType: "SUM",
      aggregationProperty: "123",
      propertyFilters: {},
      id: "test-bf64-76ab-9e8b-27b2870cb66d",
      sequenceAccountId: "test-sequence-account-id",
      parameters: [],
      propertiesToNegate: [],
      createdAt: "2021-09-01T00:00:00.000Z",
      caseSensitive: false
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/usageMetrics/putUsageMetric/putUsageMetric.api.ts
var putUsageMetric = createMutationEndpoint({
  route: ({ id }) => `/usage-metrics/${id}`,
  method: "PUT",
  body: ({ body }) => JSON.stringify(body),
  invalidateQueries: [USAGE_METRICS],
  mocking: {
    match: /\/api\/usage-metrics\/(.+)$/,
    mock: {
      id: "test-bf64-76ab-9e8b-27b2870cb66d",
      sequenceAccountId: "test-sequence-account-id",
      parameters: [],
      propertiesToNegate: [],
      createdAt: "2021-09-01T00:00:00.000Z",
      caseSensitive: false,
      name: "API CALL PART 3",
      metricType: "SIMPLE",
      eventType: "aerial_photo_api_call",
      aggregationType: "SUM",
      aggregationProperty: "123",
      propertyFilters: {}
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/usageMetrics/getCalculateUsageMetric/getCalculateUsageMetric.api.ts
var getCalculateUsageMetric = createQueryEndpoint({
  route: ({ id, customParameters, ...params }) => `/usage-metrics/${id}/calculate${createQueryParams({
    ...params,
    customerAliases: params.customerAliases.join(","),
    customParameters: customParameters ? JSON.stringify(customParameters) : void 0
  })}`,
  queryKey: [USAGE_METRICS_CALCULATION],
  mocking: {
    match: /\/api\/usage-metrics\/(.+)\/calculate(\?(.*))?$/,
    mock: {
      name: "total_messages_sent",
      metricType: "GROUPED",
      eventType: "message_sent",
      aggregationType: "SUM",
      eventCount: 5,
      value: 150.035,
      unit: "bytes",
      minEventId: "0183d0f1-6b5a-74d4-9129-1f4b90191666",
      maxEventId: "0183ea03-aca0-7019-b80d-6aacce344e92"
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/usageMetrics/index.ts
var usageMetricEndpoints = {
  getUsageMetric,
  getUsageMetrics,
  postUsageMetric,
  putUsageMetric,
  getCalculateUsageMetric
};

// src/clients/dashboard/v20240730/endpoints/customers/customers.cacheKeys.ts
var CUSTOMERS = "customers";
var CUSTOMER_ALIASES = "customer-aliases";
var CUSTOMERS_CSV_DOWNLOAD = "customers-csv-download";

// src/clients/dashboard/v20240730/endpoints/customers/getAliasesForCustomer/getAliasesForCustomer.api.ts
var getAliasesForCustomer = createQueryEndpoint({
  route: ({ customerId }) => `/customers/${customerId}/aliases`,
  queryKey: [CUSTOMER_ALIASES],
  mocking: {
    match: /\/api\/customers\/[^\/]+\/aliases$/,
    mock: {
      items: [
        {
          id: "0185a140-194a-7e9d-9fae-6a034fd4a725",
          sequenceAccountId: "cd243b67-7d8c-42ee-b19f-b4e41f584415",
          customerId: "0185a142-54ff-7ce5-846a-fc188432da6e",
          value: "customer-alias-123",
          createdAt: "2022-10-01T00:00:00Z"
        }
      ]
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/customers/getCustomer/getCustomer.api.ts
var getCustomer = createQueryEndpoint({
  route: (params) => `/customers/${params.id}`,
  queryKey: [CUSTOMERS],
  mocking: {
    match: /\/api\/customers\/[^\/]+$/,
    mock: {
      contacts: [
        {
          id: "contact-1",
          name: "John Doe",
          email: "foo@bar.com",
          billingPreference: "PRIMARY",
          createdAt: "string",
          updatedAt: "string"
        }
      ],
      createdAt: "string",
      integrationIds: [
        {
          service: "Xero",
          id: "xero-customer-1",
          isPending: false
        }
      ],
      organizations: [
        {
          id: "org-1",
          owner: {
            id: "owner-1",
            name: "Jane Doe"
          },
          members: [
            {
              id: "member-1",
              name: "John Doe"
            }
          ]
        }
      ],
      id: "customer-1",
      legalName: "string",
      address: {
        line1: "string",
        town: "string",
        state: "NY",
        postcode: "string",
        country: "US"
      },
      domain: "example.com",
      url: "string",
      label: "string",
      taxStatus: "TAXED"
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/customers/getCustomerAliases/getCustomerAliases.api.ts
var getCustomerAliases = createQueryEndpoint({
  route: (params) => `/customer-aliases${createQueryParams(params)}`,
  queryKey: [CUSTOMER_ALIASES],
  mocking: {
    match: /\/api\/customer-aliases(\?(.*))?$/,
    mock: {
      items: [
        {
          id: "01849e70-0a0f-72ca-ba07-5557dd734d43",
          sequenceAccountId: "ec1479a2-6ba1-421f-a7de-0853a20d17a1",
          customerId: "01849e70-703e-705a-b59a-d3fd575587e0",
          value: "external-customer-alias-123",
          createdAt: "2022-10-01T00:00:00Z",
          deletedAt: "2022-10-01T00:00:00Z",
          label: "CRM ID",
          archivedAt: "2022-10-01T00:00:00Z"
        }
      ],
      pagination: {
        after: "",
        before: "",
        totalResultSize: 1
      }
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/customers/getCustomers/getCustomers.api.ts
var getCustomers = createQueryEndpoint({
  route: (params) => `/customers${createQueryParams(params)}`,
  queryKey: [CUSTOMERS],
  mocking: {
    match: /\/api\/customers(\?(.*))?$/,
    mock: {
      items: [
        {
          id: "string",
          legalName: "string",
          address: {
            line1: "string",
            town: "string",
            state: "NY",
            postcode: "string",
            country: "US"
          },
          url: "string",
          label: "string",
          integrationIds: [
            {
              service: "Xero",
              id: "string",
              isPending: false
            }
          ],
          taxStatus: "TAXED",
          organizations: [
            {
              id: "org-1",
              owner: {
                id: "owner-1",
                name: "Jane Doe"
              },
              members: [
                {
                  id: "member-1",
                  name: "John Doe"
                }
              ]
            }
          ],
          createdAt: "string"
        }
      ],
      pagination: {
        after: "",
        before: "",
        totalResultSize: 1
      }
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/customers/getCustomersCsvDownload/getCustomersCsvDownload.api.ts
var getCustomersCsvDownload = createQueryEndpoint({
  route: (params) => `/customers/customers-csv${createQueryParams(params)}`,
  config: { responseTransform: (res) => res.blob() },
  queryKey: [CUSTOMERS_CSV_DOWNLOAD],
  mocking: {
    match: /\/api\/customers\/customers-csv(\?(.*))?$/,
    mock: new Blob([""], { type: "text/csv" })
  }
});

// src/clients/dashboard/v20240730/endpoints/customers/postCustomer/postCustomer.api.ts
var postCustomer = createMutationEndpoint({
  route: () => `/customers`,
  method: "POST",
  body: ({ body }) => JSON.stringify(body),
  invalidateQueries: [CUSTOMERS],
  mocking: {
    match: /\/api\/customers$/,
    mock: {
      legalName: "Company Name",
      organizations: [],
      id: "customer-2",
      integrationIds: [],
      createdAt: "2021-01-01T00:00:00Z",
      address: {
        line1: "123 Fake St",
        town: "Sydney",
        postcode: "2000",
        country: "AU"
      },
      taxStatus: "TAXED",
      contacts: [
        {
          id: "123",
          createdAt: "2021-01-01T00:00:00Z",
          updatedAt: "2021-01-01T00:00:00Z",
          name: "John Doe",
          email: "foo@bar.com",
          billingPreference: "PRIMARY"
        },
        {
          id: "456",
          createdAt: "2021-01-01T00:00:00Z",
          updatedAt: "2021-01-01T00:00:00Z",
          name: "Jane Doe",
          email: "bar@foo.com",
          billingPreference: "NONE"
        }
      ]
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/customers/putCustomer/putCustomer.api.ts
var putCustomer = createMutationEndpoint({
  route: ({ id }) => `/customers/${id}`,
  method: "PUT",
  body: ({ body }) => JSON.stringify(body),
  invalidateQueries: [CUSTOMERS],
  mocking: {
    match: /\/api\/customers\/(.+)$/,
    mock: {
      legalName: "Company Name",
      address: {
        line1: "123 Fake St",
        town: "Sydney",
        postcode: "2000",
        country: "AU"
      },
      taxStatus: "TAXED",
      contacts: [
        {
          id: "123",
          name: "John Doe",
          email: "foo@bar.com",
          billingPreference: "PRIMARY",
          createdAt: "2021-01-01T00:00:00Z",
          updatedAt: "2021-01-01T00:00:00Z"
        }
      ],
      id: "customer-2",
      integrationIds: [],
      createdAt: "2021-01-01T00:00:00Z",
      organizations: []
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/customers/postCustomerAlias/postCustomerAlias.api.ts
var postCustomerAlias = createMutationEndpoint({
  route: () => "/customer-aliases",
  method: "POST",
  body: ({ customerId, value, label }) => JSON.stringify({ customerId, value, label }),
  invalidateQueries: [CUSTOMER_ALIASES, CUSTOMERS],
  mocking: {
    match: /\/api\/customer-aliases$/,
    mock: {
      id: "01849e70-0a0f-72ca-ba07-5557dd734d43",
      sequenceAccountId: "ec1479a2-6ba1-421f-a7de-0853a20d17a1",
      customerId: "01849e70-703e-705a-b59a-d3fd575587e0",
      value: "external-customer-alias-123",
      createdAt: "2022-10-01T00:00:00Z",
      deletedAt: "2022-10-01T00:00:00Z",
      label: "CRM ID",
      archivedAt: "2022-10-01T00:00:00Z"
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/customers/getCustomerByAlias/getCustomerByAlias.api.ts
var getCustomerByAlias = createQueryEndpoint({
  route: ({ alias }) => `/customers/by-alias/${alias}`,
  queryKey: [CUSTOMERS],
  mocking: {
    match: /\/api\/customers\/by-alias\/[^\/]+$/,
    mock: {
      id: "e309e948-088e-4d77-b654-cd85498f1555",
      createdAt: "2022-06-28T16:47:00Z",
      legalName: "John Doe",
      contactName: "John Doe",
      address: {
        line1: "Flat 1",
        town: "New York",
        state: "NY",
        postcode: "AB1 2EF",
        country: "US"
      },
      email: "john@example.com",
      telephone: "02010001000",
      url: "https://example.com/",
      label: "Example customer",
      integrationIds: [
        {
          service: "Xero",
          id: "ID",
          lastSynced: "2022-06-28T16:47:00Z",
          isPending: false
        },
        {
          service: "Stripe",
          id: "cus_OCtCxqQDgu1uX9",
          lastSynced: "2022-06-28T16:47:00Z",
          isPending: false
        }
      ],
      archivedAt: "2022-06-28T16:47:00Z",
      billingEmails: ["exampleCorpInvoices@example.com"],
      taxStatus: "TAXED"
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/customers/postArchiveCustomer/postArchiveCustomer.api.ts
var postArchiveCustomer = createMutationEndpoint({
  route: ({ id }) => `/customers/${id}/archive`,
  invalidateQueries: [CUSTOMERS, CUSTOMER_ALIASES],
  method: "POST",
  mocking: {
    match: /\/api\/customers\/(.+)\/archive$/,
    mock: {
      id: "e309e948-088e-4d77-b654-cd85498f1555",
      createdAt: "2021-01-02T10:55:00Z",
      organizations: [
        {
          id: "c0dc4e79-ec52-4811-8cf5-addb2527915c",
          owner: {
            id: "50af5191-05a3-42a5-802a-6b59091157af",
            name: "FinTech Solutions Inc."
          },
          members: [
            {
              id: "e309e948-088e-4d77-b654-cd85498f1555",
              name: "Fintech Payment Processing"
            },
            {
              id: "50af5191-05a3-42a5-802a-6b59091157af",
              name: "Fintech Lending Services"
            }
          ]
        }
      ],
      legalName: "Fintech Payment Processing",
      address: {
        line1: "7500 Bluewater Rd NW",
        town: "Albuquerque",
        state: "NY",
        postcode: "87121",
        country: "US"
      },
      url: "https://example.com/",
      domain: "example.com",
      label: "Example customer",
      integrationIds: [
        {
          service: "Xero",
          id: "2aaff66f-ef30-4599-b192-c168fd4cd226",
          lastSynced: "2024-03-28T16:47:00Z",
          isPending: false
        },
        {
          service: "Stripe",
          id: "cus_OCtCxqQDgu1uX9",
          lastSynced: "2024-03-28T16:47:00Z",
          isPending: false
        }
      ],
      taxStatus: "TAXED",
      contacts: [
        {
          id: "9a1e38a9-4dc9-776f-f974-4e7ee9f4d6cc",
          name: "John Smith",
          email: "john.smith@example.com",
          billingPreference: "NONE",
          createdAt: "2022-06-28T16:47:00Z",
          updatedAt: "2022-06-28T16:47:00Z",
          archivedAt: "2023-06-28T16:47:00Z"
        }
      ]
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/customers/index.ts
var customerEndpoints = {
  getAliasesForCustomer,
  getCustomer,
  getCustomerAliases,
  getCustomers,
  getCustomersCsvDownload,
  postCustomer,
  putCustomer,
  postCustomerAlias,
  getCustomerByAlias,
  postArchiveCustomer
};

// src/clients/dashboard/v20240730/endpoints/assets/postAssets/postAssets.api.ts
import invariant from "tiny-invariant";

// src/clients/dashboard/v20240730/endpoints/assets/assets.cacheKeys.ts
var ASSETS = "assets";

// src/clients/dashboard/v20240730/endpoints/assets/postAssets/postAssets.api.ts
var postAssets = createMutationEndpoint({
  route: () => `/assets`,
  method: "POST",
  body: ({ formData }) => {
    invariant(formData.has("file"), "formData.file is required");
    return formData;
  },
  requestOptions: {
    headers: {
      "Content-Type": null
    }
  },
  invalidateQueries: [ASSETS],
  mocking: {
    match: /\/api\/assets$/,
    mock: {
      id: "123",
      type: "IMAGE",
      url: "https://example.com",
      fileName: "foo.jpg",
      fileSizeBytes: 1e4,
      format: "PDF",
      createdBy: "upload-user-1",
      createdAt: "2025-03-11T00:00:00.000Z"
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/assets/getAsset/getAsset.api.ts
var getAsset = createQueryEndpoint({
  route: ({ id }) => `/assets/${id}`,
  queryKey: [ASSETS],
  mocking: {
    match: /\/api\/assets\/([^\/]+?)$/,
    mock: {
      id: "asset-1",
      type: "DOCUMENT",
      format: "PDF",
      url: "https://example.com/document.pdf",
      fileName: "document.pdf",
      fileSizeBytes: 1234,
      createdAt: "2025-03-11T00:00:00.000Z",
      createdBy: "user-1"
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/assets/index.ts
var assetsEndpoints = {
  postAssets,
  getAsset
};

// src/clients/dashboard/v20240730/endpoints/quickbooks/getQuickbooksAccountPreferences/getQuickbooksAccountPreferences.api.ts
var getQuickbooksAccountPreferences = createQueryEndpoint({
  route: () => `/quickbooks/account-preferences`,
  queryKey: ["quickbooks", "account-preferences"],
  mocking: {
    match: /\/api\/quickbooks\/account-preferences$/,
    mock: {
      multiCurrencyEnabled: true,
      homeCurrency: "CAD"
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/quickbooks/quickbooks.cacheKeys.ts
var QUICKBOOKS_ACCOUNTS = "quickbooksAccounts";

// src/clients/dashboard/v20240730/endpoints/quickbooks/postSyncQuickbooksAccounts/postSyncQuickbooksAccounts.api.ts
var postSyncQuickbooksAccounts = createMutationEndpoint({
  route: () => `/quickbooks/accounts/pull`,
  method: "POST",
  invalidateQueries: [QUICKBOOKS_ACCOUNTS],
  mocking: {
    match: /\/api\/quickbooks\/accounts\/pull$/,
    mock: void 0
  }
});

// src/clients/dashboard/v20240730/endpoints/quickbooks/index.ts
var quickbooksEndpoints = {
  getQuickbooksAccountPreferences,
  postSyncQuickbooksAccounts
};

// src/clients/dashboard/v20240730/endpoints/users/deleteUser/deleteUser.api.ts
var deleteUser_api_exports = {};
__export(deleteUser_api_exports, {
  deleteUser: () => deleteUser
});

// src/clients/dashboard/v20240730/endpoints/users/users.cacheKeys.ts
var USERS = "users";

// src/clients/dashboard/v20240730/endpoints/users/deleteUser/deleteUser.api.ts
var deleteUser = createMutationEndpoint({
  route: ({ id }) => `/users/${id}`,
  method: "DELETE",
  invalidateQueries: [USERS],
  mocking: {
    match: /\/users\/[^\/]+$/,
    mock: {
      id: "user-1",
      email: "foo@bar.com",
      sequenceAccountIds: ["foo"],
      state: "deleted"
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/users/getUser/getUser.api.ts
var getUser_api_exports = {};
__export(getUser_api_exports, {
  getUser: () => getUser
});
var getUser = createQueryEndpoint({
  route: ({ id }) => `/users/${id}`,
  method: "GET",
  queryKey: [USERS],
  mocking: {
    match: /\/users\/[^\/]+$/,
    mock: {
      id: "user-1",
      email: "foo@bar.com",
      sequenceAccountIds: ["foo"],
      state: "active"
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/users/getUsers/getUsers.api.ts
var getUsers_api_exports = {};
__export(getUsers_api_exports, {
  getUsers: () => getUsers
});
var getUsers = createQueryEndpoint({
  route: ({ limit = 100, ...params } = {}) => `/users${createQueryParams({ limit, ...params })}`,
  method: "GET",
  queryKey: [USERS],
  mocking: {
    match: /\/api\/users(\?(.*))?$/,
    mock: {
      items: [
        {
          id: "user-1",
          email: "foo@bar.com",
          sequenceAccountIds: ["foo"],
          state: "active"
        }
      ],
      pagination: {
        after: "",
        before: "",
        totalResultSize: 1
      }
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/users/index.ts
var userEndpoints = {
  getUser,
  getUsers,
  deleteUser
};

// src/clients/dashboard/v20240730/endpoints/auth/deleteOAuthClient/deleteOAuthClient.api.ts
var deleteOAuthClient_api_exports = {};
__export(deleteOAuthClient_api_exports, {
  deleteOAuthClient: () => deleteOAuthClient
});

// src/clients/dashboard/v20240730/endpoints/auth/auth.cacheKeys.ts
var OAUTH_CLIENTS = "oauthClients";

// src/clients/dashboard/v20240730/endpoints/auth/deleteOAuthClient/deleteOAuthClient.api.ts
var deleteOAuthClient = createMutationEndpoint({
  route: ({ id }) => `/oauth-clients/${id}`,
  method: "DELETE",
  invalidateQueries: [OAUTH_CLIENTS],
  mocking: {
    match: /\/api\/oauth-clients\/[^\/]+$/,
    mock: {
      id: "client-1",
      sequenceAccountId: "foo",
      name: "client",
      scopes: ["foo"],
      pinnedApiVersion: "v20240730"
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/auth/getOAuthClients/getOAuthClients.api.ts
var getOAuthClients_api_exports = {};
__export(getOAuthClients_api_exports, {
  getOAuthClients: () => getOAuthClients
});
var getOAuthClients = createQueryEndpoint({
  route: ({ limit = 100, ...params }) => `/oauth-clients${createQueryParams({ limit, ...params })}`,
  queryKey: [OAUTH_CLIENTS],
  mocking: {
    match: /\/api\/oauth-clients(\?(.*))?$/,
    mock: {
      items: [
        {
          id: "7b1f28b9-a4ad-450b-a0ea-fd647d5109b2",
          sequenceAccountId: "c9e249d8-5ac9-49af-b9a3-0da9f0946c3d",
          name: "Client",
          scopes: ["product"],
          pinnedApiVersion: "2024-01-01"
        }
      ],
      pagination: {
        after: "",
        before: "",
        totalResultSize: 1
      }
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/auth/postOAuthClient/postOAuthClient.api.ts
var postOAuthClient_api_exports = {};
__export(postOAuthClient_api_exports, {
  postOAuthClient: () => postOAuthClient
});
var postOAuthClient = createMutationEndpoint({
  route: () => `/oauth-clients`,
  body: (params) => JSON.stringify(params),
  method: "POST",
  invalidateQueries: [OAUTH_CLIENTS],
  mocking: {
    match: /\/api\/oauth-clients$/,
    status: 201,
    mock: {
      id: "6267e4e5-688e-4eda-92d1-45a4031fe5b2",
      name: "seqhq-7e330949-a175-496a-a060-27821656ce34",
      secret: "somerandomsecret"
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/auth/postSession/postSession.api.ts
var postSession_api_exports = {};
__export(postSession_api_exports, {
  postSession: () => postSession
});
var postSession = createMutationEndpoint({
  route: () => "/auth/session",
  method: "POST",
  body: (body) => JSON.stringify(body),
  invalidateQueries: [],
  mocking: {
    match: /\/api\/auth\/session$/,
    mock: {
      session: {
        requestId: "request-id-1",
        memberAuthenticated: true,
        member: {
          id: "member-id-1",
          name: "John Doe",
          email: "",
          emailAddressVerified: true,
          organisationId: "organisation-id-1"
        },
        memberSession: {
          memberSessionId: "session-id-1",
          expiresAt: "2022-10-01T00:00:00Z",
          startedAt: "2022-10-01T00:00:00Z",
          sequenceAccountId: "account-id-1",
          sequenceUserId: "user-id-1"
        },
        sessionJwt: ""
      }
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/auth/index.ts
var authEndpoints = {
  getOAuthClients,
  deleteOAuthClient,
  postOAuthClient,
  postSession
};

// src/clients/dashboard/v20240730/endpoints/sequenceAccount/getMerchantForSequenceAccount/getMerchantForSequenceAccount.api.ts
var getMerchantForSequenceAccount_api_exports = {};
__export(getMerchantForSequenceAccount_api_exports, {
  getMerchantForSequenceAccount: () => getMerchantForSequenceAccount
});

// src/clients/dashboard/v20240730/endpoints/sequenceAccount/sequenceAccount.cacheKeys.ts
var SEQUENCE_ACCOUNT_SETTINGS = "sequenceAccountSettings";
var SEQUENCE_ACCOUNT_MERCHANT = "sequenceAccountMerchant";
var SEQUENCE_ACCOUNT_ID = "sequenceAccountId";
var SEQUENCE_ACCOUNT = "sequenceAccount";
var SEQUENCE_ACCOUNT_MEMBERS = "sequenceAccountMembers";

// src/clients/dashboard/v20240730/endpoints/sequenceAccount/getMerchantForSequenceAccount/getMerchantForSequenceAccount.api.ts
var getMerchantForSequenceAccount = createQueryEndpoint({
  route: () => "/merchant-for-sequence-account",
  queryKey: [SEQUENCE_ACCOUNT_MERCHANT],
  mocking: {
    match: /api\/merchant-for-sequence-account$/,
    mock: {
      id: "8252c275-80ce-4845-abe7-027f5b2daa8c",
      sequenceAccountId: "af9b5988-1d7b-45f6-a188-326df5e59b24",
      invoiceNumberPrefix: "SEQ",
      creditNoteNumberPrefix: "SCN",
      legalCompanyName: "The Merchant",
      address: {
        line1: "Flat 1",
        line2: "123 Fake Street",
        town: "New York",
        state: "NY",
        postcode: "AB1 2EF",
        country: "US"
      },
      phoneNumber: "0800001066",
      email: "donald@example.com",
      defaultDueDateDays: 30,
      lastInvoiceNumber: 1,
      lastCreditNoteNumber: 0,
      bankAccountDetails: [
        {
          key: "GBP",
          value: {
            type: "UK_BANK_ACCOUNT",
            ukBankAccount: {
              sortCode: "123456",
              accountNumber: "12345678"
            }
          }
        },
        {
          key: "EUR",
          value: {
            type: "IBAN",
            iban: "GB33BUKB20201555555555"
          }
        }
      ],
      appUrl: "https://www.example.com/app",
      customerPortalEnabled: true,
      includeBeneficiaryAddressInPaymentDetails: false,
      ccMerchantOnInvoiceEmails: true,
      showUsageInCustomerPortal: true,
      supportEmail: "support@example.com",
      supportUrl: "https://www.example.com/support"
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/sequenceAccount/getSequenceAccountId/getSequenceAccountId.api.ts
var getSequenceAccountId_api_exports = {};
__export(getSequenceAccountId_api_exports, {
  getSequenceAccountId: () => getSequenceAccountId
});
var getSequenceAccountId = createQueryEndpoint({
  route: () => `/sequence-account-id`,
  queryKey: [SEQUENCE_ACCOUNT_ID],
  mocking: {
    match: /\/api\/sequence-account-id$/,
    mock: {
      id: "sequence-account-id-123"
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/sequenceAccount/getSequenceAccountSettings/getSequenceAccountSettings.api.ts
var getSequenceAccountSettings_api_exports = {};
__export(getSequenceAccountSettings_api_exports, {
  getSequenceAccountSettings: () => getSequenceAccountSettings
});
var getSequenceAccountSettings = createQueryEndpoint({
  route: () => `/sequence-account-settings`,
  queryKey: [SEQUENCE_ACCOUNT_SETTINGS],
  mocking: {
    match: /\/api\/sequence-account-settings$/,
    mock: {
      id: "sequence-account-settings-123",
      sequenceAccountId: "sequence-account-123",
      enabledCurrencies: ["USD"]
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/sequenceAccount/putSequenceAccountSettings/putSequenceAccountSettings.api.ts
var putSequenceAccountSettings_api_exports = {};
__export(putSequenceAccountSettings_api_exports, {
  putSequenceAccountSettings: () => putSequenceAccountSettings
});
var putSequenceAccountSettings = createMutationEndpoint({
  route: () => `/sequence-account-settings`,
  method: "PUT",
  body: (params) => JSON.stringify({
    enabledCurrencies: params.enabledCurrencies
  }),
  invalidateQueries: [SEQUENCE_ACCOUNT_SETTINGS],
  mocking: {
    match: /\/api\/sequence-account-settings$/,
    mock: {
      id: "sequence-account-settings-123",
      sequenceAccountId: "sequence-account-123",
      enabledCurrencies: ["USD"]
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/sequenceAccount/getSequenceAccount/getSequenceAccount.api.ts
var getSequenceAccount_api_exports = {};
__export(getSequenceAccount_api_exports, {
  getSequenceAccount: () => getSequenceAccount
});
var getSequenceAccount = createQueryEndpoint({
  route: ({ id }) => `/sequence-accounts/${id}`,
  queryKey: [SEQUENCE_ACCOUNT],
  mocking: {
    match: /\/api\/sequence-accounts\/[^\/]+$/,
    mock: {
      id: "13448448-ce43-4874-8acf-8ff2a8ca24c1",
      createdAt: "2022-06-28T16:47:00Z",
      sequenceOrganisationId: "02441bde-24e8-43d5-8e2d-f3e480778885",
      companyName: "Acme Inc.",
      companyNumber: "GB123987",
      country: "GB",
      sendCustomerNotifications: true,
      alertEmailAddresses: ["alerts@example.com"]
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/sequenceAccount/postSequenceAccountInvite/postSequenceAccountInvite.api.ts
var postSequenceAccountInvite_api_exports = {};
__export(postSequenceAccountInvite_api_exports, {
  postSequenceAccountInvite: () => postSequenceAccountInvite
});
var postSequenceAccountInvite = createMutationEndpoint({
  route: () => `/sequence-accounts/invite`,
  body: ({ email }) => JSON.stringify({ email }),
  invalidateQueries: [SEQUENCE_ACCOUNT_MEMBERS],
  method: "POST",
  mocking: {
    match: /\/api\/sequence-accounts\/invite$/,
    mock: void 0
  }
});

// src/clients/dashboard/v20240730/endpoints/sequenceAccount/putSequenceAccount/putSequenceAccount.api.ts
var putSequenceAccount_api_exports = {};
__export(putSequenceAccount_api_exports, {
  putSequenceAccount: () => putSequenceAccount
});
var putSequenceAccount = createMutationEndpoint({
  route: ({ id }) => `/sequence-accounts/${id}`,
  body: ({ alertEmailAddresses }) => JSON.stringify({ alertEmailAddresses }),
  invalidateQueries: [SEQUENCE_ACCOUNT],
  method: "PUT",
  mocking: {
    match: /\/api\/sequence-accounts\/[^\/]+$/,
    mock: {
      id: "13448448-ce43-4874-8acf-8ff2a8ca24c1",
      createdAt: "2022-06-28T16:47:00Z",
      sequenceOrganisationId: "02441bde-24e8-43d5-8e2d-f3e480778885",
      companyName: "Acme Inc.",
      companyNumber: "GB123987",
      country: "GB",
      sendCustomerNotifications: true,
      alertEmailAddresses: ["alerts@example.com"]
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/sequenceAccount/index.ts
var sequenceAccountEndpoints = {
  getMerchantForSequenceAccount,
  getSequenceAccountId,
  getSequenceAccountSettings,
  putSequenceAccountSettings,
  getSequenceAccount,
  postSequenceAccountInvite,
  putSequenceAccount
};

// src/clients/dashboard/v20240730/endpoints/integrations/getAllIntegrationCustomers/getAllIntegrationCustomers.api.ts
var getAllIntegrationCustomers_api_exports = {};
__export(getAllIntegrationCustomers_api_exports, {
  getAllIntegrationCustomers: () => getAllIntegrationCustomers
});

// src/clients/dashboard/v20240730/endpoints/integrations/integrations.cacheKeys.ts
var INTEGRATIONS = "integrations";
var INTEGRATION_CUSTOMERS = "integrationCustomers";
var LINKED_ENTITIES = "linkedEntities";

// src/clients/dashboard/v20240730/endpoints/integrations/getAllIntegrationCustomers/getAllIntegrationCustomers.api.ts
var getAllIntegrationCustomers = createQueryEndpoint({
  route: () => "/integrations/customers",
  queryKey: [INTEGRATION_CUSTOMERS],
  mocking: {
    match: /\/api\/integrations\/customers$/,
    mock: {
      customers: [
        {
          key: "Xero",
          value: [
            {
              id: "id",
              companyName: "My customer",
              country: "GB",
              isLinked: true,
              invalidReason: "Something's wrong",
              externalUrl: "www.example.com"
            }
          ]
        }
      ]
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/integrations/getIntegration/getIntegration.api.ts
var getIntegration_api_exports = {};
__export(getIntegration_api_exports, {
  getIntegration: () => getIntegration
});
var getIntegration = createQueryEndpoint({
  route: (service) => `/integrations/${service}`,
  queryKey: [INTEGRATIONS],
  mocking: {
    match: /\/api\/integrations\/(.+)$/,
    mock: {
      state: "UNAVAILABLE",
      enabled: false,
      service: "Xero",
      type: "Accounting",
      authFailure: false,
      ledgerAccounts: {
        defaults: {
          chargeAccount: void 0,
          creditAccount: void 0,
          minimumAccount: void 0
        },
        options: [
          {
            id: "id",
            code: "200",
            name: "Sales"
          }
        ]
      },
      taxCodes: {
        defaults: {
          untaxedCustomerTaxCode: "NONE"
        },
        options: [
          {
            name: "None",
            taxType: "NONE"
          }
        ]
      }
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/integrations/deleteLinkedEntity/deleteLinkedEntity.api.ts
var deleteLinkedEntity_api_exports = {};
__export(deleteLinkedEntity_api_exports, {
  deleteLinkedEntity: () => deleteLinkedEntity
});
var deleteLinkedEntity = createMutationEndpoint({
  route: ({ type, id, service }) => `/linked-entities/${type}/${id}/${service}`,
  method: "DELETE",
  invalidateQueries: [LINKED_ENTITIES, INTEGRATIONS, CUSTOMERS],
  mocking: {
    match: /\/api\/linked-entities\/(.+)\/(.+)\/(.+)$/,
    mock: void 0
  }
});

// src/clients/dashboard/v20240730/endpoints/integrations/getIntegrationCustomersService/getIntegrationCustomersService.api.ts
var getIntegrationCustomersService_api_exports = {};
__export(getIntegrationCustomersService_api_exports, {
  getIntegrationCustomersService: () => getIntegrationCustomersService
});
var getIntegrationCustomersService = createQueryEndpoint({
  route: (service) => `/integrations/customers/${service}`,
  queryKey: [INTEGRATION_CUSTOMERS],
  mocking: {
    match: /\/api\/integrations\/customers\/(.+)$/,
    mock: {
      items: []
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/integrations/postPushCreditNoteToExternalService/postPushCreditNoteToExternalService.api.ts
var postPushCreditNoteToExternalService_api_exports = {};
__export(postPushCreditNoteToExternalService_api_exports, {
  postPushCreditNoteToExternalService: () => postPushCreditNoteToExternalService
});

// src/clients/dashboard/v20240730/endpoints/creditNotes/creditNotes.cacheKeys.ts
var CREDIT_NOTES_SETTINGS = "creditNotesSettings";
var CREDIT_NOTES = "creditNotes";
var CREDIT_NOTES_CSV_DOWNLOAD = "creditNotesCsvDownload";
var CREDIT_NOTES_PDF_DOWNLOAD = "creditNotesPdfDownload";

// src/clients/dashboard/v20240730/endpoints/integrations/postPushCreditNoteToExternalService/postPushCreditNoteToExternalService.api.ts
var postPushCreditNoteToExternalService = createMutationEndpoint({
  route: ({ service, creditNoteId }) => `/integrations/${service}/sync-credit-note/${creditNoteId}`,
  method: "POST",
  invalidateQueries: [CREDIT_NOTES],
  mocking: {
    match: /\/api\/integrations\/(.+)\/sync-credit-note\/(.+)$/,
    mock: void 0
  }
});

// src/clients/dashboard/v20240730/endpoints/integrations/postPushInvoiceToExternalService/postPushInvoiceToExternalService.api.ts
var postPushInvoiceToExternalService_api_exports = {};
__export(postPushInvoiceToExternalService_api_exports, {
  postPushInvoiceToExternalService: () => postPushInvoiceToExternalService
});
var postPushInvoiceToExternalService = createMutationEndpoint({
  route: ({ service, invoiceId }) => `/integrations/${service}/sync-invoice/${invoiceId}`,
  method: "POST",
  invalidateQueries: [INVOICES],
  mocking: {
    match: /\/api\/integrations\/(.+)\/sync-invoice\/(.+)$/,
    mock: void 0
  }
});

// src/clients/dashboard/v20240730/endpoints/integrations/postPushCustomerToExternalService/postPushCustomerToExternalService.api.ts
var postPushCustomerToExternalService_api_exports = {};
__export(postPushCustomerToExternalService_api_exports, {
  postPushCustomerToExternalService: () => postPushCustomerToExternalService
});
var postPushCustomerToExternalService = createMutationEndpoint({
  route: (params) => `/integrations/${params.service}/push-customer/${params.customerId}`,
  method: "POST",
  body: (params) => "data" in params ? JSON.stringify(params.data) : JSON.stringify({}),
  invalidateQueries: [INTEGRATION_CUSTOMERS, CUSTOMERS],
  mocking: {
    match: /\/api\/integrations\/(.+)\/push-customer\/(.+)$/,
    mock: void 0
  }
});

// src/clients/dashboard/v20240730/endpoints/integrations/postValidateExternalEntity/postValidateExternalEntity.api.ts
var postValidateExternalEntity_api_exports = {};
__export(postValidateExternalEntity_api_exports, {
  postValidateExternalEntity: () => postValidateExternalEntity
});
var postValidateExternalEntity = createMutationEndpoint({
  route: ({ service }) => `/integrations/${service}/validate-external-entity`,
  method: "POST",
  body: ({ id, entityType }) => JSON.stringify({ externalEntityId: id, externalEntityType: entityType }),
  invalidateQueries: [],
  mocking: {
    match: /\/api\/integrations\/(.+)\/validate-external-entity$/,
    mock: {
      isValid: true,
      name: "Opportunity 1"
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/integrations/postCreateGoogleSheetsInsightsExport/postCreateGoogleSheetsInsightsExport.api.ts
var postCreateGoogleSheetsInsightsExport_api_exports = {};
__export(postCreateGoogleSheetsInsightsExport_api_exports, {
  postCreateGoogleSheetsInsightsExport: () => postCreateGoogleSheetsInsightsExport
});
var postCreateGoogleSheetsInsightsExport = createMutationEndpoint({
  route: () => "/integrations/google-sheets/export",
  body: ({ template, sheetUrl }) => JSON.stringify({ template, sheetUrl }),
  method: "POST",
  invalidateQueries: [],
  mocking: {
    match: /\/api\/integrations\/google-sheets\/export/,
    mock: void 0
  }
});

// src/clients/dashboard/v20240730/endpoints/integrations/postCreateCustomerFromExternalService/postCreateCustomerFromExternalService.api.ts
var postCreateCustomerFromExternalService_api_exports = {};
__export(postCreateCustomerFromExternalService_api_exports, {
  postCreateCustomerFromExternalService: () => postCreateCustomerFromExternalService
});
var postCreateCustomerFromExternalService = createMutationEndpoint({
  route: ({ service }) => `/integrations/${service}/customers`,
  method: "POST",
  body: ({ customerIds }) => JSON.stringify({ customerIds }),
  /**
   * No invalidated queries since this call itself does not create customers,
   * and they happen async.
   */
  invalidateQueries: [CUSTOMERS],
  mocking: {
    match: /\/api\/integrations\/(.+)\/customers$/,
    mock: {
      count: 1
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/integrations/postCreateCustomerExternalServiceProgress/postCreateCustomerFromExternalServiceProgress.api.ts
var postCreateCustomerFromExternalServiceProgress_api_exports = {};
__export(postCreateCustomerFromExternalServiceProgress_api_exports, {
  postCreateCustomerFromExternalServiceProgress: () => postCreateCustomerFromExternalServiceProgress
});
var postCreateCustomerFromExternalServiceProgress = createMutationEndpoint({
  route: ({ service }) => `/integrations/${service}/customers/progress`,
  method: "POST",
  body: ({ customerIds }) => JSON.stringify({ customerIds }),
  /**
   * Invalidate customers when calling this. it's reasonable to assume
   * that if this endpoint is being called, we're waiting for new
   * customers to show up.
   */
  invalidateQueries: [CUSTOMERS],
  mocking: {
    match: /\/api\/integrations\/(.+)\/customers\/progress$/,
    mock: {
      totalCount: 10,
      currentCount: 1
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/integrations/postSyncInvoiceWithExternalService/postSyncInvoiceWithExternalService.api.ts
var postSyncInvoiceWithExternalService_api_exports = {};
__export(postSyncInvoiceWithExternalService_api_exports, {
  postSyncInvoiceWithExternalService: () => postSyncInvoiceWithExternalService
});
var postSyncInvoiceWithExternalService = createMutationEndpoint({
  route: ({ service, invoiceId }) => `/integrations/${service}/sync-invoice/${invoiceId}`,
  method: "POST",
  /**
   * An indirect action where invalidating immediately would have no effect (meta note - I wonder
   * if we could do with a config to 'invalidate on delay'?)
   */
  invalidateQueries: [],
  mocking: {
    match: /\/api\/integrations\/(.+)\/sync-invoice\/(.+)$/,
    mock: void 0
  }
});

// src/clients/dashboard/v20240730/endpoints/integrations/index.ts
var integrationEndpoints = {
  getAllIntegrationCustomers,
  getIntegration,
  deleteLinkedEntity,
  getIntegrationCustomersService,
  postPushCreditNoteToExternalService,
  postPushInvoiceToExternalService,
  postPushCustomerToExternalService,
  postValidateExternalEntity,
  postCreateGoogleSheetsInsightsExport,
  postCreateCustomerFromExternalService,
  postCreateCustomerFromExternalServiceProgress,
  postSyncInvoiceWithExternalService
};

// src/clients/dashboard/v20240730/endpoints/insights/getInsightsUsageEvents/getInsightsUsageEvents.api.ts
var getInsightsUsageEvents_api_exports = {};
__export(getInsightsUsageEvents_api_exports, {
  getInsightsUsageEvents: () => getInsightsUsageEvents
});

// src/clients/dashboard/v20240730/endpoints/insights/insights.cacheKeys.ts
var insightsPrefix = "insights";
var INSIGHTS_USAGE_EVENTS = `${insightsPrefix}.usageEvents`;
var INSIGHTS_CUSTOMER_TOTALS = `${insightsPrefix}.customerTotals`;
var INSIGHTS_OUTSTANDING_INVOICES = `${insightsPrefix}.outstandingInvoices`;
var INSIGHTS_INVOICED_REVENUE = `${insightsPrefix}.invoicedRevenue`;
var INSIGHTS_USAGE_DATA = `${insightsPrefix}.usageData`;

// src/clients/dashboard/v20240730/endpoints/insights/getInsightsUsageEvents/getInsightsUsageEvents.api.ts
var getInsightsUsageEvents = createQueryEndpoint({
  route: (params) => `/insights/usage-events${createQueryParams(params)}`,
  queryKey: [INSIGHTS_USAGE_EVENTS],
  mocking: {
    match: /\/insights\/usage-events(\?(.*))?$/,
    mock: {
      totalEventCount: 5,
      eventsByDay: [
        {
          eventCount: 3,
          date: "2024-01-01"
        },
        {
          eventCount: 2,
          date: "2024-01-02"
        }
      ]
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/insights/getInsightsCustomerTotals/getInsightsCustomerTotals.api.ts
var getInsightsCustomerTotals_api_exports = {};
__export(getInsightsCustomerTotals_api_exports, {
  getInsightsCustomerTotals: () => getInsightsCustomerTotals
});
var getInsightsCustomerTotals = createQueryEndpoint({
  route: () => `/insights/customer-totals`,
  queryKey: [INSIGHTS_CUSTOMER_TOTALS],
  mocking: {
    match: /\/insights\/customer-totals$/,
    mock: {
      total: 4,
      numCustomersWithActiveSchedule: 2,
      numCustomersNoLongerBilled: 1,
      numCustomersNeverBilled: 1
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/insights/getInsightsOutstandingInvoices/getInsightsOutstandingInvoices.api.ts
var getInsightsOutstandingInvoices_api_exports = {};
__export(getInsightsOutstandingInvoices_api_exports, {
  getInsightsOutstandingInvoices: () => getInsightsOutstandingInvoices
});
var getInsightsOutstandingInvoices = createQueryEndpoint({
  route: (params) => `/insights/outstanding-invoices${createQueryParams(params)}`,
  queryKey: [INSIGHTS_OUTSTANDING_INVOICES],
  mocking: {
    match: /\/insights\/outstanding-invoices(\?(.*))?$/,
    mock: {
      currency: "GBP",
      totalAmount: 4900,
      outstandingNow: {
        numberOfInvoices: 1,
        amount: 1900
      },
      overdueFrom0to30days: {
        numberOfInvoices: 1,
        amount: 1e3
      },
      overdueFrom31to60days: {
        numberOfInvoices: 1,
        amount: 1e3
      },
      overdueFrom61days: {
        numberOfInvoices: 1,
        amount: 1e3
      }
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/insights/getInsightsInvoicedRevenue/getInsightsInvoicedRevenue.api.ts
var getInsightsInvoicedRevenue_api_exports = {};
__export(getInsightsInvoicedRevenue_api_exports, {
  getInsightsInvoicedRevenue: () => getInsightsInvoicedRevenue
});
var getInsightsInvoicedRevenue = createQueryEndpoint({
  route: (params) => `/insights/invoiced-revenue${createQueryParams(params)}`,
  queryKey: [INSIGHTS_INVOICED_REVENUE],
  mocking: {
    match: /\/insights\/invoiced-revenue(\?(.*))?$/,
    mock: {
      currency: "GBP",
      totalAmount: 9400,
      invoicedRevenueByMonth: [
        {
          amount: 4900,
          periodStart: "2024-01-01"
        },
        {
          amount: 4500,
          periodStart: "2024-02-01"
        }
      ]
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/insights/getInsightsUsageData/getInsightsUsageData.api.ts
var getInsightsUsageData_api_exports = {};
__export(getInsightsUsageData_api_exports, {
  getInsightsUsageData: () => getInsightsUsageData
});
var getInsightsUsageData = createQueryEndpoint({
  route: ({ usageMetricId, ...params }) => `/insights/usage-metrics/${usageMetricId}/usage-data${createQueryParams(params)}`,
  queryKey: [INSIGHTS_USAGE_DATA],
  /**
   * We don't want the 'data' top level property to be forwarded into the app, since
   * it's not particularly common/standard!
   */
  config: {
    responseTransform: (res) => res.json().then(({ data }) => data)
  },
  mocking: {
    match: /\/insights\/usage-metrics\/.*\/usage-data(\?(.*))?$/,
    mock: {
      usageStart: "2024-01-01",
      usageEnd: "2024-01-03",
      metricName: "API Calls",
      metricId: "030a64dd-1d89-46a3-8043-580befdb011d",
      total: "35",
      usageData: [
        {
          periodStart: "2024-01-01",
          amount: "10",
          cumulativeAmount: "10"
        },
        {
          periodStart: "2024-01-02",
          amount: "15",
          cumulativeAmount: "25"
        },
        {
          periodStart: "2024-01-03",
          amount: "10",
          cumulativeAmount: "35"
        }
      ]
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/insights/index.ts
var insightsEndpoints = {
  getInsightsUsageEvents,
  getInsightsCustomerTotals,
  getInsightsOutstandingInvoices,
  getInsightsInvoicedRevenue,
  getInsightsUsageData
};

// src/clients/dashboard/v20240730/endpoints/taxRates/getTaxRates/getTaxRates.api.ts
var getTaxRates_api_exports = {};
__export(getTaxRates_api_exports, {
  getTaxRates: () => getTaxRates
});

// src/clients/dashboard/v20240730/endpoints/taxRates/taxRates.cacheKey.ts
var TAX_RATES = "taxRates";

// src/clients/dashboard/v20240730/endpoints/taxRates/getTaxRates/getTaxRates.api.ts
var getTaxRates = createQueryEndpoint({
  route: (params) => `/tax-rates${createQueryParams(params)}`,
  queryKey: [TAX_RATES],
  mocking: {
    match: /\/api\/tax-rates(\?(.*))?$/,
    mock: {
      items: [
        {
          id: "d58bf2e3-7a5d-4900-8457-2521c6880f7d",
          versionId: "82ef6f1a-afc2-4a8d-b868-6d9c72bf395f",
          version: 0,
          isPublished: false,
          name: "UK Sales Tax",
          invoiceName: "Value Added Tax",
          invoiceShortName: "VAT",
          amount: "0.2",
          country: "GB",
          taxCategoryId: "ce109c64-dcc0-487a-ac8c-e3237c60cdb6",
          isArchived: false
        }
      ],
      pagination: {
        after: "",
        before: "",
        totalResultSize: 1
      }
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/taxRates/postNewTaxRateVersion/postNewTaxRateVersion.api.ts
var postNewTaxRateVersion_api_exports = {};
__export(postNewTaxRateVersion_api_exports, {
  postNewTaxRateVersion: () => postNewTaxRateVersion
});
var postNewTaxRateVersion = createMutationEndpoint({
  route: ({ id }) => `/tax-rates/${id}/versions`,
  method: "POST",
  body: ({ body }) => JSON.stringify(body),
  invalidateQueries: [TAX_RATES],
  mocking: {
    match: /\/api\/tax-rates\/(.+)\/versions$/,
    mock: {
      id: "d58bf2e3-7a5d-4900-8457-2521c6880f7d",
      versionId: "82ef6f1a-afc2-4a8d-b868-6d9c72bf395f",
      version: 1,
      isPublished: false,
      name: "UK Sales Tax",
      invoiceName: "Value Added Tax",
      invoiceShortName: "VAT",
      description: "Value Added Tax applied to sales in the UK",
      amount: "0.2",
      country: "GB",
      taxCategoryId: "ce109c64-dcc0-487a-ac8c-e3237c60cdb6",
      isArchived: false
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/taxRates/postTaxRate/postTaxRate.api.ts
var postTaxRate_api_exports = {};
__export(postTaxRate_api_exports, {
  postTaxRate: () => postTaxRate
});
var postTaxRate = createMutationEndpoint({
  route: () => `/tax-rates`,
  method: "POST",
  body: ({ body }) => JSON.stringify(body),
  invalidateQueries: [TAX_RATES],
  mocking: {
    match: /\/api\/tax-rates$/,
    mock: {
      id: "d58bf2e3-7a5d-4900-8457-2521c6880f7d",
      versionId: "82ef6f1a-afc2-4a8d-b868-6d9c72bf395f",
      version: 0,
      isPublished: false,
      name: "UK Sales Tax",
      invoiceName: "Value Added Tax",
      invoiceShortName: "VAT",
      description: "Value Added Tax applied to sales in the UK",
      amount: "0.2",
      country: "GB",
      taxCategoryId: "ce109c64-dcc0-487a-ac8c-e3237c60cdb6",
      isArchived: false
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/taxRates/putTaxRate/putTaxRate.api.ts
var putTaxRate_api_exports = {};
__export(putTaxRate_api_exports, {
  putTaxRate: () => putTaxRate
});
var putTaxRate = createMutationEndpoint({
  route: ({ id }) => `/tax-rates/${id}`,
  method: "PUT",
  body: ({ body }) => JSON.stringify(body),
  invalidateQueries: [TAX_RATES],
  mocking: {
    match: /\/api\/tax-rates\/(.+)$/,
    mock: {
      id: "d58bf2e3-7a5d-4900-8457-2521c6880f7d",
      versionId: "82ef6f1a-afc2-4a8d-b868-6d9c72bf395f",
      version: 0,
      isPublished: false,
      name: "UK Sales Tax",
      invoiceName: "Value Added Tax",
      invoiceShortName: "VAT",
      description: "Value Added Tax applied to sales in the UK",
      amount: "0.2",
      country: "GB",
      taxCategoryId: "ce109c64-dcc0-487a-ac8c-e3237c60cdb6",
      isArchived: false
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/taxRates/postArchiveTaxRate/postArchiveTaxRate.api.ts
var postArchiveTaxRate_api_exports = {};
__export(postArchiveTaxRate_api_exports, {
  postArchiveTaxRate: () => postArchiveTaxRate
});
var postArchiveTaxRate = createMutationEndpoint({
  route: ({ id }) => `/tax-rates/${id}/archive`,
  method: "POST",
  invalidateQueries: [TAX_RATES],
  mocking: {
    match: /\/api\/tax-rates\/(.+)\/archive$/,
    mock: {
      id: "d58bf2e3-7a5d-4900-8457-2521c6880f7d",
      versionId: "82ef6f1a-afc2-4a8d-b868-6d9c72bf395f",
      sequenceAccountId: "dce9ae85-d8d0-42b4-bcad-f221a64363c6",
      version: 0,
      isPublished: false,
      name: "UK Sales Tax",
      invoiceName: "Value Added Tax",
      invoiceShortName: "VAT",
      description: "Value Added Tax applied to sales in the UK",
      amount: "0.2",
      country: "GB",
      region: "London",
      taxCategoryId: "ce109c64-dcc0-487a-ac8c-e3237c60cdb6",
      isArchived: true
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/taxRates/index.ts
var taxRatesEndpoints = {
  getTaxRates,
  postNewTaxRateVersion,
  postTaxRate,
  putTaxRate,
  postArchiveTaxRate
};

// src/clients/dashboard/v20240730/endpoints/seats/getSeatEvent/getSeatEvent.api.ts
var getSeatEvent_api_exports = {};
__export(getSeatEvent_api_exports, {
  getSeatEvent: () => getSeatEvent
});

// src/clients/dashboard/v20240730/endpoints/seats/seats.cacheKeys.ts
var SEAT_METRICS = "seatMetrics";
var SEAT_EVENTS = "seatEvents";
var SEAT_BALANCES = "seatBalances";
var SEAT_TOTALS = "seatTotals";

// src/clients/dashboard/v20240730/endpoints/seats/getSeatEvent/getSeatEvent.api.ts
var getSeatEvent = createQueryEndpoint({
  route: (params) => `/seat-events/${params.id}`,
  queryKey: [SEAT_EVENTS],
  mocking: {
    match: /\/api\/seat-events\/(.+)$/,
    mock: {
      id: "seat-event-123",
      customerAlias: "customer-alias-123",
      customerEventId: "customer-event-123",
      seatType: "seat",
      sequenceAccountId: "sequence-account-123",
      total: 12,
      eventTimestamp: "2023-09-01T10:00:00.000Z"
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/seats/getSeatBalances/getSeatBalances.api.ts
var getSeatBalances_api_exports = {};
__export(getSeatBalances_api_exports, {
  getSeatBalances: () => getSeatBalances
});
var getSeatBalances = createQueryEndpoint({
  route: (params) => `/seat-balances${createQueryParams(params)}`,
  queryKey: [SEAT_BALANCES],
  mocking: {
    match: /\/api\/seat-balances(\?(.*))?$/,
    mock: {
      items: [
        {
          id: "test-8202-70ea-a729-30308a40bbf7",
          sequenceAccountId: "",
          legalName: "test-customer",
          customerAliases: ["test-customer"],
          total: 10,
          changeTimestamp: "2023-09-01T10:00:00.000Z",
          balances: [
            {
              seatType: "administrator",
              total: 10,
              changeTimestamp: "2023-09-01T10:00:00.000Z"
            }
          ]
        }
      ],
      pagination: {
        after: "",
        before: "",
        totalResultSize: 1
      }
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/seats/getSeatEvents/getSeatEvents.api.ts
var getSeatEvents_api_exports = {};
__export(getSeatEvents_api_exports, {
  getSeatEvents: () => getSeatEvents
});
var getSeatEvents = createQueryEndpoint({
  route: (params) => `/seat-events${createQueryParams(params)}`,
  queryKey: [SEAT_EVENTS],
  mocking: {
    match: /\/api\/seat-events(\?(.*))?$/,
    mock: {
      items: [
        {
          id: "seat-event-123",
          customerAlias: "customer-alias-123",
          customerEventId: "customer-event-123",
          seatType: "seat",
          sequenceAccountId: "sequence-account-123",
          total: 12,
          eventTimestamp: "2023-09-01T10:00:00.000Z"
        }
      ],
      pagination: {
        after: "",
        before: "",
        totalResultSize: 1
      }
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/seats/getSeatMetrics/getSeatMetrics.api.ts
var getSeatMetrics_api_exports = {};
__export(getSeatMetrics_api_exports, {
  getSeatMetrics: () => getSeatMetrics
});
var getSeatMetrics = createQueryEndpoint({
  route: (params) => `/seat-metrics${createQueryParams(params)}`,
  queryKey: [SEAT_METRICS],
  mocking: {
    match: /\/api\/seat-metrics(\?(.*))?$/,
    mock: {
      items: [
        {
          id: "seat-metric-123",
          seatType: "seat",
          label: "Seat",
          description: "A seat of mine",
          createdAt: "2023-09-01T10:00:00.000Z",
          updatedAt: "2023-09-01T11:00:00.000Z"
        }
      ],
      pagination: {
        after: "",
        before: "",
        totalResultSize: 1
      }
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/seats/postSeatEvent/postSeatEvent.api.ts
var postSeatEvent_api_exports = {};
__export(postSeatEvent_api_exports, {
  postSeatEvent: () => postSeatEvent
});
var postSeatEvent = createMutationEndpoint({
  route: () => `/seat-events`,
  method: "POST",
  body: (params) => JSON.stringify(params),
  invalidateQueries: [SEAT_EVENTS, SEAT_BALANCES],
  mocking: {
    match: /\/api\/seat-events$/,
    mock: {
      id: "seat-event-123",
      customerAlias: "customer-alias-123",
      customerEventId: "customer-event-123",
      seatType: "seat",
      sequenceAccountId: "sequence-account-123",
      total: 12,
      eventTimestamp: "2023-09-01T10:00:00.000Z"
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/seats/postSeatMetrics/postSeatMetrics.api.ts
var postSeatMetrics_api_exports = {};
__export(postSeatMetrics_api_exports, {
  postSeatMetric: () => postSeatMetric
});
var postSeatMetric = createMutationEndpoint({
  route: () => `/seat-metrics`,
  method: "POST",
  body: ({ body }) => JSON.stringify(body),
  invalidateQueries: [SEAT_METRICS, SEAT_BALANCES],
  mocking: {
    match: /\/api\/seat-metrics$/,
    mock: {
      label: "Test Seat Metric",
      seatType: "Test Seat Type",
      description: "Test Description",
      id: "seat-metric-123",
      createdAt: "2023-09-01T10:00:00.000Z",
      updatedAt: "2023-09-01T11:00:00.000Z"
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/seats/getSeatTotals/getSeatTotals.api.ts
var getSeatTotals_api_exports = {};
__export(getSeatTotals_api_exports, {
  getSeatTotals: () => getSeatTotals
});
var getSeatTotals = createQueryEndpoint({
  route: (params = {}) => `/latest-seat-totals${createQueryParams({
    ...params,
    customerAliases: params.customerAliases ? params.customerAliases.join(",") : void 0,
    seatTypes: params.seatTypes ? params.seatTypes.join(",") : void 0
  })}`,
  queryKey: [SEAT_TOTALS],
  mocking: {
    match: /\/api\/latest-seat-totals(\?(.*))?$/,
    mock: {
      items: [
        {
          id: "0184421a-8202-70ea-a729-30308a40bbf7",
          customerAlias: "customer-id-2H4u5BBwBWsS5V2sroRFqJfTXpW",
          seatType: "admin",
          total: 10,
          changeTimestamp: "2022-06-28T16:47:00Z"
        }
      ],
      pagination: {
        after: "MDE4NDUyNjYtNmQ2NC03N2M4LTlhZGYtNDQ4N2Q5NGQxNDU3",
        before: "MDE4NDQyMWUtYTljYy03MTFiLWFmOTMtYmYwNTExZDRkODMz",
        totalResultSize: 1
      }
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/seats/getSeatMetric/getSeatMetric.api.ts
var getSeatMetric_api_exports = {};
__export(getSeatMetric_api_exports, {
  getSeatMetric: () => getSeatMetric
});
var getSeatMetric = createQueryEndpoint({
  route: ({ id }) => `/seat-metrics/${id}`,
  queryKey: [SEAT_METRICS],
  mocking: {
    match: /\/api\/seat-metrics\/(.+)$/,
    mock: {
      id: "bc61d4b8-e9d6-4f4a-9bcf-f5dae874c730",
      seatType: "admin",
      createdBy: "4ffd439e-32e5-4046-bb40-3cb81f40e5da",
      label: "Administrator",
      description: "Allows user to make admin changes over whole account",
      createdAt: "2022-06-28T16:47:00Z",
      updatedAt: "2022-06-28T16:47:00Z"
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/seats/index.ts
var seatsEndpoints = {
  getSeatEvent,
  getSeatBalances,
  getSeatEvents,
  getSeatMetrics,
  postSeatEvent,
  postSeatMetric,
  getSeatTotals,
  getSeatMetric
};

// src/clients/dashboard/v20240730/endpoints/prices/getPrice/getPrice.api.ts
var getPrice_api_exports = {};
__export(getPrice_api_exports, {
  getPrice: () => getPrice
});
var getPrice = createQueryEndpoint({
  route: (params) => `/prices/${params.id}`,
  queryKey: [PRICES],
  mocking: {
    match: /\/api\/prices\/(.+)$/,
    mock: {
      id: "price-123",
      name: "Example Price",
      productId: "product-123",
      structure: {
        pricingType: "ONE_TIME",
        price: "10.00"
      },
      currency: "USD",
      createdAt: "2024-06-11",
      integrationIds: [
        {
          service: "Xero",
          id: "foo"
        }
      ],
      billingFrequency: "ONE_TIME",
      billingType: "IN_ARREARS",
      customMetricParameters: []
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/prices/getPriceMetadata/getPriceMetadata.api.ts
var getPriceMetadata_api_exports = {};
__export(getPriceMetadata_api_exports, {
  getPriceMetadata: () => getPriceMetadata
});
var getPriceMetadata = createQueryEndpoint({
  route: ({ id }) => `/prices/${id}/metadata`,
  queryKey: [PRICE_METADATA],
  mocking: {
    match: /\/api\/prices\/([^/]+)\/metadata/,
    mock: {
      billingSchedules: [
        {
          billingScheduleId: "e309e948-088e-4d77-b654-cd85498f1555",
          customerLegalName: "John Doe",
          customerId: "cca32597-b6b0-4499-9669-128aef8702e4"
        }
      ]
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/prices/index.ts
var priceEndpoints = {
  getPrice,
  getPriceMetadata
};

// src/clients/dashboard/v20240730/endpoints/notifications/getAllNotificationPolicies/getAllNotificationPolicies.api.ts
var getAllNotificationPolicies_api_exports = {};
__export(getAllNotificationPolicies_api_exports, {
  getAllNotificationPolicies: () => getAllNotificationPolicies
});

// src/clients/dashboard/v20240730/endpoints/notifications/notifications.cacheKeys.ts
var NOTIFICATION_POLICIES = "notificationsPolicies";

// src/clients/dashboard/v20240730/endpoints/notifications/getAllNotificationPolicies/getAllNotificationPolicies.api.ts
var getAllNotificationPolicies = createQueryEndpoint({
  route: ({ limit = 100, ...params } = {}) => `/notifications/policies${createQueryParams({
    limit,
    ...params
  })}`,
  queryKey: [NOTIFICATION_POLICIES],
  mocking: {
    match: /\/api\/notifications\/policies(\?(.*))?$/,
    mock: {
      items: [
        {
          id: "9c4c54e7-59da-44ae-9cc5-7fd81be7c133",
          createdAt: "2022-06-28T16:47:00Z",
          sequenceAccountId: "c4602d51-0fb4-4b48-b689-6ba28f540d1e",
          name: "Finance team emails",
          notificationTypes: ["CUSTOMER_CREATED"],
          recipients: ["finance@example.com"],
          channel: "EMAIL"
        }
      ],
      pagination: {
        after: "",
        before: "",
        totalResultSize: 1
      }
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/notifications/deleteNotificationPolicy/deleteNotificationPolicy.api.ts
var deleteNotificationPolicy_api_exports = {};
__export(deleteNotificationPolicy_api_exports, {
  deleteNotificationPolicy: () => deleteNotificationPolicy
});
var deleteNotificationPolicy = createMutationEndpoint({
  route: ({ id }) => `/notifications/policies/${id}`,
  method: "DELETE",
  invalidateQueries: [NOTIFICATION_POLICIES],
  mocking: {
    match: /\/api\/notifications\/policies\/(.*)$/,
    mock: {
      id: "9c4c54e7-59da-44ae-9cc5-7fd81be7c133",
      createdAt: "2022-06-28T16:47:00Z",
      sequenceAccountId: "c4602d51-0fb4-4b48-b689-6ba28f540d1e",
      name: "Finance team emails",
      notificationTypes: ["CUSTOMER_CREATED"],
      recipients: ["finance@example.com"],
      channel: "EMAIL"
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/notifications/postNotificationPolicy/postNotificationPolicy.api.ts
var postNotificationPolicy_api_exports = {};
__export(postNotificationPolicy_api_exports, {
  postNotificationPolicy: () => postNotificationPolicy
});
var postNotificationPolicy = createMutationEndpoint({
  route: () => `/notifications/policies`,
  body: (params) => JSON.stringify(params),
  method: "POST",
  invalidateQueries: [NOTIFICATION_POLICIES],
  mocking: {
    match: /\/api\/notifications\/policies\/(.*)$/,
    mock: {
      id: "9c4c54e7-59da-44ae-9cc5-7fd81be7c133",
      createdAt: "2022-06-28T16:47:00Z",
      sequenceAccountId: "c4602d51-0fb4-4b48-b689-6ba28f540d1e",
      name: "Finance team emails",
      notificationTypes: ["CUSTOMER_CREATED"],
      recipients: ["finance@example.com"],
      channel: "EMAIL"
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/notifications/index.ts
var notificationsEndpoints = {
  getAllNotificationPolicies,
  deleteNotificationPolicy,
  postNotificationPolicy
};

// src/clients/dashboard/v20240730/endpoints/creditNotes/getCreditNotesCsvDownload/getCreditNotesCsvDownload.api.ts
var getCreditNotesCsvDownload_api_exports = {};
__export(getCreditNotesCsvDownload_api_exports, {
  getCreditNotesCsvDownload: () => getCreditNotesCsvDownload
});
var getCreditNotesCsvDownload = createQueryEndpoint({
  route: (params) => `/credit-notes/credit-notes-csv${createQueryParams(params)}`,
  config: { responseTransform: (res) => res.blob() },
  queryKey: [CREDIT_NOTES_CSV_DOWNLOAD],
  mocking: {
    match: /\/api\/credit-notes\/credit-notes-csv(\?(.*))?$/,
    mock: new Blob([""], { type: "text/csv" })
  }
});

// src/clients/dashboard/v20240730/endpoints/creditNotes/getCreditNotesSettings/getCreditNotesSettings.api.ts
var getCreditNotesSettings_api_exports = {};
__export(getCreditNotesSettings_api_exports, {
  getCreditNotesSettings: () => getCreditNotesSettings
});
var getCreditNotesSettings = createQueryEndpoint({
  route: () => `/credit-notes-settings`,
  queryKey: [CREDIT_NOTES_SETTINGS],
  mocking: {
    match: /\/api\/credit-notes-settings$/,
    mock: {
      generateCashCreditGrant: "ENABLED"
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/creditNotes/putCreditNotesSettings/putCreditNotesSettings.api.ts
var putCreditNotesSettings_api_exports = {};
__export(putCreditNotesSettings_api_exports, {
  putCreditNotesSettings: () => putCreditNotesSettings
});
var putCreditNotesSettings = createMutationEndpoint({
  route: () => `/credit-notes-settings`,
  body: ({ body }) => JSON.stringify(body),
  method: "PUT",
  invalidateQueries: [CREDIT_NOTES_SETTINGS],
  mocking: {
    match: /\/api\/credit-notes-settings$/,
    mock: {
      generateCashCreditGrant: "ENABLED"
    }
  }
});

// src/clients/dashboard/v20240730/endpoints/creditNotes/getCreditNotePdf/getCreditNotePdf.api.ts
var getCreditNotePdf_api_exports = {};
__export(getCreditNotePdf_api_exports, {
  getCreditNotePdf: () => getCreditNotePdf
});
var getCreditNotePdf = createQueryEndpoint({
  route: ({ id }) => `/credit-notes/${id}/download`,
  config: { responseTransform: (res) => res.blob() },
  queryKey: [CREDIT_NOTES_PDF_DOWNLOAD],
  mocking: {
    match: /\/api\/credit-notes\/[^\/]+\/download$/,
    mock: new Blob([""], { type: "application/pdf" })
  }
});

// src/clients/dashboard/v20240730/endpoints/creditNotes/index.ts
var creditNotesEndpoints = {
  getCreditNotesCsvDownload,
  getCreditNotesSettings,
  putCreditNotesSettings,
  getCreditNotePdf
};

// src/clients/dashboard/v20240730/endpoints/index.ts
var endpoints20240730 = {
  deleteQuoteDraftVariant,
  getAccountPaymentSettings,
  getBillingProducts,
  getBulkActions,
  getMerchant,
  getQuote,
  getQuotes,
  getTaxCategories,
  getTaxCategory,
  getTaxRegistrations,
  postNewTaxCategoryVersion,
  postProductArchive,
  postQuote,
  postQuoteAccept,
  postQuoteArchive,
  postQuoteDuplicate,
  postQuoteExecute,
  postQuotePublish,
  postReviewBulkAction,
  postTaxCategory,
  postTaxRegistration,
  putAccountPaymentSettings,
  putMerchant,
  putQuote,
  putTaxCategory,
  putTaxRegistration,
  ...creditGrantEndpoints,
  ...schedulesEndpoints,
  ...savedViewsEndpoints,
  ...quoteTemplatesEndpoints,
  ...invoiceEndpoints,
  ...usageEventsEndpoints,
  ...usageMetricEndpoints,
  ...customerEndpoints,
  ...assetsEndpoints,
  ...userEndpoints,
  ...authEndpoints,
  ...sequenceAccountEndpoints,
  ...assetsEndpoints,
  ...quickbooksEndpoints,
  ...integrationEndpoints,
  ...insightsEndpoints,
  ...seatsEndpoints,
  ...taxRatesEndpoints,
  ...priceEndpoints,
  ...notificationsEndpoints,
  ...creditNotesEndpoints
};

// src/clients/dashboard/v20240730/index.ts
var dashboard20240730Client = createDashboardApiClient({
  endpoints: endpoints20240730,
  version: "2024-07-30"
});
export {
  endpoints_exports as DashboardApi20240730,
  dashboard20240730Client
};
//# sourceMappingURL=index.js.map